import { createT as createSharedT, formatAmd, formatDateTime, onTextOverrides, textOverridesVersion, type Locale as SharedLocale } from '@barev/i18n';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/** Языки новой админки */
export const BO_LOCALES = ['ru', 'en', 'hy'] as const;
export type BoLocale = (typeof BO_LOCALES)[number];
export const BO_LOCALE_LABELS: Record<BoLocale, string> = { ru: 'Рус', en: 'Eng', hy: 'Հայ' };

const ru = {
  'ui.save': 'Сохранить',
  'ui.close': 'Закрыть',
  'ui.loading': 'Загружаем…',
  'ui.error': 'Не получилось: {msg}',
  'ui.retry': 'Повторить',
  'ui.copy': 'Скопировать',
  'ui.copied': 'Скопировано',
  'ui.days': '{n} дн.',
  'ui.yes': 'Да',
  'ui.no': 'Нет',
  'ui.none': '—',

  'err.PAYMENTS_NOT_CONFIGURED': 'Платёжная система ещё не подключена — отметьте оплату вручную.',
  'err.REFUND_TOO_BIG': 'Сумма возврата больше, чем клиент заплатил.',
  'err.REFUND_UNAVAILABLE': 'Tribute не даёт вернуть этот платёж (уже возвращён или оспаривается в банке).',
  'err.PAYMENT_PROVIDER_ERROR': 'Tribute не ответил или отказал. Попробуйте позже или сделайте это в кабинете Tribute.',
  'err.PAYMENT_UNAVAILABLE': 'Онлайн-оплата не настроена на сервере.',
  'err.ENDS_BEFORE_START': 'Подписка не может закончиться раньше, чем началась.',
  'err.CANNOT_DEMOTE_SELF': 'Нельзя отключить себя или снять с себя роль суперадмина.',
  'err.ROLE_FORBIDDEN': 'У вашей роли нет доступа к этому действию.',
  'err.NO_ROLE': 'У вашего аккаунта нет ролей в новой админке. Попросите суперадмина их выдать.',

  'login.coverWord': 'команда',
  'login.title': 'Вход для команды',
  'login.lead': 'Подтвердите вход в рабочем боте Barev Club. Пароль не нужен.',
  'login.open': 'Подтвердить в Telegram',
  'login.qr': 'С компьютера удобнее отсканировать код телефоном.',
  'login.waiting': 'Ждём подтверждения в Telegram…',
  'login.expired': 'Ссылка устарела.',
  'login.retry': 'Получить новую ссылку',
  'login.dev': 'Войти локально',
  'login.devHint': 'Только на этом компьютере — на сервере этого блока нет.',
  'login.noTelegram': 'Нет доступа? Попросите суперадмина добавить вас в команду и прислать ссылку для привязки Telegram.',

  'nav.dashboard': 'Дашборд',
  'nav.clients': 'Клиенты',
  'nav.chats': 'Чаты',
  'nav.orders': 'Заказы',
  'nav.providers': 'Исполнители',
  'nav.agencies': 'Риэлторские агентства',
  'nav.venues': 'Партнёры клуба',
  'nav.crm': 'CRM',
  'nav.products': 'Продукты',
  'nav.journal': 'Журнал',
  'nav.team': 'Команда',
  'nav.more': 'Ещё',
  'nav.soon': 'этап {n}',
  'nav.groupWork': 'Работа',
  'nav.groupPartners': 'Партнёры',
  'nav.groupSetup': 'Настройка',

  'role.SUPERADMIN': 'Суперадмин',
  'role.SUPPORT': 'Поддержка',
  'role.CONCIERGE': 'Консьерж',
  'role.SALES': 'Продажи',
  'roleHint.SUPERADMIN': 'Всё, включая деньги и команду',
  'roleHint.SUPPORT': 'Чат поддержки, заказы, подписки',
  'roleHint.CONCIERGE': 'Чат консьержа для подписчиков',
  'roleHint.SALES': 'CRM: заведения и агентства',

  'shell.logout': 'Выйти',
  'shell.oldAdmin': 'Старая админка',
  'shell.linkTg': 'Привяжите Telegram — через него вы входите и получаете уведомления.',
  'shell.linkTgBtn': 'Привязать Telegram',

  'soon.title': '{section} переезжает сюда',
  'soon.text': 'Раздел появится на этапе {n}. Пока работайте в старой админке — данные общие.',
  'soon.open': 'Открыть старую админку',

  'dash.morning': 'Доброе утро, {name}',
  'dash.day': 'Добрый день, {name}',
  'dash.evening': 'Добрый вечер, {name}',
  'dash.attention': 'Требует внимания',
  'dash.allClear': 'Срочного нет — всё разобрано.',
  'att.unassigned': 'Заказы без исполнителя',
  'att.unreadChats': 'Ждут ответа в чатах',
  'att.webRequests': 'Заявки с сайта',
  'att.leads': 'Заявки на переезд',
  'att.appRequests': 'Заявки из приложения',
  'att.lowRatings': 'Оценки 3 и ниже за неделю',
  'att.expiring': 'Подписки истекают за 7 дней',
  'dash.money': 'Деньги',
  'dash.today': 'Сегодня',
  'dash.week': '7 дней',
  'dash.month': '30 дней',
  'dash.vsYesterday': 'вчера {sum}',
  'dash.vsPrev': 'к прошлым {period}',
  'dash.orders': 'заказы',
  'dash.subs': 'подписки',
  'dash.chart': 'Выручка по дням за 30 дней',
  'dash.subsTitle': 'Подписки',
  'dash.gifts': 'Из них подарочных: {n}',
  'dash.expiring': 'Истекают в ближайшие 7 дней',
  'dash.noExpiring': 'В ближайшую неделю ничего не истекает.',
  'dash.conversion': 'Подарок → покупка',
  'dash.conversionHint': '{paid} из {total} клиентов с подарком потом купили подписку',
  'dash.growth': 'Рост',
  'dash.newClients': 'Новые клиенты',
  'dash.prevMonth': 'за прошлые 30 дней — {n}',
  'dash.sources': 'Откуда пришли за 30 дней',
  'dash.funnel': 'Воронка за 30 дней',
  'funnel.leads': 'Заявки с сайта',
  'funnel.clients': 'Клиенты в приложении',
  'funnel.paying': 'Заплатили',
  'dash.team': 'Команда и партнёры',
  'dash.topProviders': 'Лучшие исполнители',
  'dash.bottomProviders': 'Стоит поговорить',
  'dash.topPartners': 'Заведения: сканы карты за 30 дней',
  'dash.salesSoon': 'Результаты продажников появятся вместе с CRM.',
  'dash.empty': 'Пока пусто',
  'dash.feed': 'Лента',
  'feed.subscription': '{plan} · {source}',
  'feed.payment': 'Оплачен заказ №{number}',
  'dash.ratings': '{n} оценок',
  'dash.scans': '{n} сканов',

  'source.organic': 'Сами нашли',
  'source.deeplink': 'Ссылка агентства',
  'source.manual_code': 'Код агентства',
  'source.agency': 'Агентство «{name}»',
  'source.utm': 'Реклама: {name}',

  'plan.BAREV_PLUS': 'Barev+',
  'plan.ELITE': 'Elite',
  'plan.none': 'Без подписки',
  'subSource.PAID': 'Покупка',
  'subSource.MANUAL': 'Подарок команды',
  'subSource.AGENCY_GIFT': 'Подарок агентства',
  'subStatus.ACTIVE': 'Активна',
  'subStatus.EXPIRED': 'Истекла',
  'subStatus.CANCELLED': 'Отменена',
  'pay.cash': 'Наличные',
  'pay.transfer': 'Перевод',
  'pay.card': 'Карта через терминал',
  'pay.link': 'Ссылка на оплату',
  'pay.tribute': 'Tribute (онлайн)',
  'segment.MOVING': 'Переезжает',
  'segment.LIVING': 'Живёт в Армении',

  'clients.title': 'Клиенты',
  'clients.search': 'Имя, @username, телефон или номер карты',
  'clients.found': 'Найдено: {n}',
  'clients.revenueTotal': 'принесли {sum}',
  'col.client': 'Клиент',
  'col.sub': 'Подписка',
  'col.source': 'Откуда',
  'col.revenue': 'Принёс',
  'col.since': 'С нами с',
  'filter.plan': 'Подписка',
  'filter.all': 'Все',
  'filter.any': 'С подпиской',
  'filter.none': 'Без подписки',
  'filter.subSource': 'Как получена',
  'filter.source': 'Откуда',
  'filter.agency': 'Агентство',
  'filter.tag': 'Тег',
  'filter.segment': 'Сценарий',
  'filter.paying': 'Платили',
  'filter.payingYes': 'Платили',
  'filter.payingNo': 'Ни разу не платили',
  'filter.expiring': 'Истекает за 7 дней',
  'filter.reset': 'Сбросить фильтры',
  'filter.more': 'Фильтры',
  'filter.anyValue': 'Любое',
  'sort.label': 'Порядок',
  'sort.new': 'Сначала новые',
  'sort.revenue': 'Больше принесли',
  'sort.ends': 'Скоро истекает',
  'segments.title': 'Мои выборки',
  'segments.save': 'Сохранить выборку',
  'segments.name': 'Название выборки',
  'segments.saved': 'Выборка сохранена',
  'segments.remove': 'Удалить выборку «{name}»',
  'clients.export': 'Скачать для Excel',
  'clients.exported': 'Файл скачан',
  'clients.broadcast': 'Рассылка',
  'clients.broadcastSoon': 'Рассылки по выборке появятся на этапе 5.',
  'clients.empty': 'Под эти фильтры никто не подходит.',
  'clients.emptyAll': 'Клиентов пока нет — они появятся, когда напишут боту.',
  'clients.prev': 'Назад',
  'clients.next': 'Дальше',
  'clients.page': '{from}–{to} из {total}',

  'card.back': 'Все клиенты',
  'card.since': 'С нами с {date}',
  'card.telegram': 'Открыть в Telegram',
  'card.cardNo': 'Карта {code}',
  'card.noSub': 'Нет активной подписки',
  'card.noSubHint': 'Подарите или продайте подписку — клиент сразу получит скидки.',
  'card.until': 'до {date}',
  'card.daysLeft': 'осталось {n} дн.',
  'card.history': 'Все подписки',
  'card.grantedBy': 'начислил(а) {name}',
  'card.giftFrom': 'подарок от «{name}»',
  'card.paidVia': '{method}',
  'card.refunded': 'возврат {sum}',
  'card.cancelledAt': 'отменена {date}',
  'card.upcoming': 'начнётся {date}',
  'card.money': 'Принёс',
  'card.fromOrders': 'заказы',
  'card.fromSubs': 'подписки',
  'card.orders': 'Заказы',
  'card.noOrders': 'Заказов пока нет.',
  'card.newOrder': 'Создать заказ',
  'card.newOrderSoon': 'Заказ за клиента появится на этапе 3, вместе с разделом заказов.',
  'card.profile': 'Профиль',
  'card.source': 'Откуда пришёл',
  'card.agency': 'Агентство',
  'card.language': 'Язык',
  'card.phone': 'Телефон',
  'card.segment': 'Сценарий',
  'card.consent': 'Согласие на обработку данных',
  'card.tags': 'Теги',
  'card.addTag': 'Новый тег',
  'card.note': 'Заметка команды',
  'card.notePh': 'Клиент не видит эту заметку',
  'card.saved': 'Сохранено',
  'card.activity': 'Журнал по клиенту',
  'card.noActivity': 'Команда пока ничего не меняла.',
  'card.stats': 'Активность',
  'stat.cardUsages': 'сканов карты',
  'stat.leads': 'заявок на переезд',
  'stat.requests': 'заявок из приложения',
  'stat.concierge': 'сообщений в чате',

  'act.gift': 'Подарить',
  'act.sell': 'Продать',
  'act.extend': 'Изменить срок',
  'act.cancel': 'Отменить',
  'act.changePlan': 'Сменить тариф',
  'act.refund': 'Вернуть деньги',
  'op.title': 'Онлайн-оплаты (Tribute)',
  'op.empty': 'Онлайн-оплат пока нет. Здесь появятся подписки и заказы, оплаченные картой через Tribute — с кнопками «Остановить автопродление» и «Вернуть деньги».',
  'op.disabled': 'Онлайн-оплата не настроена на сервере.',
  'op.paidAt': 'оплачено {{date}}',
  'op.refunded': 'возврат',
  'op.nextCharge': 'автопродление, следующее списание {{date}}',
  'op.renewOff': 'автопродление выключено',
  'op.attention': '⚠ заказ уже был оплачен или отменён — нужен возврат',
  'op.stop': 'Остановить автопродление',
  'op.stopConfirm': 'Остановить автопродление? Оплаченный месяц сохранится, дальше списаний не будет.',
  'op.stopped': 'Автопродление остановлено',
  'op.refundConfirmSub': 'Вернуть деньги за последний платёж? Автопродление остановится, текущий период подписки отменится.',
  'op.refundConfirmOrder': 'Вернуть деньги за этот заказ на карту клиента?',
  'op.refundStarted': 'Возврат отправлен в Tribute — статус обновится, когда Tribute подтвердит',
  'op.hint': 'Деньги возвращаются на карту клиента через Tribute за последний платёж.',

  'audit.bo.clients.gift': 'подарил(а) подписку',
  'audit.bo.clients.sell': 'продал(а) подписку',
  'audit.bo.clients.extend': 'изменил(а) срок подписки',
  'audit.bo.clients.cancel': 'отменил(а) подписку',
  'audit.bo.clients.changePlan': 'сменил(а) тариф',
  'audit.bo.clients.refund': 'оформил(а) возврат',
  'audit.bo.clients.setTags': 'изменил(а) теги',
  'audit.bo.clients.setNote': 'изменил(а) заметку',

  'dlg.giftTitle': 'Подарить подписку',
  'dlg.giftHint': 'Подписка будет помечена как подарок от вас.',
  'dlg.sellTitle': 'Продать подписку',
  'dlg.plan': 'Тариф',
  'dlg.period': 'Срок',
  'dlg.period1': '1 месяц',
  'dlg.period3': '3 месяца',
  'dlg.period6': '6 месяцев',
  'dlg.period12': '1 год',
  'dlg.periodCustom': 'Своё',
  'dlg.days': 'Дней',
  'dlg.amount': 'Сумма, ֏',
  'dlg.method': 'Как оплатил',
  'dlg.note': 'Комментарий для команды',
  'dlg.linkSoon': 'Ссылку на оплату включим, когда подключим платёжную систему.',
  'dlg.giftConfirm': 'Подарить',
  'dlg.sellConfirm': 'Отметить оплату',
  'dlg.extendTitle': 'Изменить срок',
  'dlg.extendHint': 'Отрицательное число сокращает срок.',
  'dlg.newEnd': 'Новая дата окончания: {date}',
  'dlg.extendConfirm': 'Сохранить срок',
  'dlg.cancelTitle': 'Отменить подписку',
  'dlg.cancelHint': 'Клиент сразу потеряет скидки и привилегии подписки.',
  'dlg.reason': 'Причина',
  'dlg.cancelConfirm': 'Отменить подписку',
  'dlg.planTitle': 'Сменить тариф',
  'dlg.planConfirm': 'Сменить тариф',
  'dlg.refundTitle': 'Вернуть деньги',
  'dlg.refundPaid': 'Оплачено {sum}, уже возвращено {back}',
  'dlg.refundCancel': 'Сразу отменить подписку',
  'dlg.refundConfirm': 'Оформить возврат',
  'done.gift': 'Подписка подарена',
  'done.sell': 'Оплата отмечена',
  'done.extend': 'Срок изменён',
  'done.cancel': 'Подписка отменена',
  'done.changePlan': 'Тариф изменён',
  'done.refund': 'Возврат оформлен',

  'team.title': 'Команда',
  'team.lead': 'Кто работает в админке и что ему доступно. У одного человека может быть несколько ролей.',
  'team.add': 'Добавить человека',
  'team.name': 'Имя',
  'team.roles': 'Роли',
  'team.create': 'Добавить',
  'team.linkTitle': 'Ссылка для {name}',
  'team.linkHint': 'Отправьте её человеку. Он откроет рабочий бот, Telegram привяжется — и можно входить.',
  'team.tgLinked': 'Telegram привязан',
  'team.tgNotLinked': 'Telegram не привязан',
  'team.newLink': 'Ссылка для привязки',
  'team.disable': 'Отключить доступ',
  'team.enable': 'Вернуть доступ',
  'team.disabled': 'Доступ отключён',
  'team.you': 'это вы',
  'team.saved': 'Роли сохранены',

  // ───── чаты (этап 2) ─────
  'chat.title': 'Чаты',
  'chat.all': 'Все',
  'channel.SUPPORT': 'Поддержка',
  'channel.CONCIERGE': 'Консьерж',
  'chat.f.waiting': 'Ждут ответа',
  'chat.f.mine': 'Мои',
  'chat.f.unassigned': 'Без ответственного',
  'chat.f.open': 'Открытые',
  'chat.f.closed': 'Закрытые',
  'chat.f.all': 'Все',
  'chat.search': 'Имя, @username, телефон или текст',
  'chat.emptyList': 'Здесь пусто. Новые сообщения появятся сами.',
  'chat.emptyFilter': 'Ничего не нашли — попробуйте другой фильтр.',
  'chat.pick': 'Выберите диалог',
  'chat.pickHint': 'Сверху — те, кто ждёт ответа дольше всех.',
  'chat.waiting': 'ждёт {t}',
  'chat.you': 'Вы',
  'chat.provider': 'Исполнитель',
  'chat.unassigned': 'Никто не ведёт',
  'chat.take': 'Взять себе',
  'chat.transfer': 'Передать',
  'chat.transferTo': 'Передать коллеге',
  'chat.unassign': 'Снять ответственного',
  'chat.close': 'Закрыть',
  'chat.reopen': 'Открыть снова',
  'chat.closedBanner': 'Диалог закрыт. Клиент напишет — откроется сам; ответить можно и сейчас.',
  'chat.moveTo': 'Перенести в «{channel}»',
  'chat.moved': 'Диалог перенесён в «{channel}»',
  'chat.movedAway': 'Диалог перенесён в «{channel}». У вас нет доступа к этому каналу.',
  'chat.other': 'Есть переписка в канале «{channel}»',
  'chat.otherOpen': 'Открыть',
  'chat.bot': 'Диалог ведёт ИИ-консьерж.',
  'chat.botTake': 'Перехватить',
  'chat.reply': 'Ответ',
  'chat.note': 'Заметка',
  'chat.replyPh': 'Ответ клиенту — придёт ему в Telegram',
  'chat.notePh': 'Заметка для команды — клиент её не увидит',
  'chat.send': 'Отправить',
  'chat.saveNote': 'Сохранить заметку',
  'chat.attach': 'Прикрепить файл',
  'chat.templates': 'Шаблоны',
  'chat.tplManage': 'Настроить шаблоны',
  'chat.tplEmpty': 'Шаблонов пока нет.',
  'chat.tplNew': 'Новый шаблон',
  'chat.tplTitle': 'Название',
  'chat.tplText': 'Текст',
  'chat.tplTextHint': '{name} заменится на имя клиента',
  'chat.tplChannel': 'Канал',
  'chat.tplBoth': 'Оба канала',
  'chat.tplDelete': 'Удалить',
  'chat.tplSaved': 'Шаблон сохранён',
  'chat.tplSearch': 'Найти шаблон',
  'chat.noteLabel': 'Заметка · видит только команда',
  'chat.fromApp': 'из приложения',
  'chat.undelivered': 'Не доставлено: клиент заблокировал бота или ни разу его не открывал',
  'chat.fileTooBig': 'Файл больше 10 МБ',
  'chat.enterHint': 'Enter — отправить · Shift+Enter — новая строка',
  'chat.providerNotLinked': 'Исполнитель ещё не открыл рабочий бот — пока можно оставить только заметку.',
  'chat.client': 'О клиенте',
  'chat.aboutProvider': 'Об исполнителе',
  'chat.openCard': 'Карточка клиента',
  'chat.write': 'Написать',
  'chat.back': 'Все диалоги',
  'chat.sys.took': '{name} взял(а) диалог',
  'chat.sys.transferred': '{name} передал(а) диалог: {param}',
  'chat.sys.unassigned': '{name} снял(а) ответственного',
  'chat.sys.closed': '{name} закрыл(а) диалог',
  'chat.sys.reopened': '{name} открыл(а) диалог снова',
  'chat.sys.moved': '{name} перенёс(ла) диалог в «{param}»',
  'chat.ago.now': 'только что',
  'chat.ago.min': '{n} мин',
  'chat.ago.h': '{n} ч',
  'chat.ago.d': '{n} дн',
  'chat.today': 'Сегодня',
  'chat.yesterday': 'Вчера',
  'chat.image': 'Фото',
  'chat.file': 'Файл',
  'chat.subUntil': 'до {date}',
  'chat.orders': 'Заказы',
  'chat.phone': 'Телефон',
  'chat.lang': 'Язык',
  'chat.source': 'Откуда',
  'chat.linked': 'Рабочий бот открыт',
  'chat.notLinked': 'Рабочий бот не открыт',
  'chat.details': 'Подробнее',
  'chat.more': 'Ещё действия',
  'ord.tab.orders': 'Заказы',
  'ord.tab.moves': 'Переезды',
  'ord.tab.requests': 'Заявки',
  'mv.search': 'Имя, контакт, агентство или №',
  'mv.empty': 'Переездов с таким статусом нет.',
  'mv.all': 'Все',
  'mv.steps': 'Шаги {done} из {total}',
  'mv.docs': 'Документы {done}/{total}',
  'mv.now': 'Сейчас: {step}',
  'mv.noPlan': 'План ещё не собран',
  'mv.back': 'Все переезды',
  'mv.plan': 'План переезда',
  'mv.planHintApp': 'Клиент видит шаги в приложении. Когда меняете статус шага, ему приходит сообщение в бот.',
  'mv.planHintNoApp': 'Клиент ещё не открыл приложение — план пока видите только вы.',
  'mv.docsTitle': 'Документы клиента',
  'mv.docsHint': 'Клиент отмечает их в приложении, вы — здесь.',
  'mv.answers': 'Ответы в квизе',
  'mv.when': 'Когда переезд',
  'mv.package': 'Пакет',
  'mv.wants': 'Что нужно',
  'mv.agency': 'Агентство',
  'mv.lang': 'Язык',
  'mv.created': 'Заявка',
  'mv.unfinished': 'не дошёл до конца квиза',
  'mv.note': 'Заметка команды',
  'mv.notePh': 'Для себя: созвонились, что обещали, кто ведёт…',
  'mv.save': 'Сохранить',
  'mv.status': 'Статус переезда',
  'mv.contact': 'Контакт',
  'mv.write': 'Написать',
  'mv.call': 'Позвонить',
  'mv.openClient': 'Карточка клиента',
  'mv.inApp': 'в приложении',
  'mv.add': 'Добавить',
  'mv.addStep': 'Шаг из списка…',
  'mv.addDoc': 'Документ из списка…',
  'mv.custom': 'Свой пункт…',
  'mv.customPh': 'Название для этого клиента',
  'mv.stepNote': 'Комментарий для клиента — он увидит его в приложении',
  'mv.remove': 'Убрать у клиента',
  'mv.removeConfirm': 'Убрать «{name}» у этого клиента?',
  'mv.rename': 'Переименовать',
  'mv.listHint': 'Общий список шагов и документов настраивается в «Продуктах».',
  'rq.site': 'С сайта',
  'rq.app': 'Из приложения',
  'rq.open': 'Открытые',
  'rq.closed': 'Закрытые',
  'rq.siteHint': 'Уборка, заказанная на barevclub.com без регистрации. Позвоните, подтвердите время и создайте заказ.',
  'rq.appHint': 'Клиенты попросили подписку или звонок по услуге без онлайн-записи.',
  'rq.empty': 'Открытых заявок нет.',
  'rq.emptyClosed': 'Закрытых заявок пока нет.',
  'rq.callback': 'Перезвонить: {service}',
  'rq.newOrder': 'Создать заказ',
  'rq.wish': 'Желаемое время',
  'rq.price': 'Цена на сайте',
  'rq.noApp': 'не открыл бота',
  'rq.done': 'Связались',
  'rq.cancel': 'Отменить',
  'rq.reopen': 'Вернуть',
  'rq.activate': 'Включить на 30 дней',
  'rq.activated': 'Подписка включена',
  'rq.order': 'Заказ №{n}',
  'audit.bo.moves.update': 'изменил(а) переезд',
  'audit.bo.moves.updateStep': 'изменил(а) шаг переезда',
  'audit.bo.moves.addStep': 'добавил(а) шаг переезда',
  'audit.bo.moves.removeStep': 'убрал(а) шаг переезда',
  'audit.bo.moves.addDoc': 'добавил(а) документ переезда',
  'audit.bo.moves.updateDoc': 'изменил(а) документ переезда',
  'audit.bo.moves.removeDoc': 'убрал(а) документ переезда',
  'audit.bo.requests.updateSite': 'изменил(а) заявку с сайта',
  'audit.bo.requests.updateApp': 'изменил(а) заявку из приложения',
  'audit.bo.requests.activate': 'включил(а) подписку по заявке',
  'pr.newService': 'Новая услуга',
  'pr.newServiceHint': 'С нуля — с фиксированной ценой и вашими вопросами клиенту, или копией готовой услуги.',
  'pr.fromScratch': 'С нуля',
  'pr.copyOf': 'Копия готовой',
  'pr.category': 'Направление',
  'pr.copyFrom': 'Скопировать услугу',
  'pr.copyHint': 'Скопируются цены, вопросы клиенту, чек-лист и окно записи — потом поменяете что нужно. Так можно сделать, например, «Генеральную уборку офиса» из клининга.',
  'pr.who': 'Кто выполняет',
  'pr.whoHint': 'Определяет, кому система предлагает заказ и какие шаги видит исполнитель. Ремонт и уроки — только копией готовой услуги.',
  'pr.flowHint.CLEANER': 'выехал → работа → готово, с чек-листом',
  'pr.flowHint.DRIVER': 'водитель на месте → в пути → завершено',
  'pr.flowHint.CAREER_CONSULTANT': 'консультант → материалы → встреча → итог',
  'pr.flowHint.DOCS_AGENT': 'проверка документов → подача → готово',
  'pr.online': 'Онлайн — без адреса и выезда',
  'pr.namePh': 'Например: Мойка окон',
  'pr.create': 'Создать услугу',
  'pr.createHint': 'Услуга появится выключенной: настройте цену, вопросы и чек-лист, затем включите её в «Запись и отмена» — и она сразу появится в приложении.',
  'pr.created': 'Услуга создана — осталось настроить и включить',
  'pr.questions': 'Вопросы клиенту при заказе',
  'pr.questionsHint': 'Что клиент выбирает или пишет в форме заказа. Ответы видят исполнитель и команда.',
  'pr.qLabel': 'Вопрос, например «Сколько окон?»',
  'pr.qType.select': 'выбор варианта',
  'pr.qType.int': 'число',
  'pr.qType.text': 'текст',
  'pr.qType.bool': 'да / нет',
  'pr.qOption': 'Вариант {n}',
  'pr.qAddOption': 'вариант',
  'pr.qFrom': 'от',
  'pr.qTo': 'до',
  'pr.qRequired': 'обязательно',
  'pr.qAdd': 'Вопрос',
  'pr.qNone': 'Вопросов нет — клиент выберет только время и адрес.',
  'pr.delete': 'Удалить услугу',
  'pr.deleteConfirm': 'Удалить услугу? Можно, только если по ней ещё не было заказов — иначе выключите её.',
  'err.SERVICE_HAS_ORDERS': 'По услуге уже были заказы — удалить нельзя, выключите её в «Запись и отмена».',
  'err.OPTIONS_REQUIRED': 'У вопроса с выбором нужно минимум 2 варианта.',
  'err.PARAMS_LOCKED': 'У этой услуги цена зависит от ответов — вопросы меняются программистом.',
  'audit.bo.products.createService': 'создал(а) услугу',
  'audit.bo.products.saveParams': 'изменил(а) вопросы услуги',
  'audit.bo.products.deleteService': 'удалил(а) услугу',
  'audit.bo.products.moveService': 'изменил(а) порядок услуг',
  'audit.bo.crm.addTask': 'поставил(а) задачу',
  'audit.bo.crm.setTaskDone': 'отметил(а) задачу',
  'audit.bo.crm.removeTask': 'удалил(а) задачу',
  'audit.bo.crm.remove': 'удалил(а) лид',
  'audit.bo.segments.save': 'сохранил(а) сегмент клиентов',
  'audit.bo.segments.remove': 'удалил(а) сегмент клиентов',
  'audit.bo.team.linkUrl': 'выпустил(а) ссылку для привязки Telegram',
  'audit.bo.auth.telegramLink': 'привязывает свой Telegram',
  'audit.bo.auth.logout': 'вышел(а) из админки',
  'audit.bo.clients.exportCsv': 'выгрузил(а) клиентов в Excel',

  // ───── продукты, рассылки, журнал, настройки (этап 5) ─────
  'nav.settings': 'Настройки',
  'pr.title': 'Продукты',
  'pr.tab.services': 'Услуги и цены',
  'pr.tab.plans': 'Подписка и скидки',
  'pr.tab.promos': 'Промокоды',
  'pr.tab.relocation': 'Релокация',
  'pr.readOnly': 'Менять цены и условия может суперадмин — здесь всё видно для справки.',
  'pr.catDiscounts': 'скидки: Barev+ −{plus}%, Elite −{elite}%',
  'pr.on': 'включена',
  'pr.off': 'выключена',
  'pr.from': 'от {sum}',
  'pr.orders30': 'заказов за 30 дн: {n}',
  'pr.setup': 'Настроить',
  'pr.back': 'Все услуги',
  'pr.content': 'Наполнение',
  'pr.contentHint': 'Так услуга называется в приложении, боте и уведомлениях. Пустое поле — стандартный текст (виден серым). Боты подхватывают правку в течение минуты.',
  'pr.name': 'Название',
  'pr.description': 'Описание',
  'pr.extrasNames': 'Названия доп. услуг ({n})',
  'pr.price': 'Цена и длительность',
  'pr.fixedPrice': 'Цена, ֏',
  'pr.minutes': 'Длительность, мин',
  'pr.base': 'База, ֏',
  'pr.perRoom': '+ комната, ֏',
  'pr.perBath': '+ санузел, ֏',
  'pr.baseMin': 'База, мин',
  'pr.perRoomMin': '+ комната, мин',
  'pr.perBathMin': '+ санузел, мин',
  'pr.roomsHint': 'База — за 1 комнату и 1 санузел; дальше доплата за каждую комнату и санузел сверх первого.',
  'pr.extras': 'Доп. услуги',
  'pr.unitCol': 'Единица',
  'pr.extraPrice': 'Цена, ֏',
  'pr.extraMin': '+ мин',
  'pr.unit.item': 'за штуку',
  'pr.unit.service': 'услуга',
  'pr.unit.hour': 'за час',
  'pr.unit.halfHour': 'за 30 мин',
  'pr.priceHint': 'Новые цены действуют для новых заказов; уже оформленные заказы не меняются. Скидка подписки считается от этих цен.',
  'pr.settings': 'Запись и отмена',
  'pr.activeLabel': 'Услуга доступна клиентам',
  'pr.windowFrom': 'Запись с',
  'pr.windowTo': 'до',
  'pr.step': 'Шаг слотов, мин',
  'pr.lead': 'Не раньше чем через, ч',
  'pr.horizon': 'Запись на дней вперёд',
  'pr.lateFee': 'Удержание за позднюю отмену, %',
  'pr.discounts': 'Скидки по подписке',
  'pr.discountsHint': 'Действуют на всё направление «{cat}».',
  'pr.checklist': 'Чек-лист исполнителя',
  'pr.photo': 'фото',
  'pr.newItem': 'Новый пункт',
  'pr.checklistHint': 'Исполнитель отмечает пункты в рабочем боте; с «фото» — только с фотоотчётом. Меняется для новых заказов.',
  'pr.plans': 'Тарифы подписки',
  'pr.plansSuper': 'Цены подписки видит и меняет суперадмин.',
  'pr.plansHint': '«Скидка до» показывается на сайте и в приложении; реальные скидки — в таблице справа и в услугах.',
  'pr.discountTable': 'Скидки по направлениям',
  'pr.discountTableHint': 'Меняются в карточке любой услуги направления.',
  'pr.promoHint': 'Промокод даёт скидку на заказ. Со скидкой подписки не складывается — действует большая. Один код — один раз на клиента.',
  'pr.newPromo': 'Новый промокод',
  'pr.promo': 'Промокод',
  'pr.noPromos': 'Промокодов пока нет.',
  'pr.used': 'использован {n} из {max}',
  'pr.until': 'до {date}',
  'pr.state.live': 'действует',
  'pr.state.off': 'выключен',
  'pr.state.expired': 'истёк',
  'pr.state.exhausted': 'исчерпан',
  'pr.state.scheduled': 'ещё не начался',
  'pr.code': 'Код',
  'pr.pct': 'Скидка, %',
  'pr.validFrom': 'Действует с',
  'pr.validUntil': 'по',
  'pr.maxUses': 'Сколько раз всего',
  'pr.maxUsesHint': 'Пусто — без ограничения',
  'pr.forCats': 'На какие направления',
  'pr.forCatsHint': 'Ничего не выбрано — на все',
  'pr.promoNotePh': 'Откуда код: рассылка, блогер, партнёр',
  'pr.promoActive': 'Промокод действует',
  'pr.rel.STEP': 'Шаги переезда',
  'pr.rel.DOC': 'Документы для переезда',
  'pr.relHint.STEP': 'Новые клиенты получают план из этого списка; у текущих шаги добавляются вручную в старой админке.',
  'pr.relHint.DOC': 'Чек-лист документов в приложении клиента.',
  'pr.turnkey': 'под ключ',
  'pr.turnkeyLabel': 'Входит в пакет «под ключ»',
  'pr.docHint': 'Подсказка под названием',
  'pr.relActive': 'Показывать клиентам',
  'no.promo': 'Промокод',
  'no.promoPh': 'Если клиент назвал код',
  'no.promoOk': 'Код {code} применён',
  'no.promoWeaker': 'Скидка по подписке больше — код не нужен',
  'bc.title': 'Рассылка в бот',
  'bc.recipients': 'Получат: {n}',
  'bc.filtersHint': 'Получатели — клиенты из списка с текущими фильтрами, которые дали согласие в боте.',
  'bc.textRu': 'Текст (по-русски)',
  'bc.textPh': 'Например: «С 1 ноября — новые районы для уборки…»',
  'bc.otherLangs': 'Переводы для других языков',
  'bc.clientsN': 'клиентов: {n}',
  'bc.fallback': 'Для {langs} перевода нет — эти клиенты получат текст по-русски.',
  'bc.appButton': 'Кнопка «Открыть приложение» под сообщением',
  'bc.test': 'Прислать себе',
  'bc.testSent': 'Отправили вам в клиентский бот',
  'bc.send': 'Отправить {n}',
  'bc.confirm': 'Точно отправить {n}?',
  'bc.sureHint': 'Отменить отправленное нельзя. Нажмите ещё раз, чтобы запустить.',
  'bc.started': 'Рассылка запущена: {n} получателей',
  'bc.history': 'Рассылки',
  'bc.historyBtn': 'История рассылок',
  'bc.historyHint': 'Отправляем примерно 25 сообщений в секунду — так Telegram не блокирует бота.',
  'bc.none': 'Рассылок ещё не было. Запустить можно из списка клиентов — по любым фильтрам.',
  'bc.status.SENDING': 'идёт',
  'bc.status.DONE': 'отправлено',
  'bc.status.CANCELLED': 'остановлено',
  'bc.sent': 'доставлено {n} из {total}',
  'bc.blocked': 'заблокировали бота: {n}',
  'bc.failed': 'ошибок: {n}',
  'bc.cancel': 'Остановить',
  'bc.cancelConfirm': 'Остановить рассылку? Оставшиеся клиенты сообщение не получат.',
  'jr.title': 'Журнал действий',
  'jr.who': 'Кто',
  'jr.what': 'Раздел',
  'jr.everyone': 'Все сотрудники',
  'jr.allSections': 'Все разделы',
  'jr.days': '{n} дн',
  'jr.empty': 'За этот период действий нет.',
  'jr.system': 'Система',
  'jr.open': 'открыть',
  'jr.details': 'подробности',
  'jr.hide': 'скрыть',
  'jr.more': 'Показать ещё',
  'jr.entity.clients': 'Клиенты',
  'jr.entity.orders': 'Заказы',
  'jr.entity.providers': 'Исполнители',
  'jr.entity.chats': 'Чаты',
  'jr.entity.crm': 'CRM',
  'jr.entity.partners': 'Партнёры',
  'jr.entity.products': 'Продукты',
  'jr.entity.broadcasts': 'Рассылки',
  'jr.entity.settings': 'Настройки',
  'jr.entity.team': 'Команда',
  'jr.entity.segments': 'Сегменты',
  'jr.entity.auth': 'Вход',
  'st.title': 'Настройки',
  'st.saveN': 'Сохранить ({n})',
  'st.group.orders': 'Заказы',
  'st.group.subs': 'Подписка',
  'st.group.online': 'Онлайн-оплата (Tribute)',
  'st.group.agencies': 'Риэлторские агентства',
  'st.group.sales': 'Бонус продажников',
  'st.group.site': 'Цены на сайте',
  'st.group.other': 'Прочее',
  'st.payments': 'Способы оплаты',
  'st.payment': 'Способ оплаты',
  'st.code': 'Код',
  'st.paymentActive': 'Показывать клиентам',
  'err.PROMO_INVALID': 'Такого промокода нет или он выключен.',
  'err.PROMO_EXPIRED': 'Срок промокода истёк или он исчерпан.',
  'err.PROMO_USED': 'Клиент уже использовал этот промокод.',
  'err.PROMO_NOT_FOR_SERVICE': 'Промокод не действует на эту услугу.',
  'err.PROMO_CODE_FORMAT': 'Код — латиница, цифры, «-» и «_», от 3 символов.',
  'err.PROMO_CODE_TAKEN': 'Такой промокод уже есть.',
  'err.SLOT_WINDOW_INVALID': 'Конец окна записи должен быть позже начала.',
  'err.TELEGRAM_NOT_LINKED': 'Привяжите свой Telegram (баннер сверху), чтобы присылать себе проверку.',
  'err.TEST_UNREACHABLE': 'Не дошло: откройте клиентского бота Barev Club хотя бы раз и попробуйте снова.',
  'err.NO_RECIPIENTS': 'По этим фильтрам нет клиентов, которым можно написать.',
  'audit.bo.products.saveTexts': 'изменил(а) тексты услуги',
  'audit.bo.products.saveService': 'изменил(а) цену или настройки услуги',
  'audit.bo.products.saveCategory': 'изменил(а) скидки направления',
  'audit.bo.products.saveChecklist': 'изменил(а) чек-лист услуги',
  'audit.bo.products.savePromo': 'изменил(а) промокод',
  'audit.bo.products.saveRelocation': 'изменил(а) шаблон релокации',
  'audit.bo.products.moveRelocation': 'изменил(а) порядок шагов релокации',
  'audit.bo.broadcasts.send': 'запустил(а) рассылку',
  'audit.bo.broadcasts.test': 'отправил(а) себе тест рассылки',
  'audit.bo.broadcasts.cancel': 'остановил(а) рассылку',
  'audit.bo.settings.save': 'изменил(а) настройки',
  'audit.bo.settings.savePaymentMethod': 'изменил(а) способ оплаты',
  'audit.bo.team.create': 'добавил(а) сотрудника',
  'audit.bo.team.update': 'изменил(а) роли сотрудника',

  // ───── партнёры и CRM (этап 4) ─────
  'crm.title': 'CRM',
  'crm.newLead': 'Новый лид',
  'crm.editLead': 'Лид',
  'crm.stagesBtn': 'Воронка',
  'crm.tab.board': 'Воронка',
  'crm.tab.tasks': 'Задачи',
  'crm.tab.map': 'Карта',
  'crm.search': 'Название, контакт, телефон или адрес',
  'crm.kind.all': 'Все',
  'crm.kind.VENUE': 'Заведения',
  'crm.kind.AGENCY': 'Агентства',
  'crm.kind1.VENUE': 'Заведение',
  'crm.kind1.AGENCY': 'Агентство',
  'crm.owner': 'Продажник',
  'crm.allSales': 'Все продажники',
  'crm.noOwner': 'Без продажника',
  'crm.ownerIs': 'ведёт {name}',
  'crm.connected': 'Подключён',
  'crm.connect': 'Подключение',
  'crm.days': '{n} дн',
  'crm.inStage': 'Сколько дней на этом этапе',
  'crm.dropHere': 'Перетащите сюда',
  'crm.dragHint': 'Перетаскивайте карточки между этапами. «Подключён» сразу создаёт партнёра или агентство и выдаёт ссылку, «Отказ» спросит причину.',
  'crm.overdue': 'просрочена',
  'crm.tasksOpen': 'Открытые',
  'crm.tasksDone': 'Выполненные',
  'crm.addTask': 'Задача',
  'crm.noTasks': 'Открытых задач нет.',
  'crm.noDoneTasks': 'Выполненных задач пока нет.',
  'crm.noLead': 'без лида',
  'crm.tasksHint': 'В срок задачи придёт напоминание в рабочий бот (если привязан Telegram).',
  'crm.taskTitle': 'Что сделать',
  'crm.taskDay': 'День',
  'crm.taskTime': 'Время',
  'crm.taskHint': 'Напоминание придёт в рабочий бот в это время (по Еревану).',
  'crm.tp.call': 'Позвонить',
  'crm.tp.meet': 'Встреча',
  'crm.tp.send': 'Отправить условия',
  'crm.tp.check': 'Узнать решение',
  'crm.partnerOnMap': 'партнёр клуба',
  'crm.legendPartner': 'подключённые партнёры',
  'crm.legendLead': 'лиды (цвет этапа)',
  'crm.mapHint': 'Точка появляется, когда у лида есть координаты.',
  'crm.name': 'Название',
  'crm.namePhVenue': 'Например: Lumen Coffee',
  'crm.namePhAgency': 'Например: Domik Realty',
  'crm.category': 'Тип',
  'crm.contactName': 'Контактное лицо',
  'crm.contactPh': 'Имя и должность',
  'crm.address': 'Адрес',
  'crm.note': 'Заметка',
  'crm.convert': 'Подключить',
  'crm.convertTitle': 'Подключить «{name}»',
  'crm.convertVenueHint': 'Создадим партнёра клубной карты и ссылку для собственника: он откроет рабочий бот и сможет проверять карты клиентов. Предложения по уровням добавите в карточке заведения.',
  'crm.convertAgencyHint': 'Создадим агентство с кодом и ссылку для его клиентов: по ней они получают подарочный месяц и закрепляются за агентством.',
  'crm.agencyCode': 'Код агентства',
  'crm.agencyCodeHint': 'Латиница и цифры, клиенты могут ввести его вручную',
  'crm.convertedTitle': 'Подключено 🎉',
  'crm.convertedVenue': 'Отправьте эту ссылку собственнику заведения. Ссылка одноразовая.',
  'crm.convertedAgency': 'Эту ссылку агентство раздаёт клиентам.',
  'crm.openVenue': 'Карточка заведения',
  'crm.openAgency': 'Карточка агентства',
  'crm.ownerLinked': 'Собственник уже в рабочем боте.',
  'crm.ownerNotYet': 'Собственник ещё не открыл ссылку. Отправьте её ещё раз:',
  'crm.agencyInfo': 'Код {code}, клиентов: {n}',
  'crm.lostTitle': 'Почему отказ?',
  'crm.lostReason': 'Причина',
  'crm.lr.price': 'Не устроили условия',
  'crm.lr.notNow': 'Не сейчас, вернуться позже',
  'crm.lr.noAnswer': 'Не выходят на связь',
  'crm.lr.competitor': 'Работают с конкурентом',
  'crm.lostBanner': 'Отказ: {reason}',
  'crm.stagesTitle': 'Этапы воронки',
  'crm.stagesHint': 'Порядок — стрелками. Нужен хотя бы один открытый этап и один «Подключён». Этап с лидами при удалении скрывается, лиды остаются.',
  'crm.addStage': 'Этап',
  'crm.stageKind.OPEN': 'в работе',
  'crm.stageKind.WON': 'подключён',
  'crm.stageKind.LOST': 'отказ',
  'crm.bonusMonth': 'Бонус за месяц',
  'crm.bonusMineSuper': 'Бонус (ваш), месяц',
  'crm.bonusNotSet': 'не настроен',
  'crm.bonusVenues': 'Заведения: {n} → {sum}',
  'crm.bonusAgencies': 'Агентства: {n} → {sum}',
  'crm.bonusRevenue': '{pct}% выручки клиентов агентств → {sum}',
  'crm.bonusTitle': 'Формула бонуса продажника',
  'crm.bonusHint': 'Бонус за месяц = подключённые заведения × ставка + агентства × ставка + % выручки клиентов агентств продажника (первые N месяцев после подключения). Выручка — оплаченные заказы и подписки.',
  'crm.bonusPerVenue': 'За заведение, ֏',
  'crm.bonusPerAgency': 'За агентство, ֏',
  'crm.bonusPct': '% выручки клиентов агентств',
  'crm.bonusMonths': 'Сколько месяцев',
  'crm.back': 'CRM',
  'crm.created': 'с {date}',
  'crm.stage': 'Этап',
  'crm.history': 'История контактов',
  'crm.noHistory': 'Пока пусто — запишите первый звонок или встречу.',
  'crm.act.CALL': 'Звонок',
  'crm.act.MEETING': 'Встреча',
  'crm.act.NOTE': 'Заметка',
  'crm.act.FILE': 'Файл',
  'crm.act.STAGE': 'Этап',
  'crm.act.WON': 'Подключён',
  'crm.actPh.CALL': 'О чём договорились по телефону?',
  'crm.actPh.MEETING': 'Как прошла встреча, что дальше?',
  'crm.actPh.NOTE': 'Заметка для себя и команды',
  'crm.addAct': 'Записать',
  'crm.attach': 'Фото или файл',
  'crm.tasks': 'Задачи',
  'crm.noLeadTasks': 'Задач нет. Поставьте следующий шаг — придёт напоминание.',
  'crm.contacts': 'Контакты',
  'crm.noCoords': 'На карте нет точки.',
  'crm.coordsPh': 'Ссылка Google Maps или «40.18, 44.51»',
  'crm.coordsBad': 'Не нашли координаты — вставьте ссылку из Google Maps или числа через запятую.',
  'crm.remove': 'Удалить лид',
  'crm.removeConfirm': 'Удалить лид вместе с историей и задачами?',
  'venue.cat.cafe': 'Кафе',
  'venue.cat.restaurant': 'Ресторан',
  'venue.cat.shop': 'Магазин',
  'venue.cat.beauty': 'Красота',
  'venue.cat.other': 'Другое',
  'ag.title': 'Риэлторские агентства',
  'ag.add': 'Новое риэлторское агентство',
  'ag.editTitle': 'Риэлторское агентство',
  'ag.empty': 'Риэлторских агентств пока нет.',
  'ag.col.name': 'Риэлторское агентство',
  'ag.col.clients': 'Клиенты',
  'ag.col.gifts': 'Подарки',
  'ag.col.month': 'Вознаграждение, месяц',
  'ag.col.balance': 'К выплате',
  'ag.hint': 'Вознаграждение — % от оплаченных заказов клиентов агентства, пока они закреплены за ним.',
  'ag.inactive': 'выключено',
  'ag.back': 'Все риэлторские агентства',
  'ag.pct': '{n}% с заказов клиентов',
  'ag.byS': 'подключил(а) {name}',
  'ag.link': 'Ссылка для клиентов',
  'ag.linkHint': 'По ссылке клиент открывает бота, получает подарок агентства и закрепляется за ним. Или вводит код {code} командой /code.',
  'ag.clients': 'Клиенты',
  'ag.giftsN': 'подарков: {n}',
  'ag.noClients': 'Клиентов пока нет.',
  'ag.orders': 'заказов: {n}',
  'ag.toPay': 'Вознаграждение к выплате',
  'ag.accrued': 'Начислено',
  'ag.payouts': 'Выплаты',
  'ag.payoutTitle': 'Выплата агентству',
  'ag.balanceHint': 'Начислено и не выплачено: {sum}',
  'ag.pctLabel': 'Вознаграждение, %',
  'ag.sales': 'Подключил продажник',
  'ag.contactPh': 'Имя, телефон, Telegram',
  'ag.active': 'Агентство работает (код принимается)',
  'vn.title': 'Партнёры клуба',
  'vn.add': 'Новое заведение',
  'vn.editTitle': 'Заведение',
  'vn.empty': 'Заведений пока нет.',
  'vn.col.name': 'Заведение',
  'vn.col.offers': 'Предложения',
  'vn.col.staff': 'Сотрудники',
  'vn.col.visits': 'Проверки за 30 дн',
  'vn.col.last': 'Последняя проверка',
  'vn.hint': 'Проверка — сотрудник заведения отсканировал клубную карту клиента в рабочем боте.',
  'vn.noOwner': 'нет собственника',
  'vn.noOffers': 'нет предложений',
  'vn.back': 'Все заведения',
  'vn.ownerHint': 'Собственник ещё не подключён — отправьте ему одноразовую ссылку в рабочий бот.',
  'vn.makeLink': 'Создать ссылку',
  'vn.offers': 'Предложения по уровням',
  'vn.addOffer': 'Предложение',
  'vn.editOffer': 'Предложение',
  'vn.noOffersHint': 'Добавьте, что положено членам клуба: скидку, подарок, комплимент.',
  'vn.level.any': 'Любой член клуба',
  'vn.level.BAREV_PLUS': 'Barev+ и Elite',
  'vn.level.ELITE': 'Только Elite',
  'vn.limit.NONE': 'без ограничений',
  'vn.limit.ONCE': 'один раз',
  'vn.limit.WEEK': '{n} р. в неделю',
  'vn.limit.MONTH': '{n} р. в месяц',
  'vn.limitOpt.NONE': 'Без ограничений',
  'vn.limitOpt.ONCE': 'Только первый визит',
  'vn.limitOpt.WEEK': 'N раз в неделю',
  'vn.limitOpt.MONTH': 'N раз в месяц',
  'vn.offerOff': 'выключено',
  'vn.offerTitle': 'Что получает клиент',
  'vn.offerTitleHint': 'Например: «Капучино в подарок» или «−15% на всё меню»',
  'vn.forWhom': 'Для кого',
  'vn.howOften': 'Как часто',
  'vn.times': 'Сколько раз',
  'vn.offerActive': 'Предложение действует',
  'vn.activity': 'Активность',
  'vn.weeks8': 'за 8 недель',
  'vn.checks': 'проверки карты',
  'vn.redeemed': 'списания предложений',
  'vn.cardCheck': 'проверка карты',
  'vn.staff': 'Сотрудники',
  'vn.noStaff': 'Пока никого.',
  'vn.role.OWNER': 'собственник',
  'vn.role.STAFF': 'сотрудник',
  'vn.removeStaff': 'Убрать {name} из заведения?',
  'vn.staffHint': 'Сотрудников приглашает собственник в рабочем боте (QR на сутки).',
  'vn.newOwnerLink': 'Ссылка собственнику',
  'vn.newOwnerLinkHint': 'Новая одноразовая ссылка — например, если сменился собственник',
  'vn.blurb': 'Коротко о скидке (на карточке у клиента)',
  'vn.blurbHint': 'Например: «−10% по клубной карте»',
  'vn.coords': 'Точка на карте',
  'vn.activeLabel': 'Заведение видно клиентам',
  'err.LEAD_NOT_CONVERTED': 'Чтобы перевести в «Подключён», нажмите «Подключить» — так создастся партнёр.',
  'err.ALREADY_CONVERTED': 'Лид уже подключён.',
  'err.AGENCY_CODE_FORMAT': 'Код — латиница, цифры и «_», до 32 символов.',
  'err.AGENCY_CODE_TAKEN': 'Такой код уже есть у другого агентства.',
  'err.STAGES_INVALID': 'Нужен хотя бы один открытый этап и один «Подключён».',
  'audit.bo.crm.create': 'добавил(а) лид',
  'audit.bo.crm.update': 'изменил(а) лид',
  'audit.bo.crm.move': 'перевёл(а) лид на другой этап',
  'audit.bo.crm.convert': 'подключил(а) партнёра',
  'audit.bo.crm.addActivity': 'записал(а) контакт',
  'audit.bo.crm.saveStages': 'изменил(а) воронку',
  'audit.bo.crm.setBonusRules': 'изменил(а) формулу бонуса',
  'audit.bo.partners.saveAgency': 'изменил(а) агентство',
  'audit.bo.partners.agencyPayout': 'отметил(а) выплату агентству',
  'audit.bo.partners.saveVenue': 'изменил(а) заведение',
  'audit.bo.partners.saveOffer': 'изменил(а) предложение заведения',
  'audit.bo.partners.ownerInvite': 'выпустил(а) ссылку собственнику',
  'audit.bo.partners.removeMember': 'убрал(а) сотрудника заведения',

  // ───── заказы и исполнители (этап 3) ─────
  'ord.title': 'Заказы',
  'ord.new': 'Новый заказ',
  'ord.search': '№, клиент, телефон, адрес или исполнитель',
  'ord.period': 'Период',
  'ord.period.all': 'Все даты',
  'ord.period.today': 'Сегодня',
  'ord.period.tomorrow': 'Завтра',
  'ord.period.week': '7 дней',
  'ord.period.past': 'Прошедшие',
  'ord.group.active': 'В работе у команды',
  'ord.group.new': 'Ждут исполнителя',
  'ord.group.pending': 'Предложены',
  'ord.group.assigned': 'Назначены',
  'ord.group.in_progress': 'Выполняются',
  'ord.group.done': 'Готово',
  'ord.group.cancelled': 'Отменены',
  'ord.today': 'Сегодня',
  'ord.tomorrow': 'Завтра',
  'ord.empty': 'Заказов с такими условиями нет.',
  'ord.waitsAnswer': 'ждём ответа: {name}',
  'ord.needsHuman': 'нужен диспетчер',
  'ord.searching': 'система подбирает…',
  'ord.overdue': 'время прошло, исполнителя нет',
  'ord.needsDispatcher': 'Автораспределение не нашло исполнителя для заказов: {n}. Назначьте вручную.',
  'ord.autoOn': 'Автораспределение включено',
  'ord.autoOff': 'Автораспределение выключено',
  'ord.autoHint': 'Новый заказ сам предлагается лучшему свободному исполнителю (язык клиента, район, загрузка, рейтинг). Нет ответа {minutes} мин или отказ — следующему; после {tries} попыток — диспетчеру.',
  'ord.back': 'Все заказы',
  'ord.progress': 'Ход заказа',
  'ord.details': 'Детали',
  'ord.when': 'Когда',
  'ord.where': 'Где',
  'ord.minutes': '{n} мин',
  'ord.params': 'Параметры',
  'ord.extras': 'Доп. услуги',
  'ord.note': 'Комментарий клиента',
  'ord.lang': 'Язык клиента',
  'ord.created': 'Создан',
  'ord.checklist': 'Чек-лист',
  'ord.needsPhoto': 'нужно фото',
  'ord.photo': 'Фото',
  'ord.history': 'История',
  'ord.client': 'Клиент',
  'ord.writeClient': 'Написать клиенту',
  'ord.writeProvider': 'Написать исполнителю',
  'ord.call': 'Позвонить',
  'ord.noPhone': 'Телефон исполнителя не указан — добавьте его в профиле',
  'ord.money': 'Деньги',
  'ord.base': 'Без скидки',
  'ord.discount': 'Скидка {pct}%',
  'ord.payout': 'Исполнителю',
  'ord.cancelFee': 'Удержание за отмену',
  'ord.payment': 'Оплата',
  'ord.markPaid': 'Отметить оплату',
  'ord.confirmPaid': 'Отметить, что клиент оплатил заказ?',
  'ord.paidDone': 'Оплата отмечена',
  'ord.actions': 'Действия',
  'ord.setStatus': 'Статус: {status}',
  'ord.confirmStatus': 'Перевести заказ в «{status}»? Клиент получит уведомление.',
  'ord.statusChanged': 'Статус изменён',
  'ord.reschedule': 'Перенести',
  'ord.rescheduleTitle': 'Перенос заказа №{n}',
  'ord.rescheduleHint': 'Если исполнитель занят в новое время, заказ вернётся в «Ждут исполнителя» и уйдёт в автораспределение.',
  'ord.rescheduled': 'Заказ перенесён, исполнитель тот же',
  'ord.rescheduledFree': 'Заказ перенесён; исполнитель занят — ищем нового',
  'ord.comment': 'Комментарий',
  'ord.commentPh': 'Например: клиент попросил по телефону',
  'ord.cancel': 'Отменить заказ',
  'ord.cancelTitle': 'Отмена заказа',
  'ord.cancelReason': 'Причина отмены',
  'ord.cancelReasonPh': 'Например: клиент передумал',
  'ord.cancelFeeHint': 'Сколько удержать с клиента, ֏. Обычно 0.',
  'ord.cancelHint': 'Клиент и исполнитель получат уведомление.',
  'ord.cancelled': 'Заказ отменён',
  'ord.rating': 'Оценка клиента',
  'ord.noComment': 'Без комментария',
  'ord.offers': 'Кому предлагали',
  'ord.bySystem': 'система',
  'ord.provider': 'Исполнитель',
  'ord.unassign': 'Снять исполнителя',
  'ord.revoke': 'Отозвать предложение',
  'ord.confirmUnassign': 'Снять исполнителя? Заказ вернётся в «Ждут исполнителя».',
  'ord.confirmRevoke': 'Отозвать предложение? Заказ снова будет ждать исполнителя.',
  'ord.unassigned': 'Готово — заказ снова ждёт исполнителя',
  'ord.pendingProvider': 'Предложен: {name}',
  'ord.pendingCompany': 'Передан компании «{name}» — ждём, кого назначит диспетчер',
  'ord.byAuto': 'предложила система',
  'ord.minLeft': 'на ответ ≈ {n} мин',
  'ord.pickHint': 'Свободные на это время — по порядку подбора. Или поручите выбор системе.',
  'ord.autoPick': 'Подобрать автоматически',
  'ord.autoOffered': 'Предложили: {name}',
  'ord.autoNone': 'Свободных исполнителей не нашлось',
  'ord.noFree': 'На это время все заняты. Перенесите заказ или договоритесь с исполнителем.',
  'ord.busy': 'Заняты в это время: {n}',
  'ord.company': 'компания',
  'ord.tried': 'уже предлагали',
  'ord.load': 'заказов в этот день: {n}',
  'ord.offer': 'Предложить',
  'ord.offered': 'Заказ отправлен исполнителю',
  'ord.noProvider': 'Исполнитель не назначался.',
  'ord.ev.created': 'Заказ создан',
  'ord.ev.createdByStaff': 'Заказ оформила команда',
  'ord.ev.offered': 'Предложен исполнителю',
  'ord.ev.autoOffered': 'Автораспределение: предложен',
  'ord.ev.autoNoCandidate': 'Автораспределение: свободных нет',
  'ord.ev.autoGaveUp': 'Автораспределение остановлено — нужен диспетчер',
  'no.title': 'Новый заказ',
  'no.client': 'Клиент',
  'no.clientPh': 'Имя, @username или телефон',
  'no.noClients': 'Никого не нашли',
  'no.changeClient': 'Другой клиент',
  'no.service': 'Услуга',
  'no.extras': 'Доп. услуги',
  'no.address': 'Адрес',
  'no.addressPh': 'Улица, дом, квартира',
  'no.district': 'Район',
  'no.when': 'Дата и время',
  'no.day': 'День',
  'no.noSlots': 'На этот день свободного времени нет — выберите другой день или «другое время».',
  'no.otherTime': 'Другое время (договорились с исполнителем сами)',
  'no.customTime': 'Время',
  'no.customHint': 'По Еревану. Свободных исполнителей система не гарантирует.',
  'no.note': 'Комментарий для исполнителя',
  'no.notePh': 'Код домофона, животные…',
  'no.payment': 'Оплата',
  'no.hint': 'Цена — с учётом подписки клиента. Клиент получит уведомление в бота, заказ сразу уйдёт в автораспределение.',
  'no.create': 'Оформить заказ',
  'no.created': 'Заказ №{n} оформлен',
  'prv.title': 'Исполнители',
  'prv.add': 'Добавить',
  'prv.search': 'Имя или телефон',
  'prv.status.active': 'Работают',
  'prv.status.blocked': 'Заблокированы',
  'prv.status.all': 'Все',
  'prv.empty': 'Никого не нашли.',
  'prv.col.name': 'Исполнитель',
  'prv.col.rating': 'Рейтинг',
  'prv.col.done': 'Заказов',
  'prv.col.refused': 'Отказы',
  'prv.col.next': 'Ближайший заказ',
  'prv.period30': 'Заказы, отказы и низкие оценки — за 30 дней.',
  'prv.blocked': 'заблокирован',
  'prv.onShift': 'на смене',
  'prv.offShift': 'не на смене',
  'prv.notLinked': 'не открыл бота',
  'prv.noDocs': 'нет документов',
  'prv.ratings': 'оценок: {n}',
  'prv.low': 'низких: {n}',
  'prv.today': 'сегодня: {n}',
  'prv.back': 'Все исполнители',
  'prv.write': 'Написать',
  'prv.edit': 'Редактировать',
  'prv.blockedSince': 'Заблокирован с {date}.',
  'prv.unblock': 'Вернуть к работе',
  'prv.inviteHint': 'Исполнитель ещё не открыл рабочий бот — отправьте ему ссылку.',
  'prv.copyInvite': 'Скопировать ссылку',
  'prv.quality': 'Качество',
  'prv.days30': 'за 30 дней',
  'prv.acceptRate': 'принял {a} из {n} предложений',
  'prv.refusals': 'отказы: {d}, молчал: {e}',
  'prv.lowRatings': 'оценок 3 и ниже',
  'prv.upcoming': 'Ближайшие заказы',
  'prv.noUpcoming': 'Назначенных заказов нет.',
  'prv.recent': 'Последние выполненные',
  'prv.balance': 'К выплате',
  'prv.thisMonth': 'Этот месяц',
  'prv.prevMonth': 'Прошлый',
  'prv.paidTotal': 'Выплачено',
  'prv.payout': 'Отметить выплату',
  'prv.payoutTitle': 'Выплата исполнителю',
  'prv.amount': 'Сумма, ֏',
  'prv.balanceHint': 'Долг перед исполнителем: {sum}',
  'prv.payoutNote': 'Как выплатили',
  'prv.payoutNotePh': 'Наличные / перевод на карту…',
  'prv.payoutConfirm': 'Выплачено',
  'prv.payoutDone': 'Выплата отмечена',
  'prv.rateCompany': 'ставка компании {v}',
  'prv.rateOwn': 'ставка {v}',
  'prv.ratePreset': 'по пресету «{name}»',
  'prv.work': 'Работа',
  'prv.languages': 'Языки',
  'prv.districts': 'Районы',
  'prv.anyDistrict': 'любые',
  'prv.districtsHint': 'Подбор ставит выше тех, кто работает в районе заказа.',
  'prv.docs': 'Документы для допуска',
  'prv.noDocsHint': 'Паспорт, договор, справка — всё хранится в закрытом хранилище.',
  'prv.docTitlePh': 'Название: паспорт, договор…',
  'prv.upload': 'Загрузить',
  'prv.docAdded': 'Документ добавлен',
  'prv.removeDocConfirm': 'Удалить документ?',
  'prv.access': 'Допуск',
  'prv.block': 'Заблокировать',
  'prv.blockTitle': 'Заблокировать исполнителя',
  'prv.blockReason': 'Причина',
  'prv.blockReasonPh': 'Например: жалобы клиентов, нет документов',
  'prv.blockHint': 'Заказы ему больше не предлагаются, действующие предложения отзываются. Уже назначенные заказы остаются — переназначьте их вручную.',
  'prv.resetInvite': 'Новая ссылка для входа в бот',
  'prv.resetConfirm': 'Сделать новую ссылку? Текущая привязка к Telegram снимется — исполнителю нужно будет войти заново.',
  'prv.since': 'С нами с {date}',
  'prv.saved': 'Сохранено',
  'prv.addTitle': 'Новый исполнитель',
  'prv.editTitle': 'Профиль исполнителя',
  'prv.name': 'Имя',
  'prv.role': 'Роль',
  'prv.company': 'Компания',
  'prv.freelance': 'Частный исполнитель',
  'prv.schedule': 'График',
  'prv.rate': 'Ставка',
  'prv.note': 'Заметка команды',
  'err.REASON_REQUIRED': 'Укажите причину.',
  'err.SLOT_UNAVAILABLE': 'Это время уже занято или прошло. Выберите другое или «другое время».',
  'err.ALREADY_ASSIGNED': 'Заказ уже назначен или предложен — обновите страницу.',
  'err.PROVIDER_BUSY': 'Исполнитель занят в это время.',
  'err.TRANSITION_NOT_ALLOWED': 'Сейчас этот шаг для заказа недоступен.',
  'err.CHECKLIST_INCOMPLETE': 'Сначала исполнитель должен закрыть чек-лист и приложить фото.',
  'err.CONSENT_REQUIRED': 'Клиент ещё не дал согласие на обработку данных в боте — заказ оформить нельзя.',
  'err.ADDRESS_REQUIRED': 'Укажите адрес и район.',
  'err.INVALID_PARAMS': 'Проверьте параметры заказа.',
  'err.OFFER_NOT_ACTIVE': 'Предложение уже неактуально.',
  'err.BAD_FILE_TYPE_DOC': 'Можно фото или PDF.',
  'audit.bo.orders.assign': 'предложил(а) заказ исполнителю',
  'audit.bo.orders.auto': 'запустил(а) подбор исполнителя',
  'audit.bo.orders.unassign': 'снял(а) исполнителя с заказа',
  'audit.bo.orders.transition': 'сменил(а) статус заказа',
  'audit.bo.orders.cancel': 'отменил(а) заказ',
  'audit.bo.orders.reschedule': 'перенёс(ла) заказ',
  'audit.bo.orders.markPaid': 'отметил(а) оплату заказа',
  'audit.bo.orders.create': 'оформил(а) заказ за клиента',
  'audit.bo.orders.setAutoSetting': 'изменил(а) автораспределение',
  'audit.bo.providers.create': 'добавил(а) исполнителя',
  'audit.bo.providers.update': 'изменил(а) профиль исполнителя',
  'audit.bo.providers.setActive': 'изменил(а) допуск исполнителя',
  'audit.bo.providers.resetInvite': 'выпустил(а) новую ссылку исполнителю',
  'audit.bo.providers.removeDoc': 'удалил(а) документ исполнителя',
  'audit.bo.providers.addDoc': 'загрузил(а) документ исполнителя',
  'audit.bo.providers.payout': 'отметил(а) выплату исполнителю',
  'chat.order': 'Заказ №{n}',
  'chat.noOrder': 'Без заказа',
  'chat.noOrderNow': 'Сейчас без заказа',
  'chat.orderNow': 'Сейчас',
  'chat.orderNext': 'Ближайший',
  'chat.orderNoNext': 'Ближайших заказов нет',
  'chat.orderClient': 'клиент {name}',
  'chat.wroteNoOrder': 'писал(а) без заказа',
  'err.CONCIERGE_SUBSCRIBERS_ONLY': 'Консьерж — только для подписчиков. Сначала выдайте клиенту подписку.',
  'err.ASSIGNEE_NO_ACCESS': 'У этого сотрудника нет доступа к этому каналу.',
  'err.PROVIDER_NOT_LINKED': 'Исполнитель ещё не открыл рабочий бот — написать ему пока некуда.',
  'err.BAD_FILE_TYPE': 'Такой файл не подойдёт. Можно фото, PDF, Word, Excel, текст или ZIP.',
  'audit.bo.chats.send': 'ответил(а) в чате',
  'audit.bo.chats.sendFile': 'отправил(а) файл в чате',
  'audit.bo.chats.assign': 'назначил(а) ответственного в чате',
  'audit.bo.chats.close': 'закрыл(а) диалог',
  'audit.bo.chats.reopen': 'открыл(а) диалог снова',
  'audit.bo.chats.move': 'перенёс(ла) диалог в другой канал',
  'audit.bo.chats.openFor': 'открыл(а) чат с клиентом',
  'audit.bo.chats.templates.save': 'изменил(а) шаблон ответа',
  'audit.bo.chats.templates.remove': 'удалил(а) шаблон ответа',
  'nav.servicePartners': 'Партнёры услуг',
  'st.group.partners': 'Партнёры услуг',
  'ag.giftPlan': 'Подарок новым клиентам',
  'ag.giftMonths': 'На сколько',
  'ag.giftHint': 'Действует для новых клиентов; у тех, кто уже получил подарок, ничего не меняется.',
  'ag.noGift': 'без подарка',
  'ag.giftValue': '{plan} на {n} мес.',
  'ag.monthsN': '{n} мес.',
  'ord.partner': 'партнёр',
  'ord.best': 'выгоднее всего',
  'ord.partnerFree': 'свободно {n} из {of}',
  'ord.candMoney': 'Платим {cost} · нам остаётся {margin}',
  'prv.ratePartnerPays': 'Зарплату платит партнёр',
  'rq.missing': 'не хватает данных',
  'rq.noTime': 'не указано',
  'rq.noDistrict': 'район не указан',
  'rq.noAddress': 'адрес не указан',
  'sp.title': 'Партнёры услуг',
  'sp.add': 'Новый партнёр',
  'sp.editTitle': 'Партнёр услуг',
  'sp.lead': 'Клининговые компании со своими сотрудниками. Менеджер партнёра получает ссылку, сам добавляет сотрудников и распределяет заказы. Заказ партнёру назначает оператор в карточке заказа.',
  'sp.empty': 'Партнёров пока нет.',
  'sp.col.name': 'Партнёр',
  'sp.col.staff': 'Сотрудники',
  'sp.col.active': 'Заказы в работе',
  'sp.col.month': 'Доход партнёра, месяц',
  'sp.col.balance': 'К выплате',
  'sp.noManager': 'нет менеджера',
  'sp.auto': 'автораспределение',
  'sp.back': 'Все партнёры услуг',
  'sp.terms.COMMISSION': 'как Uber: наши {pct}% с заказа',
  'sp.terms.PROFIT_SHARE': 'расходы наши: ФОТ {fot}%, налог {tax}%, партнёру {pct}% прибыли',
  'sp.managerLink': 'Ссылка для менеджера',
  'sp.managerLinkHint': 'Отдайте ссылку менеджеру партнёра: он откроет рабочий бот и получит кабинет — заказы, сотрудники, автораспределение, доход. Кабинет открывается и на компьютере (кнопка в боте).',
  'sp.resetLink': 'Новая ссылка',
  'sp.resetConfirm': 'Сделать новую ссылку? Старая перестанет работать, подключённые менеджеры останутся.',
  'sp.linkReset': 'Ссылка обновлена',
  'sp.managers': 'Менеджеры',
  'sp.noManagers': 'Менеджер ещё не подключился по ссылке.',
  'sp.managerSince': 'с {date}',
  'sp.remove': 'Убрать',
  'sp.removeManagerConfirm': 'Убрать {name} из менеджеров партнёра?',
  'sp.staff': 'Сотрудники',
  'sp.staffHint': 'добавляет менеджер партнёра',
  'sp.noStaff': 'Сотрудников пока нет.',
  'sp.limitOrders': 'до {n} заказов в день',
  'sp.limitHours': 'до {n} ч в день',
  'sp.notLinked': 'не вошёл в бот',
  'sp.off': 'выключен',
  'sp.orders': 'Заказы в работе',
  'sp.noOrders': 'Сейчас заказов у партнёра нет.',
  'sp.waitingStaff': 'ждёт сотрудника',
  'sp.toPay': 'Доход партнёра к выплате',
  'sp.byOrder': 'Доход по заказам',
  'sp.noIncome': 'Доход появится, когда клиент оплатит выполненный заказ.',
  'sp.paid': 'оплачено {sum}',
  'sp.fotTax': 'ФОТ {fot}, налог {tax}',
  'sp.payoutTitle': 'Выплата партнёру',
  'sp.phone': 'Телефон',
  'sp.service': 'Услуга',
  'sp.cleaningOnly': 'Клининг (пока только он)',
  'sp.deal': 'Модель сотрудничества',
  'sp.deal.COMMISSION': 'Как Uber: наш % с заказа',
  'sp.deal.PROFIT_SHARE': 'Расходы наши: % партнёру',
  'sp.dealHint.COMMISSION': 'Мы забираем свой % с оплаченного заказа, остальное — партнёру. Зарплаты сотрудникам и расходы — его.',
  'sp.dealHint.PROFIT_SHARE': 'ФОТ клинеру и налог платим мы; партнёру — его % от того, что осталось: оплачено − ФОТ − налог.',
  'sp.commissionPct': 'Наш %, с оплаченного заказа',
  'sp.profitSharePct': '% партнёра от прибыли',
  'sp.fotPct': 'ФОТ клинера, % от заказа',
  'sp.taxPct': 'Налог, %',
  'sp.defaultHint': 'пусто — из настроек ({n}%)',
  'sp.exampleCOMMISSION': 'Пример: заказ оплачен на {sum} → нам {ours}, партнёру {partner}.',
  'sp.examplePROFIT_SHARE': 'Пример: оплачено {sum} − ФОТ {fot} − налог {tax} = {profit}; партнёру {partner}, нам {ours}.',
  'sp.activeLabel': 'Партнёр работает (можно назначать заказы)',
  'nav.points': 'Барики',
  'st.group.points': 'Барики (кэшбек)',
  'ag.hwPoints': 'Барики на новоселье',
  'ag.hwDays': 'Срок, дней',
  'ag.hwHint': '0 — не дарим. Начисляются один раз, сразу доступны.',
  'ag.hwValue': '{n} бариков на {days} дн.',
  'pt.title': 'Барики',
  'pt.tab.accrue': 'Начислить',
  'pt.tab.requests': 'Запросы',
  'pt.tab.suggestions': 'Предложено системой',
  'pt.tab.ledger': 'Журнал',
  'pt.tab.report': 'Отчёт',
  'pt.tab.import': 'Импорт CSV',
  'pt.tab.setup': 'Ставки и бонусы',
  'pt.search': 'Клиент',
  'pt.searchPh': 'Телефон, @username, имя или ID',
  'pt.notFound': 'Никого не нашли',
  'pt.pickClient': 'Найдите клиента слева — здесь появится форма начисления.',
  'pt.flagged': 'проверить: много отмен',
  'pt.balance': 'Доступно',
  'pt.pending': 'В ожидании',
  'pt.availableFrom': 'с {date}',
  'pt.tier': 'Уровень',
  'pt.burnAt': 'Сгорят',
  'pt.agency': 'Агентство',
  'pt.streak': 'Серия уборок',
  'pt.debt': 'Долг',
  'pt.redeem': 'Списать',
  'pt.redeemBtn': 'Списать',
  'pt.redeemed': 'Списано',
  'pt.allOps': 'Все операции',
  'pt.noOps': 'Операций пока нет.',
  'pt.reverse': 'Отменить',
  'pt.accrueTitle': 'Начисление',
  'pt.kind.ORDER': 'За заказ',
  'pt.kind.BONUS': 'Бонус',
  'pt.f.category': 'Категория',
  'pt.f.orderAmount': 'Сумма заказа, ֏',
  'pt.f.excluded': 'Не облагается, ֏',
  'pt.f.excludedHint': 'пошлины, материалы, чаевые',
  'pt.f.paidWithPoints': 'Оплачено бариками',
  'pt.f.serviceDate': 'Дата услуги',
  'pt.f.orderRef': 'Номер заказа',
  'pt.f.orderRefHint': 'второй раз по тому же номеру начислить нельзя',
  'pt.f.bonusType': 'Тип бонуса',
  'pt.f.ttl': 'Срок жизни, дней',
  'pt.f.ttlDefault': 'по умолчанию {n}',
  'pt.f.ttlNone': 'пусто — общее правило сгорания',
  'pt.f.amount': 'Сумма бариков',
  'pt.f.amountHint': 'подсказка: {n}',
  'pt.f.reason': 'Причина',
  'pt.f.reasonRequired': 'Причина (обязательно)',
  'pt.f.comment': 'Комментарий для клиента',
  'pt.f.commentPh': 'Увидит в истории',
  'pt.f.noWait': 'Без ожидания — сразу доступны',
  'pt.f.noWaitReason': 'Без ожидания (нужна причина)',
  'pt.f.redeemAmount': 'Сколько списать',
  'pt.f.defaultAmount': 'Сумма по умолчанию',
  'pt.formula': '{tier}: {rate}%{extra} → {n} бариков',
  'pt.accrueBtn': 'Начислить',
  'pt.confirmTitle': 'Проверьте',
  'pt.edit': 'Изменить',
  'pt.confirmOrder': 'Начислить {n} бариков {name} за {what} {sum} ֏ ({tier}, {rate}%). Станут доступны {date}.',
  'pt.confirmBonus': 'Начислить {n} бариков {name}: {what}, {until}.',
  'pt.now': 'сразу',
  'pt.forDays': 'на {n} дн.',
  'pt.noTtl': 'без своего срока',
  'pt.changedNote': 'Сумма изменена против подсказки ({n}) — попадёт в журнал с вашей причиной.',
  'pt.accrued': 'Начислено {n}',
  'pt.needsApproval': 'Выше лимита — отправлено суперадмину на подтверждение',
  'pt.zero': 'Сумма 0 — начислять нечего',
  'pt.reverseAccrual': 'Отменить начисление',
  'pt.reverseRedemption': 'Отменить списание',
  'pt.reverseAccrualText': 'Начисление {n} бариков будет отменено. Исходная запись останется в журнале.',
  'pt.reverseRedemptionText': '{n} бариков вернутся в исходные начисления с их сроками.',
  'pt.debtWarn': 'Из начисления уже потрачено {spent} — баланс уйдёт в минус: {after}. Минус погасится следующими начислениями.',
  'pt.reasonPh': 'Возврат денег, ошибка…',
  'pt.reverseHint': 'Поддержка отменяет только свои операции и только в течение 24 часов.',
  'pt.reversed': 'Отменено',
  'pt.maxHint': 'максимум {max} ({share}% заказа и не больше баланса), минимум {min}',
  'pt.redeemReasonPh': 'По звонку, заказ вне приложения…',
  'pt.toPay': 'К доплате: {sum} ֏',
  'pt.noRequests': 'Запросов нет.',
  'pt.wants': 'хочет списать {n}',
  'pt.forOrder': 'заказ №{n}',
  'pt.noOrder': 'заказ вне приложения',
  'pt.balanceN': 'баланс {n}',
  'pt.confirm': 'Подтвердить',
  'pt.confirmN': 'Списать {n}',
  'pt.confirmed': 'Списано {n}',
  'pt.reject': 'Отклонить',
  'pt.rejectReason': 'Причина — её увидит клиент',
  'pt.noSuggestions': 'Предложений нет.',
  'pt.suggestionsHint': 'Система нашла, кому положен бонус. Начислите, измените сумму или отклоните.',
  'pt.mech.first_month_route': 'Маршрут первого месяца',
  'pt.mech.photo_review': 'Отзыв с фото',
  'pt.mech.over_limit': 'Выше лимита поддержки',
  'pt.why.first_month_route': '3 разные категории за первые 30 дней',
  'pt.why.photo_review': 'прислал фото после заказа №{n}',
  'pt.why.over_limit': '{by}: {op}',
  'pt.op.accrual': 'начисление',
  'pt.op.redemption': 'списание',
  'pt.approve': 'Подтвердить',
  'pt.changeAmount': 'Изменить сумму',
  'pt.allTypes': 'Все типы',
  'pt.allCategories': 'Все категории',
  'pt.allChannels': 'Все каналы',
  'pt.allClients': 'Все клиенты',
  'pt.type.accrual': 'Начисление',
  'pt.type.bonus': 'Бонус',
  'pt.type.redemption': 'Списание',
  'pt.type.request': 'Запрос клиента',
  'pt.type.reversal': 'Отмена',
  'pt.channel.WEB': 'админка',
  'pt.channel.BOT': 'бот',
  'pt.channel.CSV': 'CSV',
  'pt.channel.AUTO': 'авто',
  'pt.channel.CLIENT': 'клиент',
  'pt.status.PENDING': 'в ожидании',
  'pt.status.ACTIVE': 'активно',
  'pt.status.SPENT': 'потрачено',
  'pt.status.EXPIRED': 'сгорело',
  'pt.status.REVERSED': 'отменено',
  'pt.status.REQUESTED': 'ждёт',
  'pt.status.CONFIRMED': 'списано',
  'pt.status.REJECTED': 'отклонено',
  'pt.status.ACCRUAL': 'начисления',
  'pt.status.REDEMPTION': 'списания',
  'pt.col.date': 'Когда',
  'pt.col.client': 'Клиент',
  'pt.col.what': 'Что',
  'pt.col.amount': 'Барики',
  'pt.col.status': 'Статус',
  'pt.col.author': 'Кто',
  'pt.system': 'система',
  'pt.suggestedN': 'подсказка {n}',
  'pt.debtN': 'в долг {n}',
  'pt.rep.accrued': 'Начислено',
  'pt.rep.spent': 'Потрачено',
  'pt.rep.expired': 'Сгорело',
  'pt.rep.reversed': 'Отменено',
  'pt.rep.liabilities': 'Обязательства сейчас',
  'pt.rep.rate': 'Доля потраченных',
  'pt.rep.byCategory': 'Стоимость программы по категориям',
  'pt.rep.cost': '{pct}% от выручки {revenue} ֏',
  'pt.rep.retention': 'Повторный заказ за 30 дней',
  'pt.rep.with': 'С бариками',
  'pt.rep.without': 'Без бариков',
  'pt.rep.clientsN': 'клиентов {n}, с 2+ категориями {multi}',
  'pt.rep.topClients': 'Топ по балансу',
  'pt.rep.topAgencies': 'Агентства: активации новоселья',
  'pt.importHint': 'CSV: телефон, категория (cleaning, documents, learning, repair, transfer), сумма заказа, дата (ГГГГ-ММ-ДД), номер заказа. Сначала — предпросмотр, начисление только после подтверждения.',
  'pt.importRows': 'Строк',
  'pt.importTotal': 'Бариков всего',
  'pt.importErr.client': 'клиент не найден',
  'pt.importErr.category': 'нет такой категории',
  'pt.importErr.date': 'неверная дата',
  'pt.importErr.duplicate': 'дубль заказа',
  'pt.importCommit': 'Начислить {n} строк',
  'pt.importDone': 'Начислено по {n} строкам',
  'pt.categories': 'Категории и ставки',
  'pt.bonusTypes': 'Типы бонусов',
  'pt.code': 'Код',
  'pt.activeLabel': 'Работает',
  'pt.rulesHint': 'Доступны через {hours} ч после услуги · оплатить можно до {share}% заказа, минимум {min} · сгорают через {burn} дней без активности. Меняется в «Настройках».',
  'err.POINTS_DUPLICATE_ORDER': 'За этот заказ барики уже начислены.',
  'err.POINTS_REASON_REQUIRED': 'Укажите причину.',
  'err.POINTS_BELOW_MIN': 'Меньше минимальной суммы списания.',
  'err.POINTS_NOT_ENOUGH': 'Не хватает бариков.',
  'err.POINTS_OVER_SHARE': 'Больше допустимой доли заказа.',
  'err.POINTS_OVER_LIMIT': 'Выше вашего лимита — нужно подтверждение суперадмина.',
  'err.POINTS_FINAL': 'Эту операцию уже нельзя отменить.',
  'err.POINTS_REQUEST_CLOSED': 'Запрос уже обработан или истёк.',
  'err.POINTS_WITH_PROMO': 'Барики нельзя вместе с промокодом.',
  'pt.formulaStreak': ' + серия уборок {streak}: +{bonus}%',
  'ord.editComposition': 'Изменить состав',
  'ord.editTitle': 'Состав заказа №{n}',
  'ord.editPrice': 'Цена: было {was} → станет {now} (скидка {pct}%, как при оформлении)',
  'ord.editDuration': 'Длительность: {was} → {now} мин',
  'ord.editBusy': 'Заказ стал дольше, а у исполнителя в это время другой заказ — проверьте график.',
  'ord.editPaid': 'Заказ уже оплачен: пересчитаем вознаграждение агентства, доход партнёра и барики. Разницу в деньгах решите с клиентом.',
  'ord.editOwnPrice': 'Своя итоговая цена, ֏',
  'ord.editOwnPriceHint': 'пусто — по прайсу: {n}',
  'ord.editReason': 'Причина',
  'ord.editReasonRequired': 'Причина (обязательно для своей цены)',
  'ord.editReasonPh': 'На месте добавили окна…',
  'ord.editNotify': 'Сообщить клиенту и исполнителю',
  'ord.edited': 'Состав заказа изменён',
  'ord.editedBusy': 'Изменено. Внимание: исполнитель может не успеть — проверьте график',
  'ord.uncheck': 'Снять отметку',
  'ord.checkForProvider': 'Отметить за исполнителя',
  'ord.noPhotoReason': 'без фото: {reason}',
  'ord.byStaff': 'отметила команда',
  'ord.checkNoPhoto': 'Отметить без фото',
  'ord.photoHint': 'Для этого пункта нужно фото. Загрузите его или отметьте без фото с причиной.',
  'ord.uploadPhoto': 'Загрузить фото',
  'ord.noPhotoField': 'Или причина, почему без фото',
  'ord.noPhotoPh': 'Клинер прислал фото в чат…',
  'err.PHOTO_REQUIRED': 'Нужно фото или причина, почему без него.',
};

export type BoKey = keyof typeof ru;
type Dict = Record<BoKey, string>;

const en: Dict = {
  'ui.save': 'Save',
  'ui.close': 'Close',
  'ui.loading': 'Loading…',
  'ui.error': 'That didn’t work: {msg}',
  'ui.retry': 'Try again',
  'ui.copy': 'Copy',
  'ui.copied': 'Copied',
  'ui.days': '{n} d',
  'ui.yes': 'Yes',
  'ui.no': 'No',
  'ui.none': '—',

  'err.PAYMENTS_NOT_CONFIGURED': 'Online payments aren’t connected yet — mark the payment manually.',
  'err.REFUND_TOO_BIG': 'The refund is larger than what the client paid.',
  'err.REFUND_UNAVAILABLE': 'Tribute won’t refund this payment (already refunded or disputed with the bank).',
  'err.PAYMENT_PROVIDER_ERROR': 'Tribute didn’t respond or refused. Try later or do it in the Tribute dashboard.',
  'err.PAYMENT_UNAVAILABLE': 'Online payments aren’t configured on the server.',
  'err.ENDS_BEFORE_START': 'A subscription can’t end before it starts.',
  'err.CANNOT_DEMOTE_SELF': 'You can’t disable yourself or remove your own super admin role.',
  'err.ROLE_FORBIDDEN': 'Your role doesn’t have access to this action.',
  'err.NO_ROLE': 'Your account has no roles in the new admin. Ask a super admin to grant them.',

  'login.coverWord': 'team',
  'login.title': 'Team sign-in',
  'login.lead': 'Confirm sign-in in the Barev Club work bot. No password needed.',
  'login.open': 'Confirm in Telegram',
  'login.qr': 'On a computer, scan the code with your phone.',
  'login.waiting': 'Waiting for confirmation in Telegram…',
  'login.expired': 'The link has expired.',
  'login.retry': 'Get a new link',
  'login.dev': 'Local sign-in',
  'login.devHint': 'Only on this computer — the server doesn’t show this block.',
  'login.noTelegram': 'No access? Ask a super admin to add you to the team and send a Telegram link.',

  'nav.dashboard': 'Dashboard',
  'nav.clients': 'Clients',
  'nav.chats': 'Chats',
  'nav.orders': 'Orders',
  'nav.providers': 'Providers',
  'nav.agencies': 'Real estate agencies',
  'nav.venues': 'Club partners',
  'nav.crm': 'CRM',
  'nav.products': 'Products',
  'nav.journal': 'Activity log',
  'nav.team': 'Team',
  'nav.more': 'More',
  'nav.soon': 'stage {n}',
  'nav.groupWork': 'Work',
  'nav.groupPartners': 'Partners',
  'nav.groupSetup': 'Setup',

  'role.SUPERADMIN': 'Super admin',
  'role.SUPPORT': 'Support',
  'role.CONCIERGE': 'Concierge',
  'role.SALES': 'Sales',
  'roleHint.SUPERADMIN': 'Everything, including money and the team',
  'roleHint.SUPPORT': 'Support chat, orders, subscriptions',
  'roleHint.CONCIERGE': 'Concierge chat for subscribers',
  'roleHint.SALES': 'CRM: venues and agencies',

  'shell.logout': 'Sign out',
  'shell.oldAdmin': 'Old admin',
  'shell.linkTg': 'Link Telegram — you sign in and get notifications through it.',
  'shell.linkTgBtn': 'Link Telegram',

  'soon.title': '{section} is moving here',
  'soon.text': 'This section arrives in stage {n}. Until then, use the old admin — the data is shared.',
  'soon.open': 'Open the old admin',

  'dash.morning': 'Good morning, {name}',
  'dash.day': 'Good afternoon, {name}',
  'dash.evening': 'Good evening, {name}',
  'dash.attention': 'Needs attention',
  'dash.allClear': 'Nothing urgent — all caught up.',
  'att.unassigned': 'Orders without a provider',
  'att.unreadChats': 'Chats awaiting reply',
  'att.webRequests': 'Website requests',
  'att.leads': 'Relocation requests',
  'att.appRequests': 'In-app requests',
  'att.lowRatings': 'Ratings of 3 or lower this week',
  'att.expiring': 'Subscriptions ending within 7 days',
  'dash.money': 'Money',
  'dash.today': 'Today',
  'dash.week': '7 days',
  'dash.month': '30 days',
  'dash.vsYesterday': 'yesterday {sum}',
  'dash.vsPrev': 'vs previous {period}',
  'dash.orders': 'orders',
  'dash.subs': 'subscriptions',
  'dash.chart': 'Daily revenue, last 30 days',
  'dash.subsTitle': 'Subscriptions',
  'dash.gifts': 'Gifts among them: {n}',
  'dash.expiring': 'Ending in the next 7 days',
  'dash.noExpiring': 'Nothing ends in the coming week.',
  'dash.conversion': 'Gift → purchase',
  'dash.conversionHint': '{paid} of {total} gifted clients later bought a subscription',
  'dash.growth': 'Growth',
  'dash.newClients': 'New clients',
  'dash.prevMonth': 'previous 30 days — {n}',
  'dash.sources': 'Where they came from, 30 days',
  'dash.funnel': 'Funnel, 30 days',
  'funnel.leads': 'Website requests',
  'funnel.clients': 'Clients in the app',
  'funnel.paying': 'Paid',
  'dash.team': 'Team and partners',
  'dash.topProviders': 'Top providers',
  'dash.bottomProviders': 'Worth a talk',
  'dash.topPartners': 'Venues: card scans, 30 days',
  'dash.salesSoon': 'Sales results will appear with the CRM.',
  'dash.empty': 'Nothing yet',
  'dash.feed': 'Feed',
  'feed.subscription': '{plan} · {source}',
  'feed.payment': 'Order #{number} paid',
  'dash.ratings': '{n} ratings',
  'dash.scans': '{n} scans',

  'source.organic': 'Found us',
  'source.deeplink': 'Agency link',
  'source.manual_code': 'Agency code',
  'source.agency': 'Agency “{name}”',
  'source.utm': 'Ads: {name}',

  'plan.BAREV_PLUS': 'Barev+',
  'plan.ELITE': 'Elite',
  'plan.none': 'No subscription',
  'subSource.PAID': 'Purchase',
  'subSource.MANUAL': 'Team gift',
  'subSource.AGENCY_GIFT': 'Agency gift',
  'subStatus.ACTIVE': 'Active',
  'subStatus.EXPIRED': 'Expired',
  'subStatus.CANCELLED': 'Cancelled',
  'pay.cash': 'Cash',
  'pay.transfer': 'Bank transfer',
  'pay.card': 'Card via terminal',
  'pay.link': 'Payment link',
  'pay.tribute': 'Tribute (online)',
  'segment.MOVING': 'Relocating',
  'segment.LIVING': 'Lives in Armenia',

  'clients.title': 'Clients',
  'clients.search': 'Name, @username, phone or card number',
  'clients.found': 'Found: {n}',
  'clients.revenueTotal': 'brought {sum}',
  'col.client': 'Client',
  'col.sub': 'Subscription',
  'col.source': 'Source',
  'col.revenue': 'Brought',
  'col.since': 'Joined',
  'filter.plan': 'Subscription',
  'filter.all': 'All',
  'filter.any': 'Subscribed',
  'filter.none': 'Not subscribed',
  'filter.subSource': 'How received',
  'filter.source': 'Source',
  'filter.agency': 'Agency',
  'filter.tag': 'Tag',
  'filter.segment': 'Scenario',
  'filter.paying': 'Paid',
  'filter.payingYes': 'Have paid',
  'filter.payingNo': 'Never paid',
  'filter.expiring': 'Ends within 7 days',
  'filter.reset': 'Reset filters',
  'filter.more': 'Filters',
  'filter.anyValue': 'Any',
  'sort.label': 'Order',
  'sort.new': 'Newest first',
  'sort.revenue': 'Brought the most',
  'sort.ends': 'Ending soon',
  'segments.title': 'My segments',
  'segments.save': 'Save segment',
  'segments.name': 'Segment name',
  'segments.saved': 'Segment saved',
  'segments.remove': 'Delete segment “{name}”',
  'clients.export': 'Download for Excel',
  'clients.exported': 'File downloaded',
  'clients.broadcast': 'Broadcast',
  'clients.broadcastSoon': 'Segment broadcasts arrive in stage 5.',
  'clients.empty': 'No one matches these filters.',
  'clients.emptyAll': 'No clients yet — they appear once they message the bot.',
  'clients.prev': 'Back',
  'clients.next': 'Next',
  'clients.page': '{from}–{to} of {total}',

  'card.back': 'All clients',
  'card.since': 'With us since {date}',
  'card.telegram': 'Open in Telegram',
  'card.cardNo': 'Card {code}',
  'card.noSub': 'No active subscription',
  'card.noSubHint': 'Gift or sell a subscription — the client gets discounts right away.',
  'card.until': 'until {date}',
  'card.daysLeft': '{n} d left',
  'card.history': 'All subscriptions',
  'card.grantedBy': 'granted by {name}',
  'card.giftFrom': 'gift from “{name}”',
  'card.paidVia': '{method}',
  'card.refunded': 'refund {sum}',
  'card.cancelledAt': 'cancelled {date}',
  'card.upcoming': 'starts {date}',
  'card.money': 'Brought',
  'card.fromOrders': 'orders',
  'card.fromSubs': 'subscriptions',
  'card.orders': 'Orders',
  'card.noOrders': 'No orders yet.',
  'card.newOrder': 'Create order',
  'card.newOrderSoon': 'Ordering on a client’s behalf arrives in stage 3, together with the orders section.',
  'card.profile': 'Profile',
  'card.source': 'Source',
  'card.agency': 'Agency',
  'card.language': 'Language',
  'card.phone': 'Phone',
  'card.segment': 'Scenario',
  'card.consent': 'Data processing consent',
  'card.tags': 'Tags',
  'card.addTag': 'New tag',
  'card.note': 'Team note',
  'card.notePh': 'The client doesn’t see this note',
  'card.saved': 'Saved',
  'card.activity': 'Client log',
  'card.noActivity': 'The team hasn’t changed anything yet.',
  'card.stats': 'Activity',
  'stat.cardUsages': 'card scans',
  'stat.leads': 'relocation requests',
  'stat.requests': 'in-app requests',
  'stat.concierge': 'chat messages',

  'act.gift': 'Gift',
  'act.sell': 'Sell',
  'act.extend': 'Change term',
  'act.cancel': 'Cancel',
  'act.changePlan': 'Change plan',
  'act.refund': 'Refund',
  'op.title': 'Online payments (Tribute)',
  'op.empty': 'No online payments yet. Memberships and orders paid by card via Tribute will appear here, with “Stop auto-renewal” and “Refund” buttons.',
  'op.disabled': 'Online payments aren’t configured on the server.',
  'op.paidAt': 'paid {{date}}',
  'op.refunded': 'refunded',
  'op.nextCharge': 'auto-renewal, next charge {{date}}',
  'op.renewOff': 'auto-renewal off',
  'op.attention': '⚠ the order was already paid or cancelled — refund needed',
  'op.stop': 'Stop auto-renewal',
  'op.stopConfirm': 'Stop auto-renewal? The paid month stays, no further charges.',
  'op.stopped': 'Auto-renewal stopped',
  'op.refundConfirmSub': 'Refund the latest payment? Auto-renewal stops and the current period is cancelled.',
  'op.refundConfirmOrder': 'Refund this order to the client’s card?',
  'op.refundStarted': 'Refund sent to Tribute — status updates once Tribute confirms',
  'op.hint': 'Money goes back to the client’s card via Tribute for the latest payment.',

  'audit.bo.clients.gift': 'gifted a subscription',
  'audit.bo.clients.sell': 'sold a subscription',
  'audit.bo.clients.extend': 'changed the subscription term',
  'audit.bo.clients.cancel': 'cancelled a subscription',
  'audit.bo.clients.changePlan': 'changed the plan',
  'audit.bo.clients.refund': 'issued a refund',
  'audit.bo.clients.setTags': 'changed tags',
  'audit.bo.clients.setNote': 'changed the note',

  'dlg.giftTitle': 'Gift a subscription',
  'dlg.giftHint': 'It will be marked as a gift from you.',
  'dlg.sellTitle': 'Sell a subscription',
  'dlg.plan': 'Plan',
  'dlg.period': 'Term',
  'dlg.period1': '1 month',
  'dlg.period3': '3 months',
  'dlg.period6': '6 months',
  'dlg.period12': '1 year',
  'dlg.periodCustom': 'Custom',
  'dlg.days': 'Days',
  'dlg.amount': 'Amount, ֏',
  'dlg.method': 'Paid by',
  'dlg.note': 'Note for the team',
  'dlg.linkSoon': 'Payment links turn on once a payment provider is connected.',
  'dlg.giftConfirm': 'Gift',
  'dlg.sellConfirm': 'Mark as paid',
  'dlg.extendTitle': 'Change term',
  'dlg.extendHint': 'A negative number shortens the term.',
  'dlg.newEnd': 'New end date: {date}',
  'dlg.extendConfirm': 'Save term',
  'dlg.cancelTitle': 'Cancel subscription',
  'dlg.cancelHint': 'The client loses subscription discounts and perks immediately.',
  'dlg.reason': 'Reason',
  'dlg.cancelConfirm': 'Cancel subscription',
  'dlg.planTitle': 'Change plan',
  'dlg.planConfirm': 'Change plan',
  'dlg.refundTitle': 'Refund',
  'dlg.refundPaid': 'Paid {sum}, already refunded {back}',
  'dlg.refundCancel': 'Cancel the subscription too',
  'dlg.refundConfirm': 'Issue refund',
  'done.gift': 'Subscription gifted',
  'done.sell': 'Payment recorded',
  'done.extend': 'Term changed',
  'done.cancel': 'Subscription cancelled',
  'done.changePlan': 'Plan changed',
  'done.refund': 'Refund issued',

  'team.title': 'Team',
  'team.lead': 'Who works in the admin and what they can access. One person can have several roles.',
  'team.add': 'Add a person',
  'team.name': 'Name',
  'team.roles': 'Roles',
  'team.create': 'Add',
  'team.linkTitle': 'Link for {name}',
  'team.linkHint': 'Send it to them. They open the work bot, Telegram gets linked, and they can sign in.',
  'team.tgLinked': 'Telegram linked',
  'team.tgNotLinked': 'Telegram not linked',
  'team.newLink': 'Linking link',
  'team.disable': 'Disable access',
  'team.enable': 'Restore access',
  'team.disabled': 'Access disabled',
  'team.you': 'you',
  'team.saved': 'Roles saved',

  // ───── чаты (этап 2) ─────
  'chat.title': 'Chats',
  'chat.all': 'All',
  'channel.SUPPORT': 'Support',
  'channel.CONCIERGE': 'Concierge',
  'chat.f.waiting': 'Awaiting reply',
  'chat.f.mine': 'Mine',
  'chat.f.unassigned': 'Unassigned',
  'chat.f.open': 'Open',
  'chat.f.closed': 'Closed',
  'chat.f.all': 'All',
  'chat.search': 'Name, @username, phone or text',
  'chat.emptyList': 'Nothing here. New messages will appear on their own.',
  'chat.emptyFilter': 'Nothing found — try another filter.',
  'chat.pick': 'Pick a conversation',
  'chat.pickHint': 'Those waiting longest are on top.',
  'chat.waiting': 'waiting {t}',
  'chat.you': 'You',
  'chat.provider': 'Provider',
  'chat.unassigned': 'Nobody assigned',
  'chat.take': 'Take it',
  'chat.transfer': 'Pass on',
  'chat.transferTo': 'Pass to a colleague',
  'chat.unassign': 'Remove assignee',
  'chat.close': 'Close',
  'chat.reopen': 'Reopen',
  'chat.closedBanner': 'The conversation is closed. It reopens when the client writes; you can still reply now.',
  'chat.moveTo': 'Move to {channel}',
  'chat.moved': 'Moved to {channel}',
  'chat.movedAway': 'Moved to {channel}. You have no access to that channel.',
  'chat.other': 'There is also a {channel} conversation',
  'chat.otherOpen': 'Open',
  'chat.bot': 'The AI concierge is handling this conversation.',
  'chat.botTake': 'Take over',
  'chat.reply': 'Reply',
  'chat.note': 'Note',
  'chat.replyPh': 'Reply to the client — goes to their Telegram',
  'chat.notePh': 'Note for the team — the client won’t see it',
  'chat.send': 'Send',
  'chat.saveNote': 'Save note',
  'chat.attach': 'Attach a file',
  'chat.templates': 'Templates',
  'chat.tplManage': 'Manage templates',
  'chat.tplEmpty': 'No templates yet.',
  'chat.tplNew': 'New template',
  'chat.tplTitle': 'Title',
  'chat.tplText': 'Text',
  'chat.tplTextHint': '{name} is replaced with the client’s name',
  'chat.tplChannel': 'Channel',
  'chat.tplBoth': 'Both channels',
  'chat.tplDelete': 'Delete',
  'chat.tplSaved': 'Template saved',
  'chat.tplSearch': 'Find a template',
  'chat.noteLabel': 'Note · team only',
  'chat.fromApp': 'from the app',
  'chat.undelivered': 'Not delivered: the client blocked the bot or never opened it',
  'chat.fileTooBig': 'The file is larger than 10 MB',
  'chat.enterHint': 'Enter to send · Shift+Enter for a new line',
  'chat.providerNotLinked': 'The provider hasn’t opened the work bot yet — you can only leave a note for now.',
  'chat.client': 'About the client',
  'chat.aboutProvider': 'About the provider',
  'chat.openCard': 'Client card',
  'chat.write': 'Message',
  'chat.back': 'All conversations',
  'chat.sys.took': '{name} took the conversation',
  'chat.sys.transferred': '{name} passed the conversation to {param}',
  'chat.sys.unassigned': '{name} removed the assignee',
  'chat.sys.closed': '{name} closed the conversation',
  'chat.sys.reopened': '{name} reopened the conversation',
  'chat.sys.moved': '{name} moved the conversation to {param}',
  'chat.ago.now': 'just now',
  'chat.ago.min': '{n} min',
  'chat.ago.h': '{n} h',
  'chat.ago.d': '{n} d',
  'chat.today': 'Today',
  'chat.yesterday': 'Yesterday',
  'chat.image': 'Photo',
  'chat.file': 'File',
  'chat.subUntil': 'until {date}',
  'chat.orders': 'Orders',
  'chat.phone': 'Phone',
  'chat.lang': 'Language',
  'chat.source': 'Source',
  'chat.linked': 'Work bot opened',
  'chat.notLinked': 'Work bot not opened',
  'chat.details': 'Details',
  'chat.more': 'More actions',
  'ord.tab.orders': 'Orders',
  'ord.tab.moves': 'Relocations',
  'ord.tab.requests': 'Requests',
  'mv.search': 'Name, contact, agency or #',
  'mv.empty': 'No relocations with this status.',
  'mv.all': 'All',
  'mv.steps': 'Steps {done} of {total}',
  'mv.docs': 'Documents {done}/{total}',
  'mv.now': 'Now: {step}',
  'mv.noPlan': 'No plan yet',
  'mv.back': 'All relocations',
  'mv.plan': 'Relocation plan',
  'mv.planHintApp': 'The client sees the steps in the app and gets a bot message when a step status changes.',
  'mv.planHintNoApp': 'The client hasn’t opened the app yet — only you see the plan.',
  'mv.docsTitle': 'Client documents',
  'mv.docsHint': 'The client ticks them in the app, you — here.',
  'mv.answers': 'Quiz answers',
  'mv.when': 'Moving date',
  'mv.package': 'Package',
  'mv.wants': 'Needs',
  'mv.agency': 'Agency',
  'mv.lang': 'Language',
  'mv.created': 'Request',
  'mv.unfinished': 'didn’t finish the quiz',
  'mv.note': 'Team note',
  'mv.notePh': 'For the team: called, what was promised, who leads…',
  'mv.save': 'Save',
  'mv.status': 'Relocation status',
  'mv.contact': 'Contact',
  'mv.write': 'Message',
  'mv.call': 'Call',
  'mv.openClient': 'Client card',
  'mv.inApp': 'in the app',
  'mv.add': 'Add',
  'mv.addStep': 'Step from the list…',
  'mv.addDoc': 'Document from the list…',
  'mv.custom': 'Custom item…',
  'mv.customPh': 'Name for this client',
  'mv.stepNote': 'Comment for the client — shown in the app',
  'mv.remove': 'Remove for this client',
  'mv.removeConfirm': 'Remove “{name}” for this client?',
  'mv.rename': 'Rename',
  'mv.listHint': 'The shared list of steps and documents is set up in Products.',
  'rq.site': 'From the website',
  'rq.app': 'From the app',
  'rq.open': 'Open',
  'rq.closed': 'Closed',
  'rq.siteHint': 'Cleaning booked on barevclub.com without sign-up. Call, confirm the time and create an order.',
  'rq.appHint': 'Clients asked for a membership or a call about a service without online booking.',
  'rq.empty': 'No open requests.',
  'rq.emptyClosed': 'No closed requests yet.',
  'rq.callback': 'Call back: {service}',
  'rq.newOrder': 'Create order',
  'rq.wish': 'Preferred time',
  'rq.price': 'Website price',
  'rq.noApp': 'hasn’t opened the bot',
  'rq.done': 'Contacted',
  'rq.cancel': 'Cancel',
  'rq.reopen': 'Reopen',
  'rq.activate': 'Turn on for 30 days',
  'rq.activated': 'Membership turned on',
  'rq.order': 'Order #{n}',
  'audit.bo.moves.update': 'updated a relocation',
  'audit.bo.moves.updateStep': 'updated a relocation step',
  'audit.bo.moves.addStep': 'added a relocation step',
  'audit.bo.moves.removeStep': 'removed a relocation step',
  'audit.bo.moves.addDoc': 'added a relocation document',
  'audit.bo.moves.updateDoc': 'updated a relocation document',
  'audit.bo.moves.removeDoc': 'removed a relocation document',
  'audit.bo.requests.updateSite': 'updated a website request',
  'audit.bo.requests.updateApp': 'updated an app request',
  'audit.bo.requests.activate': 'turned on a membership from a request',
  'pr.newService': 'New service',
  'pr.newServiceHint': 'From scratch with a fixed price and your own questions, or as a copy of an existing service.',
  'pr.fromScratch': 'From scratch',
  'pr.copyOf': 'Copy of existing',
  'pr.category': 'Category',
  'pr.copyFrom': 'Copy service',
  'pr.copyHint': 'Prices, questions, checklist and booking window are copied — change what you need. E.g. an “Office deep clean” from cleaning.',
  'pr.who': 'Who does it',
  'pr.whoHint': 'Decides who gets the order and which steps the provider sees. Repairs and lessons — only as a copy.',
  'pr.flowHint.CLEANER': 'on the way → work → done, with a checklist',
  'pr.flowHint.DRIVER': 'driver arrived → riding → done',
  'pr.flowHint.CAREER_CONSULTANT': 'consultant → materials → meeting → result',
  'pr.flowHint.DOCS_AGENT': 'document check → submission → done',
  'pr.online': 'Online — no address or visit',
  'pr.namePh': 'E.g. Window cleaning',
  'pr.create': 'Create service',
  'pr.createHint': 'The service starts switched off: set price, questions and checklist, then switch it on — it appears in the app right away.',
  'pr.created': 'Service created — set it up and switch it on',
  'pr.questions': 'Questions in the order form',
  'pr.questionsHint': 'What the client picks or types when ordering. The provider and team see the answers.',
  'pr.qLabel': 'Question, e.g. “How many windows?”',
  'pr.qType.select': 'choice',
  'pr.qType.int': 'number',
  'pr.qType.text': 'text',
  'pr.qType.bool': 'yes / no',
  'pr.qOption': 'Option {n}',
  'pr.qAddOption': 'option',
  'pr.qFrom': 'from',
  'pr.qTo': 'to',
  'pr.qRequired': 'required',
  'pr.qAdd': 'Question',
  'pr.qNone': 'No questions — the client picks only time and address.',
  'pr.delete': 'Delete service',
  'pr.deleteConfirm': 'Delete the service? Only possible without orders — otherwise switch it off.',
  'err.SERVICE_HAS_ORDERS': 'The service has orders — switch it off instead.',
  'err.OPTIONS_REQUIRED': 'A choice question needs at least 2 options.',
  'err.PARAMS_LOCKED': 'This service’s price depends on the answers — questions are changed by a developer.',
  'audit.bo.products.createService': 'created a service',
  'audit.bo.products.saveParams': 'edited service questions',
  'audit.bo.products.deleteService': 'deleted a service',
  'audit.bo.products.moveService': 'reordered services',
  'audit.bo.crm.addTask': 'added a task',
  'audit.bo.crm.setTaskDone': 'updated a task',
  'audit.bo.crm.removeTask': 'deleted a task',
  'audit.bo.crm.remove': 'deleted a lead',
  'audit.bo.segments.save': 'saved a client segment',
  'audit.bo.segments.remove': 'deleted a client segment',
  'audit.bo.team.linkUrl': 'issued a Telegram link',
  'audit.bo.auth.telegramLink': 'is linking their Telegram',
  'audit.bo.auth.logout': 'signed out',
  'audit.bo.clients.exportCsv': 'exported clients to Excel',

  // ───── продукты, рассылки, журнал, настройки (этап 5) ─────
  'nav.settings': 'Settings',
  'pr.title': 'Products',
  'pr.tab.services': 'Services & prices',
  'pr.tab.plans': 'Membership & discounts',
  'pr.tab.promos': 'Promo codes',
  'pr.tab.relocation': 'Relocation',
  'pr.readOnly': 'Only a superadmin can change prices and terms — shown here for reference.',
  'pr.catDiscounts': 'discounts: Barev+ −{plus}%, Elite −{elite}%',
  'pr.on': 'on',
  'pr.off': 'off',
  'pr.from': 'from {sum}',
  'pr.orders30': '{n} orders in 30 d',
  'pr.setup': 'Set up',
  'pr.back': 'All services',
  'pr.content': 'Content',
  'pr.contentHint': 'How the service is named in the app, bot and notifications. Empty = the standard text (shown in grey). Bots pick up changes within a minute.',
  'pr.name': 'Name',
  'pr.description': 'Description',
  'pr.extrasNames': 'Extras names ({n})',
  'pr.price': 'Price and duration',
  'pr.fixedPrice': 'Price, ֏',
  'pr.minutes': 'Duration, min',
  'pr.base': 'Base, ֏',
  'pr.perRoom': '+ room, ֏',
  'pr.perBath': '+ bathroom, ֏',
  'pr.baseMin': 'Base, min',
  'pr.perRoomMin': '+ room, min',
  'pr.perBathMin': '+ bathroom, min',
  'pr.roomsHint': 'Base covers 1 room and 1 bathroom; each extra room and bathroom is added on top.',
  'pr.extras': 'Extras',
  'pr.unitCol': 'Unit',
  'pr.extraPrice': 'Price, ֏',
  'pr.extraMin': '+ min',
  'pr.unit.item': 'per item',
  'pr.unit.service': 'service',
  'pr.unit.hour': 'per hour',
  'pr.unit.halfHour': 'per 30 min',
  'pr.priceHint': 'New prices apply to new orders; existing orders don’t change. Membership discounts apply on top.',
  'pr.settings': 'Booking and cancellation',
  'pr.activeLabel': 'Service available to clients',
  'pr.windowFrom': 'Booking from',
  'pr.windowTo': 'until',
  'pr.step': 'Slot step, min',
  'pr.lead': 'Not sooner than, h',
  'pr.horizon': 'Bookable days ahead',
  'pr.lateFee': 'Late cancellation fee, %',
  'pr.discounts': 'Membership discounts',
  'pr.discountsHint': 'Apply to the whole “{cat}” category.',
  'pr.checklist': 'Provider checklist',
  'pr.photo': 'photo',
  'pr.newItem': 'New item',
  'pr.checklistHint': 'The provider ticks items in the work bot; “photo” items need a photo. Applies to new orders.',
  'pr.plans': 'Membership plans',
  'pr.plansSuper': 'Plan prices are visible to the superadmin.',
  'pr.plansHint': '“Up to” is shown on the site and in the app; actual discounts are in the table and services.',
  'pr.discountTable': 'Discounts by category',
  'pr.discountTableHint': 'Change them in any service of the category.',
  'pr.promoHint': 'A promo code gives an order discount. It doesn’t stack with membership — the larger applies. One use per client.',
  'pr.newPromo': 'New promo code',
  'pr.promo': 'Promo code',
  'pr.noPromos': 'No promo codes yet.',
  'pr.used': 'used {n} of {max}',
  'pr.until': 'until {date}',
  'pr.state.live': 'live',
  'pr.state.off': 'off',
  'pr.state.expired': 'expired',
  'pr.state.exhausted': 'used up',
  'pr.state.scheduled': 'not started',
  'pr.code': 'Code',
  'pr.pct': 'Discount, %',
  'pr.validFrom': 'Valid from',
  'pr.validUntil': 'until',
  'pr.maxUses': 'Total uses',
  'pr.maxUsesHint': 'Empty — unlimited',
  'pr.forCats': 'Categories',
  'pr.forCatsHint': 'Nothing selected — all',
  'pr.promoNotePh': 'Where it’s from: newsletter, blogger, partner',
  'pr.promoActive': 'Promo code active',
  'pr.rel.STEP': 'Relocation steps',
  'pr.rel.DOC': 'Relocation documents',
  'pr.relHint.STEP': 'New clients get their plan from this list; existing clients are edited individually.',
  'pr.relHint.DOC': 'The documents checklist in the client app.',
  'pr.turnkey': 'turnkey',
  'pr.turnkeyLabel': 'Included in the turnkey package',
  'pr.docHint': 'Hint under the name',
  'pr.relActive': 'Show to clients',
  'no.promo': 'Promo code',
  'no.promoPh': 'If the client gave a code',
  'no.promoOk': 'Code {code} applied',
  'no.promoWeaker': 'The membership discount is bigger — no need for the code',
  'bc.title': 'Bot broadcast',
  'bc.recipients': 'Recipients: {n}',
  'bc.filtersHint': 'Recipients are the clients matching the current filters who gave consent in the bot.',
  'bc.textRu': 'Text (Russian)',
  'bc.textPh': 'E.g. “From 1 November — new districts for cleaning…”',
  'bc.otherLangs': 'Translations for other languages',
  'bc.clientsN': 'clients: {n}',
  'bc.fallback': 'No translation for {langs} — those clients get the Russian text.',
  'bc.appButton': '“Open the app” button under the message',
  'bc.test': 'Send to me',
  'bc.testSent': 'Sent to you in the client bot',
  'bc.send': 'Send to {n}',
  'bc.confirm': 'Really send to {n}?',
  'bc.sureHint': 'Sent messages can’t be recalled. Click again to start.',
  'bc.started': 'Broadcast started: {n} recipients',
  'bc.history': 'Broadcasts',
  'bc.historyBtn': 'Broadcast history',
  'bc.historyHint': 'We send about 25 messages per second so Telegram doesn’t throttle the bot.',
  'bc.none': 'No broadcasts yet. Start one from the clients list with any filters.',
  'bc.status.SENDING': 'sending',
  'bc.status.DONE': 'done',
  'bc.status.CANCELLED': 'stopped',
  'bc.sent': 'delivered {n} of {total}',
  'bc.blocked': 'blocked the bot: {n}',
  'bc.failed': 'errors: {n}',
  'bc.cancel': 'Stop',
  'bc.cancelConfirm': 'Stop the broadcast? Remaining clients won’t get it.',
  'jr.title': 'Activity log',
  'jr.who': 'Who',
  'jr.what': 'Section',
  'jr.everyone': 'All staff',
  'jr.allSections': 'All sections',
  'jr.days': '{n} d',
  'jr.empty': 'No activity for this period.',
  'jr.system': 'System',
  'jr.open': 'open',
  'jr.details': 'details',
  'jr.hide': 'hide',
  'jr.more': 'Show more',
  'jr.entity.clients': 'Clients',
  'jr.entity.orders': 'Orders',
  'jr.entity.providers': 'Providers',
  'jr.entity.chats': 'Chats',
  'jr.entity.crm': 'CRM',
  'jr.entity.partners': 'Partners',
  'jr.entity.products': 'Products',
  'jr.entity.broadcasts': 'Broadcasts',
  'jr.entity.settings': 'Settings',
  'jr.entity.team': 'Team',
  'jr.entity.segments': 'Segments',
  'jr.entity.auth': 'Sign-in',
  'st.title': 'Settings',
  'st.saveN': 'Save ({n})',
  'st.group.orders': 'Orders',
  'st.group.subs': 'Membership',
  'st.group.online': 'Online payment (Tribute)',
  'st.group.agencies': 'Real estate agencies',
  'st.group.sales': 'Sales bonus',
  'st.group.site': 'Website prices',
  'st.group.other': 'Other',
  'st.payments': 'Payment methods',
  'st.payment': 'Payment method',
  'st.code': 'Code',
  'st.paymentActive': 'Show to clients',
  'err.PROMO_INVALID': 'No such promo code, or it’s off.',
  'err.PROMO_EXPIRED': 'The promo code has expired or is used up.',
  'err.PROMO_USED': 'The client has already used this code.',
  'err.PROMO_NOT_FOR_SERVICE': 'The code doesn’t apply to this service.',
  'err.PROMO_CODE_FORMAT': 'Code: Latin letters, digits, “-” and “_”, 3+ characters.',
  'err.PROMO_CODE_TAKEN': 'This code already exists.',
  'err.SLOT_WINDOW_INVALID': 'The booking window must end after it starts.',
  'err.TELEGRAM_NOT_LINKED': 'Link your Telegram (banner at the top) to send yourself a test.',
  'err.TEST_UNREACHABLE': 'Not delivered: open the Barev Club client bot once and try again.',
  'err.NO_RECIPIENTS': 'No clients to message with these filters.',
  'audit.bo.products.saveTexts': 'edited service texts',
  'audit.bo.products.saveService': 'edited a service’s price or settings',
  'audit.bo.products.saveCategory': 'edited category discounts',
  'audit.bo.products.saveChecklist': 'edited a service checklist',
  'audit.bo.products.savePromo': 'edited a promo code',
  'audit.bo.products.saveRelocation': 'edited a relocation template',
  'audit.bo.products.moveRelocation': 'reordered relocation steps',
  'audit.bo.broadcasts.send': 'started a broadcast',
  'audit.bo.broadcasts.test': 'sent a test broadcast',
  'audit.bo.broadcasts.cancel': 'stopped a broadcast',
  'audit.bo.settings.save': 'changed settings',
  'audit.bo.settings.savePaymentMethod': 'edited a payment method',
  'audit.bo.team.create': 'added a team member',
  'audit.bo.team.update': 'changed team roles',

  // ───── партнёры и CRM (этап 4) ─────
  'crm.title': 'CRM',
  'crm.newLead': 'New lead',
  'crm.editLead': 'Lead',
  'crm.stagesBtn': 'Pipeline',
  'crm.tab.board': 'Pipeline',
  'crm.tab.tasks': 'Tasks',
  'crm.tab.map': 'Map',
  'crm.search': 'Name, contact, phone or address',
  'crm.kind.all': 'All',
  'crm.kind.VENUE': 'Venues',
  'crm.kind.AGENCY': 'Agencies',
  'crm.kind1.VENUE': 'Venue',
  'crm.kind1.AGENCY': 'Agency',
  'crm.owner': 'Sales rep',
  'crm.allSales': 'All sales reps',
  'crm.noOwner': 'Unassigned',
  'crm.ownerIs': 'owned by {name}',
  'crm.connected': 'Connected',
  'crm.connect': 'Connecting',
  'crm.days': '{n} d',
  'crm.inStage': 'Days in this stage',
  'crm.dropHere': 'Drop here',
  'crm.dragHint': 'Drag cards between stages. “Connected” creates the partner or agency and gives you a link; “Lost” asks for a reason.',
  'crm.overdue': 'overdue',
  'crm.tasksOpen': 'Open',
  'crm.tasksDone': 'Done',
  'crm.addTask': 'Task',
  'crm.noTasks': 'No open tasks.',
  'crm.noDoneTasks': 'No completed tasks yet.',
  'crm.noLead': 'no lead',
  'crm.tasksHint': 'When a task is due you get a reminder in the work bot (if Telegram is linked).',
  'crm.taskTitle': 'What to do',
  'crm.taskDay': 'Day',
  'crm.taskTime': 'Time',
  'crm.taskHint': 'A reminder comes to the work bot at this time (Yerevan).',
  'crm.tp.call': 'Call',
  'crm.tp.meet': 'Meeting',
  'crm.tp.send': 'Send the terms',
  'crm.tp.check': 'Ask for a decision',
  'crm.partnerOnMap': 'club partner',
  'crm.legendPartner': 'connected partners',
  'crm.legendLead': 'leads (stage colour)',
  'crm.mapHint': 'A dot appears once a lead has coordinates.',
  'crm.name': 'Name',
  'crm.namePhVenue': 'E.g. Lumen Coffee',
  'crm.namePhAgency': 'E.g. Domik Realty',
  'crm.category': 'Type',
  'crm.contactName': 'Contact person',
  'crm.contactPh': 'Name and position',
  'crm.address': 'Address',
  'crm.note': 'Note',
  'crm.convert': 'Connect',
  'crm.convertTitle': 'Connect “{name}”',
  'crm.convertVenueHint': 'We’ll create a club card partner and an owner link: the owner opens the work bot and can check client cards. Add offers in the venue card.',
  'crm.convertAgencyHint': 'We’ll create the agency with a code and a client link: clients get a gift month and are attached to the agency.',
  'crm.agencyCode': 'Agency code',
  'crm.agencyCodeHint': 'Latin letters and digits; clients can type it in',
  'crm.convertedTitle': 'Connected 🎉',
  'crm.convertedVenue': 'Send this link to the venue owner. It works once.',
  'crm.convertedAgency': 'The agency shares this link with clients.',
  'crm.openVenue': 'Venue card',
  'crm.openAgency': 'Agency card',
  'crm.ownerLinked': 'The owner is already in the work bot.',
  'crm.ownerNotYet': 'The owner hasn’t opened the link yet. Send it again:',
  'crm.agencyInfo': 'Code {code}, clients: {n}',
  'crm.lostTitle': 'Why lost?',
  'crm.lostReason': 'Reason',
  'crm.lr.price': 'Terms didn’t fit',
  'crm.lr.notNow': 'Not now, come back later',
  'crm.lr.noAnswer': 'Not responding',
  'crm.lr.competitor': 'Work with a competitor',
  'crm.lostBanner': 'Lost: {reason}',
  'crm.stagesTitle': 'Pipeline stages',
  'crm.stagesHint': 'Reorder with arrows. You need at least one open and one “Connected” stage. A stage with leads is hidden, not deleted.',
  'crm.addStage': 'Stage',
  'crm.stageKind.OPEN': 'open',
  'crm.stageKind.WON': 'connected',
  'crm.stageKind.LOST': 'lost',
  'crm.bonusMonth': 'Bonus this month',
  'crm.bonusMineSuper': 'Bonus (yours), month',
  'crm.bonusNotSet': 'not set',
  'crm.bonusVenues': 'Venues: {n} → {sum}',
  'crm.bonusAgencies': 'Agencies: {n} → {sum}',
  'crm.bonusRevenue': '{pct}% of agency client revenue → {sum}',
  'crm.bonusTitle': 'Sales bonus formula',
  'crm.bonusHint': 'Monthly bonus = connected venues × rate + agencies × rate + % of revenue from the rep’s agency clients (first N months). Revenue = paid orders and subscriptions.',
  'crm.bonusPerVenue': 'Per venue, ֏',
  'crm.bonusPerAgency': 'Per agency, ֏',
  'crm.bonusPct': '% of agency client revenue',
  'crm.bonusMonths': 'For how many months',
  'crm.back': 'CRM',
  'crm.created': 'since {date}',
  'crm.stage': 'Stage',
  'crm.history': 'Contact history',
  'crm.noHistory': 'Empty so far — log the first call or meeting.',
  'crm.act.CALL': 'Call',
  'crm.act.MEETING': 'Meeting',
  'crm.act.NOTE': 'Note',
  'crm.act.FILE': 'File',
  'crm.act.STAGE': 'Stage',
  'crm.act.WON': 'Connected',
  'crm.actPh.CALL': 'What did you agree on the call?',
  'crm.actPh.MEETING': 'How did the meeting go, what’s next?',
  'crm.actPh.NOTE': 'A note for yourself and the team',
  'crm.addAct': 'Log it',
  'crm.attach': 'Photo or file',
  'crm.tasks': 'Tasks',
  'crm.noLeadTasks': 'No tasks. Set the next step — you’ll get a reminder.',
  'crm.contacts': 'Contacts',
  'crm.noCoords': 'Not on the map yet.',
  'crm.coordsPh': 'Google Maps link or “40.18, 44.51”',
  'crm.coordsBad': 'No coordinates found — paste a Google Maps link or two numbers.',
  'crm.remove': 'Delete lead',
  'crm.removeConfirm': 'Delete the lead with its history and tasks?',
  'venue.cat.cafe': 'Café',
  'venue.cat.restaurant': 'Restaurant',
  'venue.cat.shop': 'Shop',
  'venue.cat.beauty': 'Beauty',
  'venue.cat.other': 'Other',
  'ag.title': 'Real estate agencies',
  'ag.add': 'New real estate agency',
  'ag.editTitle': 'Real estate agency',
  'ag.empty': 'No real estate agencies yet.',
  'ag.col.name': 'Real estate agency',
  'ag.col.clients': 'Clients',
  'ag.col.gifts': 'Gifts',
  'ag.col.month': 'Reward, month',
  'ag.col.balance': 'To pay',
  'ag.hint': 'Reward = % of paid orders of the agency’s clients while they’re attached.',
  'ag.inactive': 'inactive',
  'ag.back': 'All real estate agencies',
  'ag.pct': '{n}% of client orders',
  'ag.byS': 'connected by {name}',
  'ag.link': 'Client link',
  'ag.linkHint': 'The client opens the bot, gets the agency gift and is attached to the agency. Or types code {code} via /code.',
  'ag.clients': 'Clients',
  'ag.giftsN': 'gifts: {n}',
  'ag.noClients': 'No clients yet.',
  'ag.orders': 'orders: {n}',
  'ag.toPay': 'Reward to pay',
  'ag.accrued': 'Accrued',
  'ag.payouts': 'Payouts',
  'ag.payoutTitle': 'Payout to agency',
  'ag.balanceHint': 'Accrued and unpaid: {sum}',
  'ag.pctLabel': 'Reward, %',
  'ag.sales': 'Connected by',
  'ag.contactPh': 'Name, phone, Telegram',
  'ag.active': 'Agency active (code accepted)',
  'vn.title': 'Club partners',
  'vn.add': 'New venue',
  'vn.editTitle': 'Venue',
  'vn.empty': 'No venues yet.',
  'vn.col.name': 'Venue',
  'vn.col.offers': 'Offers',
  'vn.col.staff': 'Staff',
  'vn.col.visits': 'Checks, 30 d',
  'vn.col.last': 'Last check',
  'vn.hint': 'A check = venue staff scanned a client’s club card in the work bot.',
  'vn.noOwner': 'no owner',
  'vn.noOffers': 'no offers',
  'vn.back': 'All venues',
  'vn.ownerHint': 'The owner isn’t connected yet — send them a one-time work bot link.',
  'vn.makeLink': 'Create link',
  'vn.offers': 'Offers by level',
  'vn.addOffer': 'Offer',
  'vn.editOffer': 'Offer',
  'vn.noOffersHint': 'Add what members get: a discount, a gift, a treat.',
  'vn.level.any': 'Any member',
  'vn.level.BAREV_PLUS': 'Barev+ and Elite',
  'vn.level.ELITE': 'Elite only',
  'vn.limit.NONE': 'unlimited',
  'vn.limit.ONCE': 'once',
  'vn.limit.WEEK': '{n}× a week',
  'vn.limit.MONTH': '{n}× a month',
  'vn.limitOpt.NONE': 'Unlimited',
  'vn.limitOpt.ONCE': 'First visit only',
  'vn.limitOpt.WEEK': 'N times a week',
  'vn.limitOpt.MONTH': 'N times a month',
  'vn.offerOff': 'off',
  'vn.offerTitle': 'What the client gets',
  'vn.offerTitleHint': 'E.g. “Free cappuccino” or “−15% on the menu”',
  'vn.forWhom': 'For whom',
  'vn.howOften': 'How often',
  'vn.times': 'Times',
  'vn.offerActive': 'Offer active',
  'vn.activity': 'Activity',
  'vn.weeks8': 'last 8 weeks',
  'vn.checks': 'card checks',
  'vn.redeemed': 'offers redeemed',
  'vn.cardCheck': 'card check',
  'vn.staff': 'Staff',
  'vn.noStaff': 'Nobody yet.',
  'vn.role.OWNER': 'owner',
  'vn.role.STAFF': 'staff',
  'vn.removeStaff': 'Remove {name} from the venue?',
  'vn.staffHint': 'Staff are invited by the owner in the work bot (24-hour QR).',
  'vn.newOwnerLink': 'Owner link',
  'vn.newOwnerLinkHint': 'A new one-time link — e.g. if the owner changed',
  'vn.blurb': 'Short offer line (shown to clients)',
  'vn.blurbHint': 'E.g. “−10% with the club card”',
  'vn.coords': 'Map point',
  'vn.activeLabel': 'Visible to clients',
  'err.LEAD_NOT_CONVERTED': 'To move to “Connected”, use “Connect” — it creates the partner.',
  'err.ALREADY_CONVERTED': 'The lead is already connected.',
  'err.AGENCY_CODE_FORMAT': 'Code: Latin letters, digits and “_”, up to 32.',
  'err.AGENCY_CODE_TAKEN': 'This code is already taken.',
  'err.STAGES_INVALID': 'You need at least one open and one “Connected” stage.',
  'audit.bo.crm.create': 'added a lead',
  'audit.bo.crm.update': 'edited a lead',
  'audit.bo.crm.move': 'moved a lead',
  'audit.bo.crm.convert': 'connected a partner',
  'audit.bo.crm.addActivity': 'logged a contact',
  'audit.bo.crm.saveStages': 'edited the pipeline',
  'audit.bo.crm.setBonusRules': 'changed the bonus formula',
  'audit.bo.partners.saveAgency': 'edited an agency',
  'audit.bo.partners.agencyPayout': 'recorded an agency payout',
  'audit.bo.partners.saveVenue': 'edited a venue',
  'audit.bo.partners.saveOffer': 'edited a venue offer',
  'audit.bo.partners.ownerInvite': 'issued an owner link',
  'audit.bo.partners.removeMember': 'removed venue staff',

  // ───── заказы и исполнители (этап 3) ─────
  'ord.title': 'Orders',
  'ord.new': 'New order',
  'ord.search': 'No., client, phone, address or provider',
  'ord.period': 'Period',
  'ord.period.all': 'All dates',
  'ord.period.today': 'Today',
  'ord.period.tomorrow': 'Tomorrow',
  'ord.period.week': '7 days',
  'ord.period.past': 'Past',
  'ord.group.active': 'Active',
  'ord.group.new': 'Need a provider',
  'ord.group.pending': 'Offered',
  'ord.group.assigned': 'Assigned',
  'ord.group.in_progress': 'In progress',
  'ord.group.done': 'Done',
  'ord.group.cancelled': 'Cancelled',
  'ord.today': 'Today',
  'ord.tomorrow': 'Tomorrow',
  'ord.empty': 'No orders match these filters.',
  'ord.waitsAnswer': 'awaiting: {name}',
  'ord.needsHuman': 'needs a dispatcher',
  'ord.searching': 'auto-assigning…',
  'ord.overdue': 'time passed, no provider',
  'ord.needsDispatcher': 'Auto-assignment found no provider for {n} order(s). Assign manually.',
  'ord.autoOn': 'Auto-assign on',
  'ord.autoOff': 'Auto-assign off',
  'ord.autoHint': 'A new order is offered to the best free provider (client language, district, load, rating). No answer in {minutes} min or a decline — the next one; after {tries} tries — to a dispatcher.',
  'ord.back': 'All orders',
  'ord.progress': 'Progress',
  'ord.details': 'Details',
  'ord.when': 'When',
  'ord.where': 'Where',
  'ord.minutes': '{n} min',
  'ord.params': 'Details',
  'ord.extras': 'Extras',
  'ord.note': 'Client comment',
  'ord.lang': 'Client language',
  'ord.created': 'Created',
  'ord.checklist': 'Checklist',
  'ord.needsPhoto': 'photo needed',
  'ord.photo': 'Photo',
  'ord.history': 'History',
  'ord.client': 'Client',
  'ord.writeClient': 'Message the client',
  'ord.writeProvider': 'Message the provider',
  'ord.call': 'Call',
  'ord.noPhone': 'No phone number — add it to the profile',
  'ord.money': 'Money',
  'ord.base': 'Before discount',
  'ord.discount': 'Discount {pct}%',
  'ord.payout': 'To the provider',
  'ord.cancelFee': 'Cancellation fee',
  'ord.payment': 'Payment',
  'ord.markPaid': 'Mark as paid',
  'ord.confirmPaid': 'Mark the order as paid?',
  'ord.paidDone': 'Marked as paid',
  'ord.actions': 'Actions',
  'ord.setStatus': 'Status: {status}',
  'ord.confirmStatus': 'Move the order to “{status}”? The client will be notified.',
  'ord.statusChanged': 'Status changed',
  'ord.reschedule': 'Reschedule',
  'ord.rescheduleTitle': 'Reschedule order #{n}',
  'ord.rescheduleHint': 'If the provider is busy at the new time, the order goes back to “Need a provider” and to auto-assignment.',
  'ord.rescheduled': 'Rescheduled, same provider',
  'ord.rescheduledFree': 'Rescheduled; the provider is busy — looking for a new one',
  'ord.comment': 'Comment',
  'ord.commentPh': 'E.g. the client asked by phone',
  'ord.cancel': 'Cancel order',
  'ord.cancelTitle': 'Cancel the order',
  'ord.cancelReason': 'Reason',
  'ord.cancelReasonPh': 'E.g. the client changed their mind',
  'ord.cancelFeeHint': 'How much to charge the client, ֏. Usually 0.',
  'ord.cancelHint': 'The client and the provider will be notified.',
  'ord.cancelled': 'Order cancelled',
  'ord.rating': 'Client rating',
  'ord.noComment': 'No comment',
  'ord.offers': 'Offers',
  'ord.bySystem': 'system',
  'ord.provider': 'Provider',
  'ord.unassign': 'Remove provider',
  'ord.revoke': 'Withdraw offer',
  'ord.confirmUnassign': 'Remove the provider? The order goes back to “Need a provider”.',
  'ord.confirmRevoke': 'Withdraw the offer? The order will wait for a provider again.',
  'ord.unassigned': 'Done — the order needs a provider again',
  'ord.pendingProvider': 'Offered to {name}',
  'ord.pendingCompany': 'Passed to {name} — waiting for their dispatcher',
  'ord.byAuto': 'offered automatically',
  'ord.minLeft': '≈ {n} min to answer',
  'ord.pickHint': 'Free at this time, best match first. Or let the system pick.',
  'ord.autoPick': 'Pick automatically',
  'ord.autoOffered': 'Offered to {name}',
  'ord.autoNone': 'No free provider found',
  'ord.noFree': 'Everyone is busy at this time. Reschedule or arrange with a provider.',
  'ord.busy': 'Busy at this time: {n}',
  'ord.company': 'company',
  'ord.tried': 'already offered',
  'ord.load': '{n} orders that day',
  'ord.offer': 'Offer',
  'ord.offered': 'Offer sent',
  'ord.noProvider': 'No provider was assigned.',
  'ord.ev.created': 'Order created',
  'ord.ev.createdByStaff': 'Created by the team',
  'ord.ev.offered': 'Offered to a provider',
  'ord.ev.autoOffered': 'Auto-assign: offered',
  'ord.ev.autoNoCandidate': 'Auto-assign: nobody free',
  'ord.ev.autoGaveUp': 'Auto-assign stopped — needs a dispatcher',
  'no.title': 'New order',
  'no.client': 'Client',
  'no.clientPh': 'Name, @username or phone',
  'no.noClients': 'Nobody found',
  'no.changeClient': 'Another client',
  'no.service': 'Service',
  'no.extras': 'Extras',
  'no.address': 'Address',
  'no.addressPh': 'Street, building, apartment',
  'no.district': 'District',
  'no.when': 'Date and time',
  'no.day': 'Day',
  'no.noSlots': 'No free time that day — pick another day or “other time”.',
  'no.otherTime': 'Other time (arranged with a provider directly)',
  'no.customTime': 'Time',
  'no.customHint': 'Yerevan time. A free provider is not guaranteed.',
  'no.note': 'Comment for the provider',
  'no.notePh': 'Door code, pets…',
  'no.payment': 'Payment',
  'no.hint': 'The price includes the client’s subscription discount. The client is notified and the order goes to auto-assignment.',
  'no.create': 'Create order',
  'no.created': 'Order #{n} created',
  'prv.title': 'Providers',
  'prv.add': 'Add',
  'prv.search': 'Name or phone',
  'prv.status.active': 'Active',
  'prv.status.blocked': 'Blocked',
  'prv.status.all': 'All',
  'prv.empty': 'Nobody found.',
  'prv.col.name': 'Provider',
  'prv.col.rating': 'Rating',
  'prv.col.done': 'Orders',
  'prv.col.refused': 'Declines',
  'prv.col.next': 'Next order',
  'prv.period30': 'Orders, declines and low ratings — last 30 days.',
  'prv.blocked': 'blocked',
  'prv.onShift': 'on shift',
  'prv.offShift': 'off shift',
  'prv.notLinked': 'bot not opened',
  'prv.noDocs': 'no documents',
  'prv.ratings': '{n} ratings',
  'prv.low': '{n} low',
  'prv.today': 'today: {n}',
  'prv.back': 'All providers',
  'prv.write': 'Message',
  'prv.edit': 'Edit',
  'prv.blockedSince': 'Blocked since {date}.',
  'prv.unblock': 'Unblock',
  'prv.inviteHint': 'The provider hasn’t opened the work bot yet — send them the link.',
  'prv.copyInvite': 'Copy link',
  'prv.quality': 'Quality',
  'prv.days30': 'last 30 days',
  'prv.acceptRate': 'accepted {a} of {n} offers',
  'prv.refusals': 'declined: {d}, no answer: {e}',
  'prv.lowRatings': 'ratings of 3 or less',
  'prv.upcoming': 'Upcoming orders',
  'prv.noUpcoming': 'No assigned orders.',
  'prv.recent': 'Recently done',
  'prv.balance': 'To pay',
  'prv.thisMonth': 'This month',
  'prv.prevMonth': 'Last month',
  'prv.paidTotal': 'Paid',
  'prv.payout': 'Record a payout',
  'prv.payoutTitle': 'Payout to provider',
  'prv.amount': 'Amount, ֏',
  'prv.balanceHint': 'Owed to the provider: {sum}',
  'prv.payoutNote': 'How it was paid',
  'prv.payoutNotePh': 'Cash / card transfer…',
  'prv.payoutConfirm': 'Paid',
  'prv.payoutDone': 'Payout recorded',
  'prv.rateCompany': 'company rate {v}',
  'prv.rateOwn': 'rate {v}',
  'prv.ratePreset': 'preset “{name}”',
  'prv.work': 'Work',
  'prv.languages': 'Languages',
  'prv.districts': 'Districts',
  'prv.anyDistrict': 'any',
  'prv.districtsHint': 'Assignment favors providers working in the order’s district.',
  'prv.docs': 'Admission documents',
  'prv.noDocsHint': 'Passport, contract, certificate — kept in private storage.',
  'prv.docTitlePh': 'Title: passport, contract…',
  'prv.upload': 'Upload',
  'prv.docAdded': 'Document added',
  'prv.removeDocConfirm': 'Delete the document?',
  'prv.access': 'Access',
  'prv.block': 'Block',
  'prv.blockTitle': 'Block the provider',
  'prv.blockReason': 'Reason',
  'prv.blockReasonPh': 'E.g. client complaints, missing documents',
  'prv.blockHint': 'No new offers; pending offers are withdrawn. Already assigned orders stay — reassign them manually.',
  'prv.resetInvite': 'New bot link',
  'prv.resetConfirm': 'Create a new link? The current Telegram link is removed — the provider will need to log in again.',
  'prv.since': 'With us since {date}',
  'prv.saved': 'Saved',
  'prv.addTitle': 'New provider',
  'prv.editTitle': 'Provider profile',
  'prv.name': 'Name',
  'prv.role': 'Role',
  'prv.company': 'Company',
  'prv.freelance': 'Freelancer',
  'prv.schedule': 'Schedule',
  'prv.rate': 'Rate',
  'prv.note': 'Team note',
  'err.REASON_REQUIRED': 'Please give a reason.',
  'err.SLOT_UNAVAILABLE': 'This time is taken or has passed. Pick another one.',
  'err.ALREADY_ASSIGNED': 'The order is already assigned or offered — refresh the page.',
  'err.PROVIDER_BUSY': 'The provider is busy at this time.',
  'err.TRANSITION_NOT_ALLOWED': 'This step isn’t available for the order now.',
  'err.CHECKLIST_INCOMPLETE': 'The provider must finish the checklist and add photos first.',
  'err.CONSENT_REQUIRED': 'The client hasn’t given data-processing consent in the bot yet.',
  'err.ADDRESS_REQUIRED': 'Please enter the address and district.',
  'err.INVALID_PARAMS': 'Check the order details.',
  'err.OFFER_NOT_ACTIVE': 'The offer is no longer active.',
  'err.BAD_FILE_TYPE_DOC': 'Photo or PDF only.',
  'audit.bo.orders.assign': 'offered an order',
  'audit.bo.orders.auto': 'ran auto-assignment',
  'audit.bo.orders.unassign': 'removed a provider from an order',
  'audit.bo.orders.transition': 'changed an order status',
  'audit.bo.orders.cancel': 'cancelled an order',
  'audit.bo.orders.reschedule': 'rescheduled an order',
  'audit.bo.orders.markPaid': 'marked an order as paid',
  'audit.bo.orders.create': 'created an order for a client',
  'audit.bo.orders.setAutoSetting': 'changed auto-assignment',
  'audit.bo.providers.create': 'added a provider',
  'audit.bo.providers.update': 'edited a provider',
  'audit.bo.providers.setActive': 'changed provider access',
  'audit.bo.providers.resetInvite': 'reissued a provider link',
  'audit.bo.providers.removeDoc': 'deleted a provider document',
  'audit.bo.providers.addDoc': 'uploaded a provider document',
  'audit.bo.providers.payout': 'recorded a payout',
  'chat.order': 'Order #{n}',
  'chat.noOrder': 'No order',
  'chat.noOrderNow': 'No current order',
  'chat.orderNow': 'Now',
  'chat.orderNext': 'Next',
  'chat.orderNoNext': 'No upcoming orders',
  'chat.orderClient': 'client {name}',
  'chat.wroteNoOrder': 'wrote without an order',
  'err.CONCIERGE_SUBSCRIBERS_ONLY': 'The concierge is for members only. Give the client a subscription first.',
  'err.ASSIGNEE_NO_ACCESS': 'This team member has no access to this channel.',
  'err.PROVIDER_NOT_LINKED': 'The provider hasn’t opened the work bot yet.',
  'err.BAD_FILE_TYPE': 'This file type isn’t supported. Use a photo, PDF, Word, Excel, text or ZIP.',
  'audit.bo.chats.send': 'replied in a chat',
  'audit.bo.chats.sendFile': 'sent a file in a chat',
  'audit.bo.chats.assign': 'assigned a chat',
  'audit.bo.chats.close': 'closed a conversation',
  'audit.bo.chats.reopen': 'reopened a conversation',
  'audit.bo.chats.move': 'moved a conversation to another channel',
  'audit.bo.chats.openFor': 'opened a chat with a client',
  'audit.bo.chats.templates.save': 'edited a reply template',
  'audit.bo.chats.templates.remove': 'deleted a reply template',
  'nav.servicePartners': 'Service partners',
  'st.group.partners': 'Service partners',
  'ag.giftPlan': 'Gift for new clients',
  'ag.giftMonths': 'For how long',
  'ag.giftHint': 'Applies to new clients; those who already got a gift keep theirs.',
  'ag.noGift': 'no gift',
  'ag.giftValue': '{plan} for {n} mo',
  'ag.monthsN': '{n} mo',
  'ord.partner': 'partner',
  'ord.best': 'best for us',
  'ord.partnerFree': '{n} of {of} free',
  'ord.candMoney': 'We pay {cost} · we keep {margin}',
  'prv.ratePartnerPays': 'Paid by the partner',
  'rq.missing': 'missing details',
  'rq.noTime': 'not given',
  'rq.noDistrict': 'no district',
  'rq.noAddress': 'no address',
  'sp.title': 'Service partners',
  'sp.add': 'New partner',
  'sp.editTitle': 'Service partner',
  'sp.lead': 'Cleaning companies with their own staff. The partner’s manager gets a link, adds staff and assigns orders. An operator gives an order to a partner from the order card.',
  'sp.empty': 'No partners yet.',
  'sp.col.name': 'Partner',
  'sp.col.staff': 'Staff',
  'sp.col.active': 'Active orders',
  'sp.col.month': 'Partner income, month',
  'sp.col.balance': 'To pay',
  'sp.noManager': 'no manager',
  'sp.auto': 'auto-assign',
  'sp.back': 'All service partners',
  'sp.terms.COMMISSION': 'Uber-style: we take {pct}%',
  'sp.terms.PROFIT_SHARE': 'our costs: payroll {fot}%, tax {tax}%, partner gets {pct}% of profit',
  'sp.managerLink': 'Manager link',
  'sp.managerLinkHint': 'Give this link to the partner’s manager: they open the work bot and get the cabinet — orders, staff, auto-assignment, income. It also opens on a computer (button in the bot).',
  'sp.resetLink': 'New link',
  'sp.resetConfirm': 'Make a new link? The old one stops working; connected managers stay.',
  'sp.linkReset': 'Link updated',
  'sp.managers': 'Managers',
  'sp.noManagers': 'No manager has joined via the link yet.',
  'sp.managerSince': 'since {date}',
  'sp.remove': 'Remove',
  'sp.removeManagerConfirm': 'Remove {name} from the partner’s managers?',
  'sp.staff': 'Staff',
  'sp.staffHint': 'added by the partner’s manager',
  'sp.noStaff': 'No staff yet.',
  'sp.limitOrders': 'up to {n} orders a day',
  'sp.limitHours': 'up to {n} h a day',
  'sp.notLinked': 'not in the bot',
  'sp.off': 'off',
  'sp.orders': 'Active orders',
  'sp.noOrders': 'The partner has no orders right now.',
  'sp.waitingStaff': 'waiting for staff',
  'sp.toPay': 'Partner income to pay',
  'sp.byOrder': 'Income by order',
  'sp.noIncome': 'Income appears once a client pays for a completed order.',
  'sp.paid': 'paid {sum}',
  'sp.fotTax': 'payroll {fot}, tax {tax}',
  'sp.payoutTitle': 'Payout to partner',
  'sp.phone': 'Phone',
  'sp.service': 'Service',
  'sp.cleaningOnly': 'Cleaning (only this for now)',
  'sp.deal': 'Deal model',
  'sp.deal.COMMISSION': 'Uber-style: our % of order',
  'sp.deal.PROFIT_SHARE': 'Our costs: % to partner',
  'sp.dealHint.COMMISSION': 'We keep our % of each paid order, the rest goes to the partner. Wages and costs are theirs.',
  'sp.dealHint.PROFIT_SHARE': 'We pay the cleaner and the tax; the partner gets their % of what’s left: paid − payroll − tax.',
  'sp.commissionPct': 'Our %, of paid order',
  'sp.profitSharePct': 'Partner’s % of profit',
  'sp.fotPct': 'Cleaner payroll, % of order',
  'sp.taxPct': 'Tax, %',
  'sp.defaultHint': 'empty — from settings ({n}%)',
  'sp.exampleCOMMISSION': 'Example: order paid {sum} → we keep {ours}, partner gets {partner}.',
  'sp.examplePROFIT_SHARE': 'Example: paid {sum} − payroll {fot} − tax {tax} = {profit}; partner {partner}, us {ours}.',
  'sp.activeLabel': 'Partner active (orders can be assigned)',
  'nav.points': 'Bariki',
  'st.group.points': 'Bariki (cashback)',
  'ag.hwPoints': 'Housewarming bariki',
  'ag.hwDays': 'Valid, days',
  'ag.hwHint': '0 — none. Credited once, available right away.',
  'ag.hwValue': '{n} bariki for {days} days',
  'pt.title': 'Bariki',
  'pt.tab.accrue': 'Credit',
  'pt.tab.requests': 'Requests',
  'pt.tab.suggestions': 'Suggested',
  'pt.tab.ledger': 'Log',
  'pt.tab.report': 'Report',
  'pt.tab.import': 'CSV import',
  'pt.tab.setup': 'Rates & bonuses',
  'pt.search': 'Client',
  'pt.searchPh': 'Phone, @username, name or ID',
  'pt.notFound': 'No one found',
  'pt.pickClient': 'Find a client on the left — the credit form appears here.',
  'pt.flagged': 'check: many reversals',
  'pt.balance': 'Available',
  'pt.pending': 'Pending',
  'pt.availableFrom': 'from {date}',
  'pt.tier': 'Level',
  'pt.burnAt': 'Expire on',
  'pt.agency': 'Agency',
  'pt.streak': 'Cleaning streak',
  'pt.debt': 'Debt',
  'pt.redeem': 'Redeem',
  'pt.redeemBtn': 'Redeem',
  'pt.redeemed': 'Redeemed',
  'pt.allOps': 'All operations',
  'pt.noOps': 'No operations yet.',
  'pt.reverse': 'Reverse',
  'pt.accrueTitle': 'Credit',
  'pt.kind.ORDER': 'For an order',
  'pt.kind.BONUS': 'Bonus',
  'pt.f.category': 'Category',
  'pt.f.orderAmount': 'Order amount, ֏',
  'pt.f.excluded': 'Not eligible, ֏',
  'pt.f.excludedHint': 'fees, materials, tips',
  'pt.f.paidWithPoints': 'Paid with bariki',
  'pt.f.serviceDate': 'Service date',
  'pt.f.orderRef': 'Order number',
  'pt.f.orderRefHint': 'the same number can’t be credited twice',
  'pt.f.bonusType': 'Bonus type',
  'pt.f.ttl': 'Valid for, days',
  'pt.f.ttlDefault': 'default {n}',
  'pt.f.ttlNone': 'empty — general expiry rule',
  'pt.f.amount': 'Bariki amount',
  'pt.f.amountHint': 'suggested: {n}',
  'pt.f.reason': 'Reason',
  'pt.f.reasonRequired': 'Reason (required)',
  'pt.f.comment': 'Comment for the client',
  'pt.f.commentPh': 'Shown in their history',
  'pt.f.noWait': 'No waiting — available now',
  'pt.f.noWaitReason': 'No waiting (reason required)',
  'pt.f.redeemAmount': 'How much to redeem',
  'pt.f.defaultAmount': 'Default amount',
  'pt.formula': '{tier}: {rate}%{extra} → {n} bariki',
  'pt.accrueBtn': 'Credit',
  'pt.confirmTitle': 'Please check',
  'pt.edit': 'Edit',
  'pt.confirmOrder': 'Credit {n} bariki to {name} for {what} {sum} ֏ ({tier}, {rate}%). Available {date}.',
  'pt.confirmBonus': 'Credit {n} bariki to {name}: {what}, {until}.',
  'pt.now': 'right away',
  'pt.forDays': 'for {n} days',
  'pt.noTtl': 'no own expiry',
  'pt.changedNote': 'Amount differs from the suggestion ({n}) — logged with your reason.',
  'pt.accrued': 'Credited {n}',
  'pt.needsApproval': 'Above the limit — sent to a super admin',
  'pt.zero': 'Amount is 0 — nothing to credit',
  'pt.reverseAccrual': 'Reverse credit',
  'pt.reverseRedemption': 'Reverse redemption',
  'pt.reverseAccrualText': 'The credit of {n} bariki will be reversed. The original stays in the log.',
  'pt.reverseRedemptionText': '{n} bariki return to the original credits with their expiry.',
  'pt.debtWarn': '{spent} of this credit is already spent — balance goes negative: {after}. Future credits will cover it.',
  'pt.reasonPh': 'Refund, mistake…',
  'pt.reverseHint': 'Support can reverse only their own operations within 24 hours.',
  'pt.reversed': 'Reversed',
  'pt.maxHint': 'max {max} ({share}% of the order, within balance), min {min}',
  'pt.redeemReasonPh': 'By phone, order outside the app…',
  'pt.toPay': 'Left to pay: {sum} ֏',
  'pt.noRequests': 'No requests.',
  'pt.wants': 'wants to use {n}',
  'pt.forOrder': 'order #{n}',
  'pt.noOrder': 'order outside the app',
  'pt.balanceN': 'balance {n}',
  'pt.confirm': 'Confirm',
  'pt.confirmN': 'Redeem {n}',
  'pt.confirmed': 'Redeemed {n}',
  'pt.reject': 'Decline',
  'pt.rejectReason': 'Reason — the client will see it',
  'pt.noSuggestions': 'No suggestions.',
  'pt.suggestionsHint': 'The system found who qualifies for a bonus. Credit, change the amount or decline.',
  'pt.mech.first_month_route': 'First-month route',
  'pt.mech.photo_review': 'Photo review',
  'pt.mech.over_limit': 'Above support limit',
  'pt.why.first_month_route': '3 different categories in the first 30 days',
  'pt.why.photo_review': 'sent a photo after order #{n}',
  'pt.why.over_limit': '{by}: {op}',
  'pt.op.accrual': 'credit',
  'pt.op.redemption': 'redemption',
  'pt.approve': 'Approve',
  'pt.changeAmount': 'Change amount',
  'pt.allTypes': 'All types',
  'pt.allCategories': 'All categories',
  'pt.allChannels': 'All channels',
  'pt.allClients': 'All clients',
  'pt.type.accrual': 'Credit',
  'pt.type.bonus': 'Bonus',
  'pt.type.redemption': 'Redemption',
  'pt.type.request': 'Client request',
  'pt.type.reversal': 'Reversal',
  'pt.channel.WEB': 'admin',
  'pt.channel.BOT': 'bot',
  'pt.channel.CSV': 'CSV',
  'pt.channel.AUTO': 'auto',
  'pt.channel.CLIENT': 'client',
  'pt.status.PENDING': 'pending',
  'pt.status.ACTIVE': 'active',
  'pt.status.SPENT': 'spent',
  'pt.status.EXPIRED': 'expired',
  'pt.status.REVERSED': 'reversed',
  'pt.status.REQUESTED': 'waiting',
  'pt.status.CONFIRMED': 'redeemed',
  'pt.status.REJECTED': 'declined',
  'pt.status.ACCRUAL': 'of a credit',
  'pt.status.REDEMPTION': 'of a redemption',
  'pt.col.date': 'When',
  'pt.col.client': 'Client',
  'pt.col.what': 'What',
  'pt.col.amount': 'Bariki',
  'pt.col.status': 'Status',
  'pt.col.author': 'Who',
  'pt.system': 'system',
  'pt.suggestedN': 'suggested {n}',
  'pt.debtN': 'debt {n}',
  'pt.rep.accrued': 'Credited',
  'pt.rep.spent': 'Spent',
  'pt.rep.expired': 'Expired',
  'pt.rep.reversed': 'Reversed',
  'pt.rep.liabilities': 'Outstanding now',
  'pt.rep.rate': 'Redemption rate',
  'pt.rep.byCategory': 'Programme cost by category',
  'pt.rep.cost': '{pct}% of {revenue} ֏ revenue',
  'pt.rep.retention': 'Repeat order within 30 days',
  'pt.rep.with': 'With bariki',
  'pt.rep.without': 'Without bariki',
  'pt.rep.clientsN': '{n} clients, {multi} with 2+ categories',
  'pt.rep.topClients': 'Top by balance',
  'pt.rep.topAgencies': 'Agencies: housewarming activations',
  'pt.importHint': 'CSV: phone, category (cleaning, documents, learning, repair, transfer), order amount, date (YYYY-MM-DD), order number. Preview first; credited only after you confirm.',
  'pt.importRows': 'Rows',
  'pt.importTotal': 'Total bariki',
  'pt.importErr.client': 'client not found',
  'pt.importErr.category': 'unknown category',
  'pt.importErr.date': 'bad date',
  'pt.importErr.duplicate': 'duplicate order',
  'pt.importCommit': 'Credit {n} rows',
  'pt.importDone': 'Credited {n} rows',
  'pt.categories': 'Categories and rates',
  'pt.bonusTypes': 'Bonus types',
  'pt.code': 'Code',
  'pt.activeLabel': 'Active',
  'pt.rulesHint': 'Available {hours} h after the service · up to {share}% of an order, min {min} · expire after {burn} days of inactivity. Change in Settings.',
  'err.POINTS_DUPLICATE_ORDER': 'Bariki for this order were already credited.',
  'err.POINTS_REASON_REQUIRED': 'Please give a reason.',
  'err.POINTS_BELOW_MIN': 'Below the minimum redemption.',
  'err.POINTS_NOT_ENOUGH': 'Not enough bariki.',
  'err.POINTS_OVER_SHARE': 'More than the allowed share of the order.',
  'err.POINTS_OVER_LIMIT': 'Above your limit — needs a super admin.',
  'err.POINTS_FINAL': 'This can no longer be reversed.',
  'err.POINTS_REQUEST_CLOSED': 'Request already handled or expired.',
  'err.POINTS_WITH_PROMO': 'Bariki can’t be combined with a promo code.',
  'pt.formulaStreak': ' + cleaning streak {streak}: +{bonus}%',
  'ord.editComposition': 'Edit composition',
  'ord.editTitle': 'Order #{n} composition',
  'ord.editPrice': 'Price: was {was} → now {now} ({pct}% discount, as when booked)',
  'ord.editDuration': 'Duration: {was} → {now} min',
  'ord.editBusy': 'The order got longer and the provider has another job then — check the schedule.',
  'ord.editPaid': 'Already paid: agency reward, partner income and bariki will be recalculated. Settle the money difference with the client.',
  'ord.editOwnPrice': 'Custom total, ֏',
  'ord.editOwnPriceHint': 'empty — price list: {n}',
  'ord.editReason': 'Reason',
  'ord.editReasonRequired': 'Reason (required for a custom price)',
  'ord.editReasonPh': 'Added windows on site…',
  'ord.editNotify': 'Notify the client and the provider',
  'ord.edited': 'Order updated',
  'ord.editedBusy': 'Updated. The provider may not have time — check the schedule',
  'ord.uncheck': 'Uncheck',
  'ord.checkForProvider': 'Check for the provider',
  'ord.noPhotoReason': 'no photo: {reason}',
  'ord.byStaff': 'checked by the team',
  'ord.checkNoPhoto': 'Check without photo',
  'ord.photoHint': 'This item needs a photo. Upload one or check it without a photo and a reason.',
  'ord.uploadPhoto': 'Upload photo',
  'ord.noPhotoField': 'Or why there is no photo',
  'ord.noPhotoPh': 'The cleaner sent it in the chat…',
  'err.PHOTO_REQUIRED': 'A photo or a reason is required.',
};

const hy: Dict = {
  'ui.save': 'Պահպանել',
  'ui.close': 'Փակել',
  'ui.loading': 'Բեռնվում է…',
  'ui.error': 'Չստացվեց՝ {msg}',
  'ui.retry': 'Կրկնել',
  'ui.copy': 'Պատճենել',
  'ui.copied': 'Պատճենված է',
  'ui.days': '{n} օր',
  'ui.yes': 'Այո',
  'ui.no': 'Ոչ',
  'ui.none': '—',

  'err.PAYMENTS_NOT_CONFIGURED': 'Առցանց վճարումները դեռ միացված չեն․ նշեք վճարումը ձեռքով։',
  'err.REFUND_TOO_BIG': 'Վերադարձի գումարը մեծ է հաճախորդի վճարածից։',
  'err.REFUND_UNAVAILABLE': 'Tribute-ը թույլ չի տալիս վերադարձնել այս վճարումը (արդեն վերադարձված է կամ վիճարկվում է բանկում)։',
  'err.PAYMENT_PROVIDER_ERROR': 'Tribute-ը չպատասխանեց կամ մերժեց։ Փորձեք ավելի ուշ կամ արեք Tribute-ի կաբինետում։',
  'err.PAYMENT_UNAVAILABLE': 'Առցանց վճարումը կարգավորված չէ սերվերում։',
  'err.ENDS_BEFORE_START': 'Բաժանորդագրությունը չի կարող ավարտվել սկսվելուց առաջ։',
  'err.CANNOT_DEMOTE_SELF': 'Չեք կարող անջատել ինքներդ ձեզ կամ հանել ձեր սուպերադմինի դերը։',
  'err.ROLE_FORBIDDEN': 'Ձեր դերը մուտք չունի այս գործողությանը։',
  'err.NO_ROLE': 'Ձեր հաշիվը նոր ադմինում դերեր չունի։ Խնդրեք սուպերադմինին տրամադրել։',

  'login.coverWord': 'թիմ',
  'login.title': 'Մուտք թիմի համար',
  'login.lead': 'Հաստատեք մուտքը Barev Club-ի աշխատանքային բոտում։ Գաղտնաբառ պետք չէ։',
  'login.open': 'Հաստատել Telegram-ում',
  'login.qr': 'Համակարգչից հարմար է կոդը սկանավորել հեռախոսով։',
  'login.waiting': 'Սպասում ենք հաստատմանը Telegram-ում…',
  'login.expired': 'Հղումը հնացել է։',
  'login.retry': 'Ստանալ նոր հղում',
  'login.dev': 'Տեղային մուտք',
  'login.devHint': 'Միայն այս համակարգչում․ սերվերում այս բլոկը չկա։',
  'login.noTelegram': 'Մուտք չունե՞ք։ Խնդրեք սուպերադմինին ավելացնել ձեզ թիմում և ուղարկել Telegram-ի կապման հղում։',

  'nav.dashboard': 'Վահանակ',
  'nav.clients': 'Հաճախորդներ',
  'nav.chats': 'Չատեր',
  'nav.orders': 'Պատվերներ',
  'nav.providers': 'Կատարողներ',
  'nav.agencies': 'Անշարժ գույքի գործակալություններ',
  'nav.venues': 'Ակումբի գործընկերներ',
  'nav.crm': 'CRM',
  'nav.products': 'Ապրանքներ',
  'nav.journal': 'Մատյան',
  'nav.team': 'Թիմ',
  'nav.more': 'Ավելին',
  'nav.soon': 'փուլ {n}',
  'nav.groupWork': 'Աշխատանք',
  'nav.groupPartners': 'Գործընկերներ',
  'nav.groupSetup': 'Կարգավորում',

  'role.SUPERADMIN': 'Սուպերադմին',
  'role.SUPPORT': 'Աջակցություն',
  'role.CONCIERGE': 'Կոնսիերժ',
  'role.SALES': 'Վաճառք',
  'roleHint.SUPERADMIN': 'Ամեն ինչ, ներառյալ գումարներն ու թիմը',
  'roleHint.SUPPORT': 'Աջակցության չատ, պատվերներ, բաժանորդագրություններ',
  'roleHint.CONCIERGE': 'Կոնսիերժի չատ բաժանորդների համար',
  'roleHint.SALES': 'CRM՝ հաստատություններ և գործակալություններ',

  'shell.logout': 'Ելք',
  'shell.oldAdmin': 'Հին ադմին',
  'shell.linkTg': 'Կապեք Telegram-ը․ դրանով եք մուտք գործում և ստանում ծանուցումներ։',
  'shell.linkTgBtn': 'Կապել Telegram-ը',

  'soon.title': '{section} բաժինը տեղափոխվում է այստեղ',
  'soon.text': 'Բաժինը կհայտնվի {n}-րդ փուլում։ Մինչ այդ աշխատեք հին ադմինում․ տվյալները ընդհանուր են։',
  'soon.open': 'Բացել հին ադմինը',

  'dash.morning': 'Բարի լույս, {name}',
  'dash.day': 'Բարի օր, {name}',
  'dash.evening': 'Բարի երեկո, {name}',
  'dash.attention': 'Պահանջում է ուշադրություն',
  'dash.allClear': 'Շտապ ոչինչ չկա․ ամեն ինչ արված է։',
  'att.unassigned': 'Պատվերներ առանց կատարողի',
  'att.unreadChats': 'Պատասխանի սպասող զրույցներ',
  'att.webRequests': 'Հայտեր կայքից',
  'att.leads': 'Տեղափոխման հայտեր',
  'att.appRequests': 'Հայտեր հավելվածից',
  'att.lowRatings': '3 և ցածր գնահատականներ այս շաբաթ',
  'att.expiring': 'Ավարտվում են 7 օրում',
  'dash.money': 'Գումար',
  'dash.today': 'Այսօր',
  'dash.week': '7 օր',
  'dash.month': '30 օր',
  'dash.vsYesterday': 'երեկ՝ {sum}',
  'dash.vsPrev': 'նախորդ {period}-ի համեմատ',
  'dash.orders': 'պատվերներ',
  'dash.subs': 'բաժանորդագրություններ',
  'dash.chart': 'Եկամուտն ըստ օրերի, 30 օր',
  'dash.subsTitle': 'Բաժանորդագրություններ',
  'dash.gifts': 'Որից նվեր՝ {n}',
  'dash.expiring': 'Ավարտվում են առաջիկա 7 օրում',
  'dash.noExpiring': 'Առաջիկա շաբաթ ոչինչ չի ավարտվում։',
  'dash.conversion': 'Նվեր → գնում',
  'dash.conversionHint': 'Նվեր ստացած {total} հաճախորդից {paid}-ը հետո գնել են',
  'dash.growth': 'Աճ',
  'dash.newClients': 'Նոր հաճախորդներ',
  'dash.prevMonth': 'նախորդ 30 օրում՝ {n}',
  'dash.sources': 'Որտեղից են եկել, 30 օր',
  'dash.funnel': 'Ձագար, 30 օր',
  'funnel.leads': 'Հայտեր կայքից',
  'funnel.clients': 'Հաճախորդներ հավելվածում',
  'funnel.paying': 'Վճարել են',
  'dash.team': 'Թիմ և գործընկերներ',
  'dash.topProviders': 'Լավագույն կատարողներ',
  'dash.bottomProviders': 'Արժե խոսել',
  'dash.topPartners': 'Հաստատություններ՝ քարտի սկանավորումներ, 30 օր',
  'dash.salesSoon': 'Վաճառողների արդյունքները կհայտնվեն CRM-ի հետ։',
  'dash.empty': 'Դեռ դատարկ է',
  'dash.feed': 'Լրահոս',
  'feed.subscription': '{plan} · {source}',
  'feed.payment': '№{number} պատվերը վճարված է',
  'dash.ratings': '{n} գնահատական',
  'dash.scans': '{n} սկան',

  'source.organic': 'Ինքն են գտել',
  'source.deeplink': 'Գործակալության հղում',
  'source.manual_code': 'Գործակալության կոդ',
  'source.agency': '«{name}» գործակալություն',
  'source.utm': 'Գովազդ՝ {name}',

  'plan.BAREV_PLUS': 'Barev+',
  'plan.ELITE': 'Elite',
  'plan.none': 'Առանց բաժանորդագրության',
  'subSource.PAID': 'Գնում',
  'subSource.MANUAL': 'Թիմի նվեր',
  'subSource.AGENCY_GIFT': 'Գործակալության նվեր',
  'subStatus.ACTIVE': 'Ակտիվ',
  'subStatus.EXPIRED': 'Ավարտված',
  'subStatus.CANCELLED': 'Չեղարկված',
  'pay.cash': 'Կանխիկ',
  'pay.transfer': 'Փոխանցում',
  'pay.card': 'Քարտ տերմինալով',
  'pay.link': 'Վճարման հղում',
  'pay.tribute': 'Tribute (առցանց)',
  'segment.MOVING': 'Տեղափոխվում է',
  'segment.LIVING': 'Ապրում է Հայաստանում',

  'clients.title': 'Հաճախորդներ',
  'clients.search': 'Անուն, @username, հեռախոս կամ քարտի համար',
  'clients.found': 'Գտնվել է՝ {n}',
  'clients.revenueTotal': 'բերել են {sum}',
  'col.client': 'Հաճախորդ',
  'col.sub': 'Բաժանորդագրություն',
  'col.source': 'Աղբյուր',
  'col.revenue': 'Բերել է',
  'col.since': 'Մեզ հետ է',
  'filter.plan': 'Բաժանորդագրություն',
  'filter.all': 'Բոլորը',
  'filter.any': 'Բաժանորդագրությամբ',
  'filter.none': 'Առանց բաժանորդագրության',
  'filter.subSource': 'Ինչպես է ստացել',
  'filter.source': 'Աղբյուր',
  'filter.agency': 'Գործակալություն',
  'filter.tag': 'Պիտակ',
  'filter.segment': 'Սցենար',
  'filter.paying': 'Վճարել են',
  'filter.payingYes': 'Վճարել են',
  'filter.payingNo': 'Երբեք չեն վճարել',
  'filter.expiring': 'Ավարտվում է 7 օրում',
  'filter.reset': 'Զրոյացնել ֆիլտրերը',
  'filter.more': 'Ֆիլտրեր',
  'filter.anyValue': 'Ցանկացած',
  'sort.label': 'Հերթականություն',
  'sort.new': 'Նախ նորերը',
  'sort.revenue': 'Ամենաշատը բերածները',
  'sort.ends': 'Շուտով ավարտվող',
  'segments.title': 'Իմ ընտրանիները',
  'segments.save': 'Պահպանել ընտրանին',
  'segments.name': 'Ընտրանու անունը',
  'segments.saved': 'Ընտրանին պահպանված է',
  'segments.remove': 'Ջնջել «{name}» ընտրանին',
  'clients.export': 'Ներբեռնել Excel-ի համար',
  'clients.exported': 'Ֆայլը ներբեռնված է',
  'clients.broadcast': 'Առաքում',
  'clients.broadcastSoon': 'Ըստ ընտրանու առաքումները կհայտնվեն 5-րդ փուլում։',
  'clients.empty': 'Այս ֆիլտրերին ոչ ոք չի համապատասխանում։',
  'clients.emptyAll': 'Հաճախորդներ դեռ չկան․ կհայտնվեն, երբ գրեն բոտին։',
  'clients.prev': 'Հետ',
  'clients.next': 'Առաջ',
  'clients.page': '{from}–{to}՝ {total}-ից',

  'card.back': 'Բոլոր հաճախորդները',
  'card.since': 'Մեզ հետ է {date}-ից',
  'card.telegram': 'Բացել Telegram-ում',
  'card.cardNo': 'Քարտ {code}',
  'card.noSub': 'Ակտիվ բաժանորդագրություն չկա',
  'card.noSubHint': 'Նվիրեք կամ վաճառեք բաժանորդագրություն․ հաճախորդը միանգամից կստանա զեղչեր։',
  'card.until': 'մինչև {date}',
  'card.daysLeft': 'մնաց {n} օր',
  'card.history': 'Բոլոր բաժանորդագրությունները',
  'card.grantedBy': 'տրամադրել է {name}',
  'card.giftFrom': 'նվեր «{name}»-ից',
  'card.paidVia': '{method}',
  'card.refunded': 'վերադարձ {sum}',
  'card.cancelledAt': 'չեղարկված {date}',
  'card.upcoming': 'կսկսվի {date}',
  'card.money': 'Բերել է',
  'card.fromOrders': 'պատվերներ',
  'card.fromSubs': 'բաժանորդագրություններ',
  'card.orders': 'Պատվերներ',
  'card.noOrders': 'Պատվերներ դեռ չկան։',
  'card.newOrder': 'Ստեղծել պատվեր',
  'card.newOrderSoon': 'Պատվեր հաճախորդի անունից՝ 3-րդ փուլում, պատվերների բաժնի հետ։',
  'card.profile': 'Պրոֆիլ',
  'card.source': 'Որտեղից է եկել',
  'card.agency': 'Գործակալություն',
  'card.language': 'Լեզու',
  'card.phone': 'Հեռախոս',
  'card.segment': 'Սցենար',
  'card.consent': 'Տվյալների մշակման համաձայնություն',
  'card.tags': 'Պիտակներ',
  'card.addTag': 'Նոր պիտակ',
  'card.note': 'Թիմի նշում',
  'card.notePh': 'Հաճախորդը չի տեսնում այս նշումը',
  'card.saved': 'Պահպանված է',
  'card.activity': 'Հաճախորդի մատյան',
  'card.noActivity': 'Թիմը դեռ ոչինչ չի փոխել։',
  'card.stats': 'Ակտիվություն',
  'stat.cardUsages': 'քարտի սկան',
  'stat.leads': 'տեղափոխման հայտ',
  'stat.requests': 'հայտ հավելվածից',
  'stat.concierge': 'հաղորդագրություն չատում',

  'act.gift': 'Նվիրել',
  'act.sell': 'Վաճառել',
  'act.extend': 'Փոխել ժամկետը',
  'act.cancel': 'Չեղարկել',
  'act.changePlan': 'Փոխել սակագինը',
  'act.refund': 'Վերադարձնել գումարը',
  'op.title': 'Առցանց վճարումներ (Tribute)',
  'op.empty': 'Առցանց վճարումներ դեռ չկան։ Այստեղ կհայտնվեն Tribute-ով քարտով վճարված բաժանորդագրություններն ու պատվերները՝ «Դադարեցնել ավտոերկարաձգումը» և «Վերադարձնել գումարը» կոճակներով։',
  'op.disabled': 'Առցանց վճարումը կարգավորված չէ սերվերում։',
  'op.paidAt': 'վճարված է {{date}}',
  'op.refunded': 'վերադարձ',
  'op.nextCharge': 'ավտոերկարաձգում, հաջորդ գանձումը՝ {{date}}',
  'op.renewOff': 'ավտոերկարաձգումն անջատված է',
  'op.attention': '⚠ պատվերն արդեն վճարված կամ չեղարկված էր — անհրաժեշտ է վերադարձ',
  'op.stop': 'Դադարեցնել ավտոերկարաձգումը',
  'op.stopConfirm': 'Դադարեցնե՞լ ավտոերկարաձգումը։ Վճարված ամիսը կմնա, հետագա գանձումներ չեն լինի։',
  'op.stopped': 'Ավտոերկարաձգումը դադարեցված է',
  'op.refundConfirmSub': 'Վերադարձնե՞լ վերջին վճարումը։ Ավտոերկարաձգումը կդադարի, ընթացիկ ժամկետը կչեղարկվի։',
  'op.refundConfirmOrder': 'Վերադարձնե՞լ այս պատվերի գումարը հաճախորդի քարտին։',
  'op.refundStarted': 'Վերադարձն ուղարկված է Tribute — կարգավիճակը կթարմացվի հաստատումից հետո',
  'op.hint': 'Գումարը վերադարձվում է հաճախորդի քարտին Tribute-ի միջոցով՝ վերջին վճարման համար։',

  'audit.bo.clients.gift': 'նվիրել է բաժանորդագրություն',
  'audit.bo.clients.sell': 'վաճառել է բաժանորդագրություն',
  'audit.bo.clients.extend': 'փոխել է ժամկետը',
  'audit.bo.clients.cancel': 'չեղարկել է բաժանորդագրությունը',
  'audit.bo.clients.changePlan': 'փոխել է սակագինը',
  'audit.bo.clients.refund': 'ձևակերպել է վերադարձ',
  'audit.bo.clients.setTags': 'փոխել է պիտակները',
  'audit.bo.clients.setNote': 'փոխել է նշումը',

  'dlg.giftTitle': 'Նվիրել բաժանորդագրություն',
  'dlg.giftHint': 'Այն կնշվի որպես ձեր նվերը։',
  'dlg.sellTitle': 'Վաճառել բաժանորդագրություն',
  'dlg.plan': 'Սակագին',
  'dlg.period': 'Ժամկետ',
  'dlg.period1': '1 ամիս',
  'dlg.period3': '3 ամիս',
  'dlg.period6': '6 ամիս',
  'dlg.period12': '1 տարի',
  'dlg.periodCustom': 'Այլ',
  'dlg.days': 'Օր',
  'dlg.amount': 'Գումար, ֏',
  'dlg.method': 'Ինչպես է վճարել',
  'dlg.note': 'Մեկնաբանություն թիմի համար',
  'dlg.linkSoon': 'Վճարման հղումը կմիացնենք վճարային համակարգը միացնելուց հետո։',
  'dlg.giftConfirm': 'Նվիրել',
  'dlg.sellConfirm': 'Նշել վճարումը',
  'dlg.extendTitle': 'Փոխել ժամկետը',
  'dlg.extendHint': 'Բացասական թիվը կրճատում է ժամկետը։',
  'dlg.newEnd': 'Նոր ավարտի ամսաթիվ՝ {date}',
  'dlg.extendConfirm': 'Պահպանել ժամկետը',
  'dlg.cancelTitle': 'Չեղարկել բաժանորդագրությունը',
  'dlg.cancelHint': 'Հաճախորդը միանգամից կկորցնի զեղչերն ու արտոնությունները։',
  'dlg.reason': 'Պատճառ',
  'dlg.cancelConfirm': 'Չեղարկել բաժանորդագրությունը',
  'dlg.planTitle': 'Փոխել սակագինը',
  'dlg.planConfirm': 'Փոխել սակագինը',
  'dlg.refundTitle': 'Վերադարձնել գումարը',
  'dlg.refundPaid': 'Վճարված է {sum}, արդեն վերադարձված՝ {back}',
  'dlg.refundCancel': 'Միանգամից չեղարկել բաժանորդագրությունը',
  'dlg.refundConfirm': 'Ձևակերպել վերադարձ',
  'done.gift': 'Բաժանորդագրությունը նվիրված է',
  'done.sell': 'Վճարումը նշված է',
  'done.extend': 'Ժամկետը փոխված է',
  'done.cancel': 'Բաժանորդագրությունը չեղարկված է',
  'done.changePlan': 'Սակագինը փոխված է',
  'done.refund': 'Վերադարձը ձևակերպված է',

  'team.title': 'Թիմ',
  'team.lead': 'Ով է աշխատում ադմինում և ինչ հասանելիություն ունի։ Մեկ մարդը կարող է ունենալ մի քանի դեր։',
  'team.add': 'Ավելացնել մարդու',
  'team.name': 'Անուն',
  'team.roles': 'Դերեր',
  'team.create': 'Ավելացնել',
  'team.linkTitle': 'Հղում {name}-ի համար',
  'team.linkHint': 'Ուղարկեք նրան։ Կբացի աշխատանքային բոտը, Telegram-ը կկապվի, և կարող է մուտք գործել։',
  'team.tgLinked': 'Telegram-ը կապված է',
  'team.tgNotLinked': 'Telegram-ը կապված չէ',
  'team.newLink': 'Կապման հղում',
  'team.disable': 'Անջատել մուտքը',
  'team.enable': 'Վերադարձնել մուտքը',
  'team.disabled': 'Մուտքն անջատված է',
  'team.you': 'դուք',
  'team.saved': 'Դերերը պահպանված են',

  // ───── чаты (этап 2) ─────
  'chat.title': 'Զրույցներ',
  'chat.all': 'Բոլորը',
  'channel.SUPPORT': 'Աջակցություն',
  'channel.CONCIERGE': 'Կոնսիերժ',
  'chat.f.waiting': 'Սպասում են պատասխանի',
  'chat.f.mine': 'Իմը',
  'chat.f.unassigned': 'Առանց պատասխանատուի',
  'chat.f.open': 'Բաց',
  'chat.f.closed': 'Փակ',
  'chat.f.all': 'Բոլորը',
  'chat.search': 'Անուն, @username, հեռախոս կամ տեքստ',
  'chat.emptyList': 'Այստեղ դատարկ է։ Նոր հաղորդագրությունները կհայտնվեն ինքնուրույն։',
  'chat.emptyFilter': 'Ոչինչ չգտանք․ փորձեք այլ ֆիլտր։',
  'chat.pick': 'Ընտրեք զրույցը',
  'chat.pickHint': 'Վերևում նրանք են, ովքեր ամենաերկար են սպասում։',
  'chat.waiting': 'սպասում է {t}',
  'chat.you': 'Դուք',
  'chat.provider': 'Կատարող',
  'chat.unassigned': 'Պատասխանատու չկա',
  'chat.take': 'Վերցնել ինձ',
  'chat.transfer': 'Փոխանցել',
  'chat.transferTo': 'Փոխանցել գործընկերոջը',
  'chat.unassign': 'Հանել պատասխանատուին',
  'chat.close': 'Փակել',
  'chat.reopen': 'Կրկին բացել',
  'chat.closedBanner': 'Զրույցը փակ է։ Կբացվի, երբ հաճախորդը գրի․ կարող եք պատասխանել նաև հիմա։',
  'chat.moveTo': 'Տեղափոխել «{channel}»',
  'chat.moved': 'Տեղափոխվեց «{channel}»',
  'chat.movedAway': 'Տեղափոխվեց «{channel}»։ Դուք մուտք չունեք այդ ալիքին։',
  'chat.other': 'Կա նաև զրույց «{channel}» ալիքում',
  'chat.otherOpen': 'Բացել',
  'chat.bot': 'Զրույցը վարում է ԱԲ կոնսիերժը։',
  'chat.botTake': 'Վերցնել',
  'chat.reply': 'Պատասխան',
  'chat.note': 'Նշում',
  'chat.replyPh': 'Պատասխան հաճախորդին՝ կգա Telegram-ում',
  'chat.notePh': 'Նշում թիմի համար՝ հաճախորդը չի տեսնի',
  'chat.send': 'Ուղարկել',
  'chat.saveNote': 'Պահել նշումը',
  'chat.attach': 'Կցել ֆայլ',
  'chat.templates': 'Ձևանմուշներ',
  'chat.tplManage': 'Կարգավորել ձևանմուշները',
  'chat.tplEmpty': 'Ձևանմուշներ դեռ չկան։',
  'chat.tplNew': 'Նոր ձևանմուշ',
  'chat.tplTitle': 'Անվանում',
  'chat.tplText': 'Տեքստ',
  'chat.tplTextHint': '{name}-ը կփոխարինվի հաճախորդի անունով',
  'chat.tplChannel': 'Ալիք',
  'chat.tplBoth': 'Երկու ալիքները',
  'chat.tplDelete': 'Ջնջել',
  'chat.tplSaved': 'Ձևանմուշը պահպանված է',
  'chat.tplSearch': 'Գտնել ձևանմուշ',
  'chat.noteLabel': 'Նշում · տեսանելի է միայն թիմին',
  'chat.fromApp': 'հավելվածից',
  'chat.undelivered': 'Չի առաքվել․ հաճախորդը արգելափակել է բոտը կամ երբեք չի բացել',
  'chat.fileTooBig': 'Ֆայլը 10 ՄԲ-ից մեծ է',
  'chat.enterHint': 'Enter՝ ուղարկել · Shift+Enter՝ նոր տող',
  'chat.providerNotLinked': 'Կատարողը դեռ չի բացել աշխատանքային բոտը․ առայժմ կարող եք թողնել միայն նշում։',
  'chat.client': 'Հաճախորդի մասին',
  'chat.aboutProvider': 'Կատարողի մասին',
  'chat.openCard': 'Հաճախորդի քարտ',
  'chat.write': 'Գրել',
  'chat.back': 'Բոլոր զրույցները',
  'chat.sys.took': '{name}-ը վերցրեց զրույցը',
  'chat.sys.transferred': '{name}-ը զրույցը փոխանցեց {param}-ին',
  'chat.sys.unassigned': '{name}-ը հանեց պատասխանատուին',
  'chat.sys.closed': '{name}-ը փակեց զրույցը',
  'chat.sys.reopened': '{name}-ը կրկին բացեց զրույցը',
  'chat.sys.moved': '{name}-ը զրույցը տեղափոխեց «{param}»',
  'chat.ago.now': 'հենց հիմա',
  'chat.ago.min': '{n} ր',
  'chat.ago.h': '{n} ժ',
  'chat.ago.d': '{n} օր',
  'chat.today': 'Այսօր',
  'chat.yesterday': 'Երեկ',
  'chat.image': 'Լուսանկար',
  'chat.file': 'Ֆայլ',
  'chat.subUntil': 'մինչև {date}',
  'chat.orders': 'Պատվերներ',
  'chat.phone': 'Հեռախոս',
  'chat.lang': 'Լեզու',
  'chat.source': 'Աղբյուր',
  'chat.linked': 'Աշխատանքային բոտը բացված է',
  'chat.notLinked': 'Աշխատանքային բոտը բացված չէ',
  'chat.details': 'Մանրամասներ',
  'chat.more': 'Այլ գործողություններ',
  'ord.tab.orders': 'Պատվերներ',
  'ord.tab.moves': 'Տեղափոխություններ',
  'ord.tab.requests': 'Հայտեր',
  'mv.search': 'Անուն, կոնտակտ, գործակալություն կամ №',
  'mv.empty': 'Այս կարգավիճակով տեղափոխություններ չկան։',
  'mv.all': 'Բոլորը',
  'mv.steps': 'Քայլեր՝ {done}/{total}',
  'mv.docs': 'Փաստաթղթեր {done}/{total}',
  'mv.now': 'Հիմա՝ {step}',
  'mv.noPlan': 'Պլանը դեռ չկա',
  'mv.back': 'Բոլոր տեղափոխությունները',
  'mv.plan': 'Տեղափոխության պլան',
  'mv.planHintApp': 'Հաճախորդը տեսնում է քայլերը հավելվածում և ստանում է հաղորդագրություն։',
  'mv.planHintNoApp': 'Հաճախորդը դեռ չի բացել հավելվածը։',
  'mv.docsTitle': 'Հաճախորդի փաստաթղթեր',
  'mv.docsHint': 'Հաճախորդը նշում է հավելվածում, դուք՝ այստեղ։',
  'mv.answers': 'Պատասխաններ',
  'mv.when': 'Երբ',
  'mv.package': 'Փաթեթ',
  'mv.wants': 'Ինչ է պետք',
  'mv.agency': 'Գործակալություն',
  'mv.lang': 'Լեզու',
  'mv.created': 'Հայտ',
  'mv.unfinished': 'չի ավարտել',
  'mv.note': 'Թիմի նշում',
  'mv.notePh': 'Թիմի համար…',
  'mv.save': 'Պահպանել',
  'mv.status': 'Կարգավիճակ',
  'mv.contact': 'Կոնտակտ',
  'mv.write': 'Գրել',
  'mv.call': 'Զանգել',
  'mv.openClient': 'Հաճախորդի քարտ',
  'mv.inApp': 'հավելվածում',
  'mv.add': 'Ավելացնել',
  'mv.addStep': 'Քայլ ցուցակից…',
  'mv.addDoc': 'Փաստաթուղթ ցուցակից…',
  'mv.custom': 'Սեփական…',
  'mv.customPh': 'Անուն այս հաճախորդի համար',
  'mv.stepNote': 'Մեկնաբանություն հաճախորդի համար',
  'mv.remove': 'Հեռացնել',
  'mv.removeConfirm': 'Հեռացնե՞լ «{name}»։',
  'mv.rename': 'Վերանվանել',
  'mv.listHint': 'Ընդհանուր ցուցակը՝ «Ապրանքներ»-ում։',
  'rq.site': 'Կայքից',
  'rq.app': 'Հավելվածից',
  'rq.open': 'Բաց',
  'rq.closed': 'Փակ',
  'rq.siteHint': 'Մաքրում՝ պատվիրված կայքում։ Զանգեք և ստեղծեք պատվեր։',
  'rq.appHint': 'Հաճախորդները խնդրել են բաժանորդագրություն կամ զանգ։',
  'rq.empty': 'Բաց հայտեր չկան։',
  'rq.emptyClosed': 'Փակ հայտեր չկան։',
  'rq.callback': 'Հետ զանգել՝ {service}',
  'rq.newOrder': 'Ստեղծել պատվեր',
  'rq.wish': 'Ցանկալի ժամ',
  'rq.price': 'Գինը կայքում',
  'rq.noApp': 'բոտը չի բացել',
  'rq.done': 'Կապ հաստատվեց',
  'rq.cancel': 'Չեղարկել',
  'rq.reopen': 'Վերաբացել',
  'rq.activate': 'Միացնել 30 օրով',
  'rq.activated': 'Բաժանորդագրությունը միացված է',
  'rq.order': 'Պատվեր №{n}',
  'audit.bo.moves.update': 'փոխեց տեղափոխությունը',
  'audit.bo.moves.updateStep': 'փոխեց քայլը',
  'audit.bo.moves.addStep': 'ավելացրեց քայլ',
  'audit.bo.moves.removeStep': 'հեռացրեց քայլը',
  'audit.bo.moves.addDoc': 'ավելացրեց փաստաթուղթ',
  'audit.bo.moves.updateDoc': 'փոխեց փաստաթուղթը',
  'audit.bo.moves.removeDoc': 'հեռացրեց փաստաթուղթը',
  'audit.bo.requests.updateSite': 'փոխեց կայքի հայտը',
  'audit.bo.requests.updateApp': 'փոխեց հավելվածի հայտը',
  'audit.bo.requests.activate': 'միացրեց բաժանորդագրություն',
  'pr.newService': 'Նոր ծառայություն',
  'pr.newServiceHint': 'Զրոյից՝ ֆիքսված գնով, կամ պատրաստի ծառայության պատճենով։',
  'pr.fromScratch': 'Զրոյից',
  'pr.copyOf': 'Պատրաստիի պատճեն',
  'pr.category': 'Ուղղություն',
  'pr.copyFrom': 'Պատճենել ծառայությունը',
  'pr.copyHint': 'Կպատճենվեն գները, հարցերը, ստուգացանկը։',
  'pr.who': 'Ով է կատարում',
  'pr.whoHint': 'Որոշում է, ում է առաջարկվում պատվերը։',
  'pr.flowHint.CLEANER': 'ճանապարհին → աշխատանք → պատրաստ',
  'pr.flowHint.DRIVER': 'վարորդը տեղում → ճանապարհին → ավարտ',
  'pr.flowHint.CAREER_CONSULTANT': 'խորհրդատու → հանդիպում → արդյունք',
  'pr.flowHint.DOCS_AGENT': 'ստուգում → ներկայացում → պատրաստ',
  'pr.online': 'Առցանց՝ առանց հասցեի',
  'pr.namePh': 'Օրինակ՝ Պատուհանների լվացում',
  'pr.create': 'Ստեղծել ծառայությունը',
  'pr.createHint': 'Ծառայությունը կստեղծվի անջատված՝ կարգավորեք և միացրեք։',
  'pr.created': 'Ծառայությունը ստեղծված է',
  'pr.questions': 'Հարցեր պատվերի ժամանակ',
  'pr.questionsHint': 'Ինչ է ընտրում հաճախորդը պատվերի ձևում։',
  'pr.qLabel': 'Հարց',
  'pr.qType.select': 'ընտրություն',
  'pr.qType.int': 'թիվ',
  'pr.qType.text': 'տեքստ',
  'pr.qType.bool': 'այո / ոչ',
  'pr.qOption': 'Տարբերակ {n}',
  'pr.qAddOption': 'տարբերակ',
  'pr.qFrom': 'սկսած',
  'pr.qTo': 'մինչև',
  'pr.qRequired': 'պարտադիր',
  'pr.qAdd': 'Հարց',
  'pr.qNone': 'Հարցեր չկան։',
  'pr.delete': 'Ջնջել ծառայությունը',
  'pr.deleteConfirm': 'Ջնջե՞լ ծառայությունը։',
  'err.SERVICE_HAS_ORDERS': 'Ծառայությունն ունի պատվերներ՝ անջատեք այն։',
  'err.OPTIONS_REQUIRED': 'Անհրաժեշտ է նվազագույնը 2 տարբերակ։',
  'err.PARAMS_LOCKED': 'Այս ծառայության հարցերը փոխում է ծրագրավորողը։',
  'audit.bo.products.createService': 'ստեղծեց ծառայություն',
  'audit.bo.products.saveParams': 'փոխեց ծառայության հարցերը',
  'audit.bo.products.deleteService': 'ջնջեց ծառայությունը',
  'audit.bo.products.moveService': 'փոխեց հերթականությունը',
  'audit.bo.crm.addTask': 'ավելացրեց առաջադրանք',
  'audit.bo.crm.setTaskDone': 'նշեց առաջադրանքը',
  'audit.bo.crm.removeTask': 'ջնջեց առաջադրանքը',
  'audit.bo.crm.remove': 'ջնջեց լիդը',
  'audit.bo.segments.save': 'պահպանեց սեգմենտը',
  'audit.bo.segments.remove': 'ջնջեց սեգմենտը',
  'audit.bo.team.linkUrl': 'տվեց Telegram հղում',
  'audit.bo.auth.telegramLink': 'կապում է Telegram-ը',
  'audit.bo.auth.logout': 'դուրս եկավ',
  'audit.bo.clients.exportCsv': 'արտահանեց հաճախորդներին',

  // ───── продукты, рассылки, журнал, настройки (этап 5) ─────
  'nav.settings': 'Կարգավորումներ',
  'pr.title': 'Ապրանքներ',
  'pr.tab.services': 'Ծառայություններ և գներ',
  'pr.tab.plans': 'Բաժանորդագրություն և զեղչեր',
  'pr.tab.promos': 'Պրոմոկոդեր',
  'pr.tab.relocation': 'Տեղափոխություն',
  'pr.readOnly': 'Գները փոխում է միայն սուպերադմինը։',
  'pr.catDiscounts': 'զեղչեր՝ Barev+ −{plus}%, Elite −{elite}%',
  'pr.on': 'միացված',
  'pr.off': 'անջատված',
  'pr.from': '{sum}-ից',
  'pr.orders30': '30 օրում պատվերներ՝ {n}',
  'pr.setup': 'Կարգավորել',
  'pr.back': 'Բոլոր ծառայությունները',
  'pr.content': 'Բովանդակություն',
  'pr.contentHint': 'Այսպես է կոչվում ծառայությունը հավելվածում և բոտում։ Դատարկ՝ ստանդարտ տեքստ։',
  'pr.name': 'Անվանում',
  'pr.description': 'Նկարագրություն',
  'pr.extrasNames': 'Լրացուցիչ ծառայությունների անվանումներ ({n})',
  'pr.price': 'Գին և տևողություն',
  'pr.fixedPrice': 'Գին, ֏',
  'pr.minutes': 'Տևողություն, ր',
  'pr.base': 'Բազա, ֏',
  'pr.perRoom': '+ սենյակ, ֏',
  'pr.perBath': '+ սանհանգույց, ֏',
  'pr.baseMin': 'Բազա, ր',
  'pr.perRoomMin': '+ սենյակ, ր',
  'pr.perBathMin': '+ սանհանգույց, ր',
  'pr.roomsHint': 'Բազան՝ 1 սենյակ և 1 սանհանգույց։',
  'pr.extras': 'Լրացուցիչ',
  'pr.unitCol': 'Միավոր',
  'pr.extraPrice': 'Գին, ֏',
  'pr.extraMin': '+ ր',
  'pr.unit.item': 'հատը',
  'pr.unit.service': 'ծառայություն',
  'pr.unit.hour': 'ժամը',
  'pr.unit.halfHour': '30 րոպեն',
  'pr.priceHint': 'Նոր գները գործում են նոր պատվերների համար։',
  'pr.settings': 'Գրանցում և չեղարկում',
  'pr.activeLabel': 'Ծառայությունը հասանելի է',
  'pr.windowFrom': 'Գրանցում՝ սկսած',
  'pr.windowTo': 'մինչև',
  'pr.step': 'Քայլ, ր',
  'pr.lead': 'Ոչ շուտ քան, ժ',
  'pr.horizon': 'Օրեր առաջ',
  'pr.lateFee': 'Ուշ չեղարկման պահում, %',
  'pr.discounts': 'Բաժանորդային զեղչեր',
  'pr.discountsHint': 'Գործում են ամբողջ «{cat}» ուղղության համար։',
  'pr.checklist': 'Կատարողի ստուգացանկ',
  'pr.photo': 'լուսանկար',
  'pr.newItem': 'Նոր կետ',
  'pr.checklistHint': 'Կատարողը նշում է կետերը բոտում։',
  'pr.plans': 'Բաժանորդագրության սակագներ',
  'pr.plansSuper': 'Գները տեսնում է սուպերադմինը։',
  'pr.plansHint': '«Մինչև» զեղչը ցուցադրվում է կայքում։',
  'pr.discountTable': 'Զեղչեր ըստ ուղղության',
  'pr.discountTableHint': 'Փոխվում են ծառայության քարտում։',
  'pr.promoHint': 'Պրոմոկոդը զեղչ է տալիս։ Չի գումարվում բաժանորդային զեղչին։',
  'pr.newPromo': 'Նոր պրոմոկոդ',
  'pr.promo': 'Պրոմոկոդ',
  'pr.noPromos': 'Պրոմոկոդեր դեռ չկան։',
  'pr.used': 'օգտագործված {n}/{max}',
  'pr.until': 'մինչև {date}',
  'pr.state.live': 'գործում է',
  'pr.state.off': 'անջատված',
  'pr.state.expired': 'ժամկետանց',
  'pr.state.exhausted': 'սպառված',
  'pr.state.scheduled': 'դեռ չի սկսվել',
  'pr.code': 'Կոդ',
  'pr.pct': 'Զեղչ, %',
  'pr.validFrom': 'Գործում է՝ սկսած',
  'pr.validUntil': 'մինչև',
  'pr.maxUses': 'Ընդհանուր քանակ',
  'pr.maxUsesHint': 'Դատարկ՝ անսահմանափակ',
  'pr.forCats': 'Ուղղություններ',
  'pr.forCatsHint': 'Ոչինչ՝ բոլորը',
  'pr.promoNotePh': 'Որտեղից է կոդը',
  'pr.promoActive': 'Պրոմոկոդն ակտիվ է',
  'pr.rel.STEP': 'Տեղափոխության քայլեր',
  'pr.rel.DOC': 'Փաստաթղթեր',
  'pr.relHint.STEP': 'Նոր հաճախորդները ստանում են այս ցանկը։',
  'pr.relHint.DOC': 'Փաստաթղթերի ցանկ հավելվածում։',
  'pr.turnkey': 'բանալի հանձնած',
  'pr.turnkeyLabel': 'Մտնում է «բանալի հանձնած» փաթեթում',
  'pr.docHint': 'Հուշում',
  'pr.relActive': 'Ցույց տալ հաճախորդներին',
  'no.promo': 'Պրոմոկոդ',
  'no.promoPh': 'Եթե հաճախորդը կոդ ունի',
  'no.promoOk': '{code} կոդը կիրառված է',
  'no.promoWeaker': 'Բաժանորդային զեղչն ավելի մեծ է',
  'bc.title': 'Ուղարկում բոտով',
  'bc.recipients': 'Կստանան՝ {n}',
  'bc.filtersHint': 'Ստացողներ՝ ընթացիկ ֆիլտրերով հաճախորդներ։',
  'bc.textRu': 'Տեքստ (ռուսերեն)',
  'bc.textPh': 'Օրինակ՝ «Նոյեմբերի 1-ից…»',
  'bc.otherLangs': 'Թարգմանություններ',
  'bc.clientsN': 'հաճախորդներ՝ {n}',
  'bc.fallback': '{langs}-ի համար թարգմանություն չկա՝ կստանան ռուսերեն։',
  'bc.appButton': '«Բացել հավելվածը» կոճակ',
  'bc.test': 'Ուղարկել ինձ',
  'bc.testSent': 'Ուղարկվեց ձեզ',
  'bc.send': 'Ուղարկել {n}',
  'bc.confirm': 'Իսկապե՞ս ուղարկել {n}',
  'bc.sureHint': 'Ուղարկվածը հետ կանչել հնարավոր չէ։',
  'bc.started': 'Ուղարկումը սկսվեց՝ {n}',
  'bc.history': 'Ուղարկումներ',
  'bc.historyBtn': 'Ուղարկումների պատմություն',
  'bc.historyHint': 'Ուղարկում ենք վայրկյանում ~25 հաղորդագրություն։',
  'bc.none': 'Ուղարկումներ դեռ չեն եղել։',
  'bc.status.SENDING': 'ընթացքում',
  'bc.status.DONE': 'ուղարկված',
  'bc.status.CANCELLED': 'կանգնեցված',
  'bc.sent': 'առաքված {n}/{total}',
  'bc.blocked': 'արգելափակել են բոտը՝ {n}',
  'bc.failed': 'սխալներ՝ {n}',
  'bc.cancel': 'Կանգնեցնել',
  'bc.cancelConfirm': 'Կանգնեցնե՞լ ուղարկումը։',
  'jr.title': 'Գործողությունների մատյան',
  'jr.who': 'Ով',
  'jr.what': 'Բաժին',
  'jr.everyone': 'Բոլոր աշխատակիցները',
  'jr.allSections': 'Բոլոր բաժինները',
  'jr.days': '{n} օր',
  'jr.empty': 'Այս ժամանակահատվածում գործողություններ չկան։',
  'jr.system': 'Համակարգ',
  'jr.open': 'բացել',
  'jr.details': 'մանրամասներ',
  'jr.hide': 'թաքցնել',
  'jr.more': 'Ցույց տալ ավելին',
  'jr.entity.clients': 'Հաճախորդներ',
  'jr.entity.orders': 'Պատվերներ',
  'jr.entity.providers': 'Կատարողներ',
  'jr.entity.chats': 'Զրույցներ',
  'jr.entity.crm': 'CRM',
  'jr.entity.partners': 'Գործընկերներ',
  'jr.entity.products': 'Ապրանքներ',
  'jr.entity.broadcasts': 'Ուղարկումներ',
  'jr.entity.settings': 'Կարգավորումներ',
  'jr.entity.team': 'Թիմ',
  'jr.entity.segments': 'Սեգմենտներ',
  'jr.entity.auth': 'Մուտք',
  'st.title': 'Կարգավորումներ',
  'st.saveN': 'Պահպանել ({n})',
  'st.group.orders': 'Պատվերներ',
  'st.group.subs': 'Բաժանորդագրություն',
  'st.group.online': 'Առցանց վճարում (Tribute)',
  'st.group.agencies': 'Անշարժ գույքի գործակալություններ',
  'st.group.sales': 'Վաճառողների բոնուս',
  'st.group.site': 'Կայքի գներ',
  'st.group.other': 'Այլ',
  'st.payments': 'Վճարման եղանակներ',
  'st.payment': 'Վճարման եղանակ',
  'st.code': 'Կոդ',
  'st.paymentActive': 'Ցույց տալ',
  'err.PROMO_INVALID': 'Այդպիսի պրոմոկոդ չկա։',
  'err.PROMO_EXPIRED': 'Պրոմոկոդի ժամկետն անցել է։',
  'err.PROMO_USED': 'Հաճախորդն արդեն օգտագործել է այս կոդը։',
  'err.PROMO_NOT_FOR_SERVICE': 'Կոդը չի գործում այս ծառայության համար։',
  'err.PROMO_CODE_FORMAT': 'Կոդ՝ լատինատառ և թվեր։',
  'err.PROMO_CODE_TAKEN': 'Այս կոդն արդեն կա։',
  'err.SLOT_WINDOW_INVALID': 'Պատուհանի ավարտը պետք է լինի սկզբից հետո։',
  'err.TELEGRAM_NOT_LINKED': 'Կապեք ձեր Telegram-ը։',
  'err.TEST_UNREACHABLE': 'Չհասավ․ բացեք հաճախորդի բոտը։',
  'err.NO_RECIPIENTS': 'Այս ֆիլտրերով հաճախորդներ չկան։',
  'audit.bo.products.saveTexts': 'փոխեց ծառայության տեքստերը',
  'audit.bo.products.saveService': 'փոխեց ծառայության գինը',
  'audit.bo.products.saveCategory': 'փոխեց զեղչերը',
  'audit.bo.products.saveChecklist': 'փոխեց ստուգացանկը',
  'audit.bo.products.savePromo': 'փոխեց պրոմոկոդը',
  'audit.bo.products.saveRelocation': 'փոխեց տեղափոխության ձևանմուշը',
  'audit.bo.products.moveRelocation': 'փոխեց քայլերի հերթականությունը',
  'audit.bo.broadcasts.send': 'սկսեց ուղարկումը',
  'audit.bo.broadcasts.test': 'ուղարկեց փորձնական',
  'audit.bo.broadcasts.cancel': 'կանգնեցրեց ուղարկումը',
  'audit.bo.settings.save': 'փոխեց կարգավորումները',
  'audit.bo.settings.savePaymentMethod': 'փոխեց վճարման եղանակը',
  'audit.bo.team.create': 'ավելացրեց աշխատակից',
  'audit.bo.team.update': 'փոխեց դերերը',

  // ───── партнёры и CRM (этап 4) ─────
  'crm.title': 'CRM',
  'crm.newLead': 'Նոր լիդ',
  'crm.editLead': 'Լիդ',
  'crm.stagesBtn': 'Ձագար',
  'crm.tab.board': 'Ձագար',
  'crm.tab.tasks': 'Առաջադրանքներ',
  'crm.tab.map': 'Քարտեզ',
  'crm.search': 'Անուն, կոնտակտ, հեռախոս կամ հասցե',
  'crm.kind.all': 'Բոլորը',
  'crm.kind.VENUE': 'Հաստատություններ',
  'crm.kind.AGENCY': 'Գործակալություններ',
  'crm.kind1.VENUE': 'Հաստատություն',
  'crm.kind1.AGENCY': 'Գործակալություն',
  'crm.owner': 'Վաճառող',
  'crm.allSales': 'Բոլոր վաճառողները',
  'crm.noOwner': 'Առանց վաճառողի',
  'crm.ownerIs': 'վարում է {name}',
  'crm.connected': 'Միացված',
  'crm.connect': 'Միացում',
  'crm.days': '{n} օր',
  'crm.inStage': 'Օրեր այս փուլում',
  'crm.dropHere': 'Քաշեք այստեղ',
  'crm.dragHint': 'Քաշեք քարտերը փուլերի միջև։ «Միացված»-ը ստեղծում է գործընկեր կամ գործակալություն։',
  'crm.overdue': 'ժամկետանց',
  'crm.tasksOpen': 'Բաց',
  'crm.tasksDone': 'Կատարված',
  'crm.addTask': 'Առաջադրանք',
  'crm.noTasks': 'Բաց առաջադրանքներ չկան։',
  'crm.noDoneTasks': 'Կատարված առաջադրանքներ դեռ չկան։',
  'crm.noLead': 'առանց լիդի',
  'crm.tasksHint': 'Ժամկետին հիշեցում կգա աշխատանքային բոտում։',
  'crm.taskTitle': 'Ինչ անել',
  'crm.taskDay': 'Օր',
  'crm.taskTime': 'Ժամ',
  'crm.taskHint': 'Հիշեցումը կգա այդ ժամին (Երևան)։',
  'crm.tp.call': 'Զանգահարել',
  'crm.tp.meet': 'Հանդիպում',
  'crm.tp.send': 'Ուղարկել պայմանները',
  'crm.tp.check': 'Իմանալ որոշումը',
  'crm.partnerOnMap': 'ակումբի գործընկեր',
  'crm.legendPartner': 'միացված գործընկերներ',
  'crm.legendLead': 'լիդեր (փուլի գույն)',
  'crm.mapHint': 'Կետը հայտնվում է կոորդինատներ ունենալիս։',
  'crm.name': 'Անվանում',
  'crm.namePhVenue': 'Օրինակ՝ Lumen Coffee',
  'crm.namePhAgency': 'Օրինակ՝ Domik Realty',
  'crm.category': 'Տեսակ',
  'crm.contactName': 'Կոնտակտային անձ',
  'crm.contactPh': 'Անուն և պաշտոն',
  'crm.address': 'Հասցե',
  'crm.note': 'Նշում',
  'crm.convert': 'Միացնել',
  'crm.convertTitle': 'Միացնել «{name}»',
  'crm.convertVenueHint': 'Կստեղծենք գործընկեր և հղում սեփականատիրոջ համար։',
  'crm.convertAgencyHint': 'Կստեղծենք գործակալություն կոդով և հղում հաճախորդների համար։',
  'crm.agencyCode': 'Գործակալության կոդ',
  'crm.agencyCodeHint': 'Լատինատառ և թվեր',
  'crm.convertedTitle': 'Միացված է 🎉',
  'crm.convertedVenue': 'Ուղարկեք այս հղումը սեփականատիրոջը։ Միանգամյա է։',
  'crm.convertedAgency': 'Այս հղումը գործակալությունը տալիս է հաճախորդներին։',
  'crm.openVenue': 'Հաստատության քարտ',
  'crm.openAgency': 'Գործակալության քարտ',
  'crm.ownerLinked': 'Սեփականատերն արդեն բոտում է։',
  'crm.ownerNotYet': 'Սեփականատերը դեռ չի բացել հղումը։ Ուղարկեք կրկին՝',
  'crm.agencyInfo': 'Կոդ {code}, հաճախորդներ՝ {n}',
  'crm.lostTitle': 'Ինչու՞ հրաժարում',
  'crm.lostReason': 'Պատճառ',
  'crm.lr.price': 'Պայմանները չհամապատասխանեցին',
  'crm.lr.notNow': 'Ոչ հիմա',
  'crm.lr.noAnswer': 'Չեն պատասխանում',
  'crm.lr.competitor': 'Աշխատում են մրցակցի հետ',
  'crm.lostBanner': 'Հրաժարում՝ {reason}',
  'crm.stagesTitle': 'Ձագարի փուլեր',
  'crm.stagesHint': 'Հերթականությունը՝ սլաքներով։',
  'crm.addStage': 'Փուլ',
  'crm.stageKind.OPEN': 'ընթացքում',
  'crm.stageKind.WON': 'միացված',
  'crm.stageKind.LOST': 'հրաժարում',
  'crm.bonusMonth': 'Բոնուս այս ամիս',
  'crm.bonusMineSuper': 'Բոնուս (ձեր), ամիս',
  'crm.bonusNotSet': 'կարգավորված չէ',
  'crm.bonusVenues': 'Հաստատություններ՝ {n} → {sum}',
  'crm.bonusAgencies': 'Գործակալություններ՝ {n} → {sum}',
  'crm.bonusRevenue': '{pct}% եկամուտից → {sum}',
  'crm.bonusTitle': 'Վաճառողի բոնուսի բանաձև',
  'crm.bonusHint': 'Բոնուս = հաստատություններ × դրույք + գործակալություններ × դրույք + % եկամուտից։',
  'crm.bonusPerVenue': 'Հաստատության համար, ֏',
  'crm.bonusPerAgency': 'Գործակալության համար, ֏',
  'crm.bonusPct': '% եկամուտից',
  'crm.bonusMonths': 'Քանի ամիս',
  'crm.back': 'CRM',
  'crm.created': '{date}-ից',
  'crm.stage': 'Փուլ',
  'crm.history': 'Կոնտակտների պատմություն',
  'crm.noHistory': 'Դեռ դատարկ է։',
  'crm.act.CALL': 'Զանգ',
  'crm.act.MEETING': 'Հանդիպում',
  'crm.act.NOTE': 'Նշում',
  'crm.act.FILE': 'Ֆայլ',
  'crm.act.STAGE': 'Փուլ',
  'crm.act.WON': 'Միացված',
  'crm.actPh.CALL': 'Ինչի՞ մասին պայմանավորվեցիք',
  'crm.actPh.MEETING': 'Ինչպե՞ս անցավ հանդիպումը',
  'crm.actPh.NOTE': 'Նշում ձեզ և թիմի համար',
  'crm.addAct': 'Գրանցել',
  'crm.attach': 'Լուսանկար կամ ֆայլ',
  'crm.tasks': 'Առաջադրանքներ',
  'crm.noLeadTasks': 'Առաջադրանքներ չկան։',
  'crm.contacts': 'Կոնտակտներ',
  'crm.noCoords': 'Քարտեզում չկա։',
  'crm.coordsPh': 'Google Maps հղում կամ «40.18, 44.51»',
  'crm.coordsBad': 'Կոորդինատներ չգտնվեցին։',
  'crm.remove': 'Ջնջել լիդը',
  'crm.removeConfirm': 'Ջնջե՞լ լիդը։',
  'venue.cat.cafe': 'Սրճարան',
  'venue.cat.restaurant': 'Ռեստորան',
  'venue.cat.shop': 'Խանութ',
  'venue.cat.beauty': 'Գեղեցկություն',
  'venue.cat.other': 'Այլ',
  'ag.title': 'Անշարժ գույքի գործակալություններ',
  'ag.add': 'Նոր անշարժ գույքի գործակալություն',
  'ag.editTitle': 'Անշարժ գույքի գործակալություն',
  'ag.empty': 'Անշարժ գույքի գործակալություններ դեռ չկան։',
  'ag.col.name': 'Գործակալություն',
  'ag.col.clients': 'Հաճախորդներ',
  'ag.col.gifts': 'Նվերներ',
  'ag.col.month': 'Պարգև, ամիս',
  'ag.col.balance': 'Վճարման',
  'ag.hint': 'Պարգև՝ % վճարված պատվերներից։',
  'ag.inactive': 'անջատված',
  'ag.back': 'Բոլոր գործակալությունները',
  'ag.pct': '{n}% հաճախորդների պատվերներից',
  'ag.byS': 'միացրել է {name}',
  'ag.link': 'Հղում հաճախորդների համար',
  'ag.linkHint': 'Հաճախորդը բացում է բոտը, ստանում գործակալության նվերը և կցվում նրան։ Կամ մուտքագրում է {code} կոդը /code հրամանով։',
  'ag.clients': 'Հաճախորդներ',
  'ag.giftsN': 'նվերներ՝ {n}',
  'ag.noClients': 'Հաճախորդներ դեռ չկան։',
  'ag.orders': 'պատվերներ՝ {n}',
  'ag.toPay': 'Վճարման ենթակա պարգև',
  'ag.accrued': 'Հաշվեգրված',
  'ag.payouts': 'Վճարումներ',
  'ag.payoutTitle': 'Վճարում գործակալությանը',
  'ag.balanceHint': 'Չվճարված՝ {sum}',
  'ag.pctLabel': 'Պարգև, %',
  'ag.sales': 'Միացրել է',
  'ag.contactPh': 'Անուն, հեռախոս, Telegram',
  'ag.active': 'Գործակալությունն աշխատում է (կոդն ընդունվում է)',
  'vn.title': 'Ակումբի գործընկերներ',
  'vn.add': 'Նոր հաստատություն',
  'vn.editTitle': 'Հաստատություն',
  'vn.empty': 'Հաստատություններ դեռ չկան։',
  'vn.col.name': 'Հաստատություն',
  'vn.col.offers': 'Առաջարկներ',
  'vn.col.staff': 'Աշխատակիցներ',
  'vn.col.visits': 'Ստուգումներ, 30 օր',
  'vn.col.last': 'Վերջին ստուգումը',
  'vn.hint': 'Ստուգում՝ աշխատակիցը սկանավորել է ակումբային քարտը։',
  'vn.noOwner': 'սեփականատեր չկա',
  'vn.noOffers': 'առաջարկներ չկան',
  'vn.back': 'Բոլոր հաստատությունները',
  'vn.ownerHint': 'Սեփականատերը դեռ միացված չէ՝ ուղարկեք միանգամյա հղում։',
  'vn.makeLink': 'Ստեղծել հղում',
  'vn.offers': 'Առաջարկներ ըստ մակարդակի',
  'vn.addOffer': 'Առաջարկ',
  'vn.editOffer': 'Առաջարկ',
  'vn.noOffersHint': 'Ավելացրեք, թե ինչ են ստանում անդամները։',
  'vn.level.any': 'Ցանկացած անդամ',
  'vn.level.BAREV_PLUS': 'Barev+ և Elite',
  'vn.level.ELITE': 'Միայն Elite',
  'vn.limit.NONE': 'առանց սահմանափակման',
  'vn.limit.ONCE': 'մեկ անգամ',
  'vn.limit.WEEK': 'շաբաթական {n} անգամ',
  'vn.limit.MONTH': 'ամսական {n} անգամ',
  'vn.limitOpt.NONE': 'Առանց սահմանափակման',
  'vn.limitOpt.ONCE': 'Միայն առաջին այցը',
  'vn.limitOpt.WEEK': 'N անգամ շաբաթական',
  'vn.limitOpt.MONTH': 'N անգամ ամսական',
  'vn.offerOff': 'անջատված',
  'vn.offerTitle': 'Ինչ է ստանում հաճախորդը',
  'vn.offerTitleHint': 'Օրինակ՝ «Կապուչինո նվեր»',
  'vn.forWhom': 'Ում համար',
  'vn.howOften': 'Որքան հաճախ',
  'vn.times': 'Քանի անգամ',
  'vn.offerActive': 'Առաջարկն ակտիվ է',
  'vn.activity': 'Ակտիվություն',
  'vn.weeks8': '8 շաբաթում',
  'vn.checks': 'քարտի ստուգումներ',
  'vn.redeemed': 'օգտագործված առաջարկներ',
  'vn.cardCheck': 'քարտի ստուգում',
  'vn.staff': 'Աշխատակիցներ',
  'vn.noStaff': 'Դեռ ոչ ոք։',
  'vn.role.OWNER': 'սեփականատեր',
  'vn.role.STAFF': 'աշխատակից',
  'vn.removeStaff': 'Հեռացնե՞լ {name}-ին։',
  'vn.staffHint': 'Աշխատակիցներին հրավիրում է սեփականատերը։',
  'vn.newOwnerLink': 'Հղում սեփականատիրոջը',
  'vn.newOwnerLinkHint': 'Նոր միանգամյա հղում',
  'vn.blurb': 'Կարճ նկարագրություն',
  'vn.blurbHint': 'Օրինակ՝ «−10% ակումբային քարտով»',
  'vn.coords': 'Քարտեզի կետ',
  'vn.activeLabel': 'Տեսանելի է հաճախորդներին',
  'err.LEAD_NOT_CONVERTED': 'Օգտագործեք «Միացնել»։',
  'err.ALREADY_CONVERTED': 'Լիդն արդեն միացված է։',
  'err.AGENCY_CODE_FORMAT': 'Կոդ՝ լատինատառ և թվեր։',
  'err.AGENCY_CODE_TAKEN': 'Այս կոդն արդեն զբաղված է։',
  'err.STAGES_INVALID': 'Անհրաժեշտ է գոնե մեկ բաց և մեկ «Միացված» փուլ։',
  'audit.bo.crm.create': 'ավելացրեց լիդ',
  'audit.bo.crm.update': 'փոխեց լիդը',
  'audit.bo.crm.move': 'տեղափոխեց լիդը',
  'audit.bo.crm.convert': 'միացրեց գործընկեր',
  'audit.bo.crm.addActivity': 'գրանցեց կոնտակտ',
  'audit.bo.crm.saveStages': 'փոխեց ձագարը',
  'audit.bo.crm.setBonusRules': 'փոխեց բոնուսի բանաձևը',
  'audit.bo.partners.saveAgency': 'փոխեց գործակալությունը',
  'audit.bo.partners.agencyPayout': 'նշեց վճարում գործակալությանը',
  'audit.bo.partners.saveVenue': 'փոխեց հաստատությունը',
  'audit.bo.partners.saveOffer': 'փոխեց առաջարկը',
  'audit.bo.partners.ownerInvite': 'հղում տվեց սեփականատիրոջը',
  'audit.bo.partners.removeMember': 'հեռացրեց աշխատակցին',

  // ───── заказы и исполнители (этап 3) ─────
  'ord.title': 'Պատվերներ',
  'ord.new': 'Նոր պատվեր',
  'ord.search': '№, հաճախորդ, հեռախոս, հասցե կամ կատարող',
  'ord.period': 'Ժամանակահատված',
  'ord.period.all': 'Բոլոր օրերը',
  'ord.period.today': 'Այսօր',
  'ord.period.tomorrow': 'Վաղը',
  'ord.period.week': '7 օր',
  'ord.period.past': 'Անցածները',
  'ord.group.active': 'Ակտիվ',
  'ord.group.new': 'Սպասում են կատարողի',
  'ord.group.pending': 'Առաջարկված',
  'ord.group.assigned': 'Նշանակված',
  'ord.group.in_progress': 'Կատարվում են',
  'ord.group.done': 'Պատրաստ',
  'ord.group.cancelled': 'Չեղարկված',
  'ord.today': 'Այսօր',
  'ord.tomorrow': 'Վաղը',
  'ord.empty': 'Այս պայմաններով պատվերներ չկան։',
  'ord.waitsAnswer': 'սպասում ենք՝ {name}',
  'ord.needsHuman': 'պետք է դիսպետչեր',
  'ord.searching': 'համակարգը ընտրում է…',
  'ord.overdue': 'ժամն անցել է, կատարող չկա',
  'ord.needsDispatcher': 'Ավտոմատ բաշխումը կատարող չգտավ {n} պատվերի համար։ Նշանակեք ձեռքով։',
  'ord.autoOn': 'Ավտոմատ բաշխումը միացված է',
  'ord.autoOff': 'Ավտոմատ բաշխումն անջատված է',
  'ord.autoHint': 'Նոր պատվերն ինքնաբերաբար առաջարկվում է լավագույն ազատ կատարողին։ {minutes} րոպեում պատասխան չկա կամ հրաժարվեց՝ հաջորդին, {tries} փորձից հետո՝ դիսպետչերին։',
  'ord.back': 'Բոլոր պատվերները',
  'ord.progress': 'Ընթացք',
  'ord.details': 'Մանրամասներ',
  'ord.when': 'Երբ',
  'ord.where': 'Որտեղ',
  'ord.minutes': '{n} ր',
  'ord.params': 'Պարամետրեր',
  'ord.extras': 'Լրացուցիչ',
  'ord.note': 'Հաճախորդի մեկնաբանություն',
  'ord.lang': 'Հաճախորդի լեզուն',
  'ord.created': 'Ստեղծված',
  'ord.checklist': 'Ստուգացանկ',
  'ord.needsPhoto': 'լուսանկար է պետք',
  'ord.photo': 'Լուսանկար',
  'ord.history': 'Պատմություն',
  'ord.client': 'Հաճախորդ',
  'ord.writeClient': 'Գրել հաճախորդին',
  'ord.writeProvider': 'Գրել կատարողին',
  'ord.call': 'Զանգահարել',
  'ord.noPhone': 'Հեռախոսահամարը նշված չէ',
  'ord.money': 'Գումար',
  'ord.base': 'Առանց զեղչի',
  'ord.discount': 'Զեղչ {pct}%',
  'ord.payout': 'Կատարողին',
  'ord.cancelFee': 'Չեղարկման պահում',
  'ord.payment': 'Վճարում',
  'ord.markPaid': 'Նշել վճարումը',
  'ord.confirmPaid': 'Նշե՞լ, որ հաճախորդը վճարել է։',
  'ord.paidDone': 'Վճարումը նշված է',
  'ord.actions': 'Գործողություններ',
  'ord.setStatus': 'Կարգավիճակ՝ {status}',
  'ord.confirmStatus': 'Տեղափոխե՞լ «{status}»։ Հաճախորդը ծանուցում կստանա։',
  'ord.statusChanged': 'Կարգավիճակը փոխված է',
  'ord.reschedule': 'Տեղափոխել',
  'ord.rescheduleTitle': 'Պատվեր №{n}-ի տեղափոխում',
  'ord.rescheduleHint': 'Եթե կատարողը զբաղված է նոր ժամին, պատվերը կվերադառնա «Սպասում են կատարողի»։',
  'ord.rescheduled': 'Տեղափոխված է, կատարողը նույնն է',
  'ord.rescheduledFree': 'Տեղափոխված է, կատարողը զբաղված է՝ փնտրում ենք նորը',
  'ord.comment': 'Մեկնաբանություն',
  'ord.commentPh': 'Օրինակ՝ հաճախորդը խնդրեց հեռախոսով',
  'ord.cancel': 'Չեղարկել պատվերը',
  'ord.cancelTitle': 'Պատվերի չեղարկում',
  'ord.cancelReason': 'Չեղարկման պատճառը',
  'ord.cancelReasonPh': 'Օրինակ՝ հաճախորդը փոխեց միտքը',
  'ord.cancelFeeHint': 'Որքան պահել հաճախորդից, ֏։ Սովորաբար 0։',
  'ord.cancelHint': 'Հաճախորդը և կատարողը ծանուցում կստանան։',
  'ord.cancelled': 'Պատվերը չեղարկված է',
  'ord.rating': 'Հաճախորդի գնահատականը',
  'ord.noComment': 'Առանց մեկնաբանության',
  'ord.offers': 'Ում է առաջարկվել',
  'ord.bySystem': 'համակարգ',
  'ord.provider': 'Կատարող',
  'ord.unassign': 'Հանել կատարողին',
  'ord.revoke': 'Հետ կանչել առաջարկը',
  'ord.confirmUnassign': 'Հանե՞լ կատարողին։ Պատվերը կվերադառնա։',
  'ord.confirmRevoke': 'Հետ կանչե՞լ առաջարկը։',
  'ord.unassigned': 'Պատվերը կրկին սպասում է կատարողի',
  'ord.pendingProvider': 'Առաջարկված է՝ {name}',
  'ord.pendingCompany': 'Հանձնված է «{name}» ընկերությանը',
  'ord.byAuto': 'առաջարկել է համակարգը',
  'ord.minLeft': 'պատասխանի համար ≈ {n} ր',
  'ord.pickHint': 'Այս ժամին ազատներ՝ ըստ համապատասխանության։',
  'ord.autoPick': 'Ընտրել ավտոմատ',
  'ord.autoOffered': 'Առաջարկվեց՝ {name}',
  'ord.autoNone': 'Ազատ կատարող չգտնվեց',
  'ord.noFree': 'Այս ժամին բոլորը զբաղված են։',
  'ord.busy': 'Զբաղված են՝ {n}',
  'ord.company': 'ընկերություն',
  'ord.tried': 'արդեն առաջարկվել է',
  'ord.load': 'այդ օրը պատվերներ՝ {n}',
  'ord.offer': 'Առաջարկել',
  'ord.offered': 'Առաջարկն ուղարկված է',
  'ord.noProvider': 'Կատարող չի նշանակվել։',
  'ord.ev.created': 'Պատվերը ստեղծված է',
  'ord.ev.createdByStaff': 'Ստեղծել է թիմը',
  'ord.ev.offered': 'Առաջարկվել է կատարողին',
  'ord.ev.autoOffered': 'Ավտոմատ բաշխում՝ առաջարկված',
  'ord.ev.autoNoCandidate': 'Ավտոմատ բաշխում՝ ազատ չկա',
  'ord.ev.autoGaveUp': 'Ավտոմատ բաշխումը կանգնեց',
  'no.title': 'Նոր պատվեր',
  'no.client': 'Հաճախորդ',
  'no.clientPh': 'Անուն, @username կամ հեռախոս',
  'no.noClients': 'Ոչ ոք չգտնվեց',
  'no.changeClient': 'Այլ հաճախորդ',
  'no.service': 'Ծառայություն',
  'no.extras': 'Լրացուցիչ',
  'no.address': 'Հասցե',
  'no.addressPh': 'Փողոց, շենք, բնակարան',
  'no.district': 'Վարչական շրջան',
  'no.when': 'Օր և ժամ',
  'no.day': 'Օր',
  'no.noSlots': 'Այս օրը ազատ ժամ չկա։',
  'no.otherTime': 'Այլ ժամ (պայմանավորվել ենք կատարողի հետ)',
  'no.customTime': 'Ժամ',
  'no.customHint': 'Երևանի ժամով։',
  'no.note': 'Մեկնաբանություն կատարողի համար',
  'no.notePh': 'Դոմոֆոնի կոդ, կենդանիներ…',
  'no.payment': 'Վճարում',
  'no.hint': 'Գինը՝ հաշվի առնելով բաժանորդագրությունը։ Հաճախորդը ծանուցում կստանա։',
  'no.create': 'Ձևակերպել պատվերը',
  'no.created': 'Պատվեր №{n}-ը ձևակերպված է',
  'prv.title': 'Կատարողներ',
  'prv.add': 'Ավելացնել',
  'prv.search': 'Անուն կամ հեռախոս',
  'prv.status.active': 'Աշխատում են',
  'prv.status.blocked': 'Արգելափակված',
  'prv.status.all': 'Բոլորը',
  'prv.empty': 'Ոչ ոք չգտնվեց։',
  'prv.col.name': 'Կատարող',
  'prv.col.rating': 'Վարկանիշ',
  'prv.col.done': 'Պատվերներ',
  'prv.col.refused': 'Հրաժարումներ',
  'prv.col.next': 'Մոտակա պատվերը',
  'prv.period30': 'Պատվերներ, հրաժարումներ, ցածր գնահատականներ՝ 30 օրում։',
  'prv.blocked': 'արգելափակված',
  'prv.onShift': 'հերթափոխում',
  'prv.offShift': 'հերթափոխում չէ',
  'prv.notLinked': 'բոտը բացված չէ',
  'prv.noDocs': 'փաստաթղթեր չկան',
  'prv.ratings': 'գնահատականներ՝ {n}',
  'prv.low': 'ցածր՝ {n}',
  'prv.today': 'այսօր՝ {n}',
  'prv.back': 'Բոլոր կատարողները',
  'prv.write': 'Գրել',
  'prv.edit': 'Խմբագրել',
  'prv.blockedSince': 'Արգելափակված է {date}-ից։',
  'prv.unblock': 'Վերադարձնել աշխատանքի',
  'prv.inviteHint': 'Կատարողը դեռ չի բացել աշխատանքային բոտը՝ ուղարկեք հղումը։',
  'prv.copyInvite': 'Պատճենել հղումը',
  'prv.quality': 'Որակ',
  'prv.days30': '30 օրում',
  'prv.acceptRate': 'ընդունել է {a}-ը {n}-ից',
  'prv.refusals': 'հրաժարվել՝ {d}, լռել՝ {e}',
  'prv.lowRatings': 'գնահատական 3 և ցածր',
  'prv.upcoming': 'Մոտակա պատվերներ',
  'prv.noUpcoming': 'Նշանակված պատվերներ չկան։',
  'prv.recent': 'Վերջին կատարվածները',
  'prv.balance': 'Վճարման ենթակա',
  'prv.thisMonth': 'Այս ամիս',
  'prv.prevMonth': 'Անցյալ',
  'prv.paidTotal': 'Վճարված',
  'prv.payout': 'Նշել վճարումը',
  'prv.payoutTitle': 'Վճարում կատարողին',
  'prv.amount': 'Գումար, ֏',
  'prv.balanceHint': 'Պարտք կատարողին՝ {sum}',
  'prv.payoutNote': 'Ինչպես է վճարվել',
  'prv.payoutNotePh': 'Կանխիկ / փոխանցում…',
  'prv.payoutConfirm': 'Վճարված է',
  'prv.payoutDone': 'Վճարումը նշված է',
  'prv.rateCompany': 'ընկերության դրույք {v}',
  'prv.rateOwn': 'դրույք {v}',
  'prv.ratePreset': '«{name}» նախադրույքով',
  'prv.work': 'Աշխատանք',
  'prv.languages': 'Լեզուներ',
  'prv.districts': 'Շրջաններ',
  'prv.anyDistrict': 'ցանկացած',
  'prv.districtsHint': 'Ընտրությունը նախապատվություն է տալիս շրջանում աշխատողներին։',
  'prv.docs': 'Թույլտվության փաստաթղթեր',
  'prv.noDocsHint': 'Անձնագիր, պայմանագիր, տեղեկանք՝ փակ պահոցում։',
  'prv.docTitlePh': 'Անվանում՝ անձնագիր, պայմանագիր…',
  'prv.upload': 'Վերբեռնել',
  'prv.docAdded': 'Փաստաթուղթն ավելացված է',
  'prv.removeDocConfirm': 'Ջնջե՞լ փաստաթուղթը։',
  'prv.access': 'Թույլտվություն',
  'prv.block': 'Արգելափակել',
  'prv.blockTitle': 'Արգելափակել կատարողին',
  'prv.blockReason': 'Պատճառը',
  'prv.blockReasonPh': 'Օրինակ՝ բողոքներ, փաստաթղթեր չկան',
  'prv.blockHint': 'Նոր առաջարկներ չեն լինի։ Նշանակված պատվերները վերանշանակեք ձեռքով։',
  'prv.resetInvite': 'Նոր հղում բոտի համար',
  'prv.resetConfirm': 'Ստեղծե՞լ նոր հղում։ Ընթացիկ կապը կհանվի։',
  'prv.since': 'Մեզ հետ է {date}-ից',
  'prv.saved': 'Պահպանված է',
  'prv.addTitle': 'Նոր կատարող',
  'prv.editTitle': 'Կատարողի պրոֆիլ',
  'prv.name': 'Անուն',
  'prv.role': 'Դեր',
  'prv.company': 'Ընկերություն',
  'prv.freelance': 'Անհատ կատարող',
  'prv.schedule': 'Ժամանակացույց',
  'prv.rate': 'Դրույք',
  'prv.note': 'Թիմի նշում',
  'err.REASON_REQUIRED': 'Նշեք պատճառը։',
  'err.SLOT_UNAVAILABLE': 'Այս ժամը զբաղված է կամ անցել է։',
  'err.ALREADY_ASSIGNED': 'Պատվերն արդեն նշանակված է։',
  'err.PROVIDER_BUSY': 'Կատարողը զբաղված է այդ ժամին։',
  'err.TRANSITION_NOT_ALLOWED': 'Այս քայլը հիմա հասանելի չէ։',
  'err.CHECKLIST_INCOMPLETE': 'Կատարողը նախ պետք է ավարտի ստուգացանկը։',
  'err.CONSENT_REQUIRED': 'Հաճախորդը դեռ համաձայնություն չի տվել։',
  'err.ADDRESS_REQUIRED': 'Նշեք հասցեն և շրջանը։',
  'err.INVALID_PARAMS': 'Ստուգեք պատվերի պարամետրերը։',
  'err.OFFER_NOT_ACTIVE': 'Առաջարկն այլևս ակտիվ չէ։',
  'err.BAD_FILE_TYPE_DOC': 'Միայն լուսանկար կամ PDF։',
  'audit.bo.orders.assign': 'առաջարկեց պատվերը',
  'audit.bo.orders.auto': 'գործարկեց ավտոմատ ընտրությունը',
  'audit.bo.orders.unassign': 'հանեց կատարողին',
  'audit.bo.orders.transition': 'փոխեց պատվերի կարգավիճակը',
  'audit.bo.orders.cancel': 'չեղարկեց պատվերը',
  'audit.bo.orders.reschedule': 'տեղափոխեց պատվերը',
  'audit.bo.orders.markPaid': 'նշեց վճարումը',
  'audit.bo.orders.create': 'ձևակերպեց պատվեր',
  'audit.bo.orders.setAutoSetting': 'փոխեց ավտոմատ բաշխումը',
  'audit.bo.providers.create': 'ավելացրեց կատարող',
  'audit.bo.providers.update': 'փոխեց կատարողի պրոֆիլը',
  'audit.bo.providers.setActive': 'փոխեց կատարողի թույլտվությունը',
  'audit.bo.providers.resetInvite': 'նոր հղում տվեց կատարողին',
  'audit.bo.providers.removeDoc': 'ջնջեց կատարողի փաստաթուղթը',
  'audit.bo.providers.addDoc': 'վերբեռնեց կատարողի փաստաթուղթը',
  'audit.bo.providers.payout': 'նշեց վճարում կատարողին',
  'chat.order': 'Պատվեր №{n}',
  'chat.noOrder': 'Առանց պատվերի',
  'chat.noOrderNow': 'Այժմ պատվեր չկա',
  'chat.orderNow': 'Այժմ',
  'chat.orderNext': 'Հաջորդը',
  'chat.orderNoNext': 'Մոտակա պատվերներ չկան',
  'chat.orderClient': 'հաճախորդ {name}',
  'chat.wroteNoOrder': 'գրել է առանց պատվերի',
  'err.CONCIERGE_SUBSCRIBERS_ONLY': 'Կոնսիերժը միայն բաժանորդների համար է։ Նախ տրամադրեք բաժանորդագրություն։',
  'err.ASSIGNEE_NO_ACCESS': 'Այս աշխատակիցը մուտք չունի այս ալիքին։',
  'err.PROVIDER_NOT_LINKED': 'Կատարողը դեռ չի բացել աշխատանքային բոտը։',
  'err.BAD_FILE_TYPE': 'Այս ֆայլը չի համապատասխանում։ Կարելի է լուսանկար, PDF, Word, Excel, տեքստ կամ ZIP։',
  'audit.bo.chats.send': 'պատասխանեց զրույցում',
  'audit.bo.chats.sendFile': 'ֆայլ ուղարկեց զրույցում',
  'audit.bo.chats.assign': 'նշանակեց զրույցի պատասխանատու',
  'audit.bo.chats.close': 'փակեց զրույցը',
  'audit.bo.chats.reopen': 'կրկին բացեց զրույցը',
  'audit.bo.chats.move': 'զրույցը տեղափոխեց այլ ալիք',
  'audit.bo.chats.openFor': 'բացեց զրույց հաճախորդի հետ',
  'audit.bo.chats.templates.save': 'փոխեց պատասխանի ձևանմուշը',
  'audit.bo.chats.templates.remove': 'ջնջեց պատասխանի ձևանմուշը',
  'nav.servicePartners': 'Ծառայությունների գործընկերներ',
  'st.group.partners': 'Ծառայությունների գործընկերներ',
  'ag.giftPlan': 'Նվեր նոր հաճախորդներին',
  'ag.giftMonths': 'Որքան ժամանակով',
  'ag.giftHint': 'Գործում է նոր հաճախորդների համար․ արդեն նվեր ստացածների մոտ ոչինչ չի փոխվում։',
  'ag.noGift': 'առանց նվերի',
  'ag.giftValue': '{plan}՝ {n} ամիս',
  'ag.monthsN': '{n} ամիս',
  'ord.partner': 'գործընկեր',
  'ord.best': 'ամենաշահավետը',
  'ord.partnerFree': 'ազատ է {n}-ը {of}-ից',
  'ord.candMoney': 'Վճարում ենք {cost} · մեզ մնում է {margin}',
  'prv.ratePartnerPays': 'Աշխատավարձը վճարում է գործընկերը',
  'rq.missing': 'տվյալներ չեն բավարարում',
  'rq.noTime': 'նշված չէ',
  'rq.noDistrict': 'շրջանը նշված չէ',
  'rq.noAddress': 'հասցեն նշված չէ',
  'sp.title': 'Ծառայությունների գործընկերներ',
  'sp.add': 'Նոր գործընկեր',
  'sp.editTitle': 'Ծառայությունների գործընկեր',
  'sp.lead': 'Մաքրման ընկերություններ՝ իրենց աշխատակիցներով։ Գործընկերոջ մենեջերը ստանում է հղում, ինքն է ավելացնում աշխատակիցներին և բաշխում պատվերները։ Պատվերը գործընկերոջը նշանակում է օպերատորը։',
  'sp.empty': 'Գործընկերներ դեռ չկան։',
  'sp.col.name': 'Գործընկեր',
  'sp.col.staff': 'Աշխատակիցներ',
  'sp.col.active': 'Ընթացիկ պատվերներ',
  'sp.col.month': 'Գործընկերոջ եկամուտը, ամիս',
  'sp.col.balance': 'Վճարման ենթակա',
  'sp.noManager': 'մենեջեր չկա',
  'sp.auto': 'ավտոբաշխում',
  'sp.back': 'Բոլոր գործընկերները',
  'sp.terms.COMMISSION': 'Uber-ի պես՝ մեր {pct}%-ը',
  'sp.terms.PROFIT_SHARE': 'ծախսերը մերն են՝ աշխ. {fot}%, հարկ {tax}%, գործընկերոջը՝ շահույթի {pct}%',
  'sp.managerLink': 'Մենեջերի հղումը',
  'sp.managerLinkHint': 'Տվեք հղումը գործընկերոջ մենեջերին․ նա կբացի աշխատանքային բոտը և կստանա կաբինետ՝ պատվերներ, աշխատակիցներ, ավտոբաշխում, եկամուտ։ Կաբինետը բացվում է նաև համակարգչում (կոճակը բոտում)։',
  'sp.resetLink': 'Նոր հղում',
  'sp.resetConfirm': 'Ստեղծե՞լ նոր հղում։ Հինը կդադարի աշխատել, միացված մենեջերները կմնան։',
  'sp.linkReset': 'Հղումը թարմացված է',
  'sp.managers': 'Մենեջերներ',
  'sp.noManagers': 'Մենեջերը դեռ չի միացել հղումով։',
  'sp.managerSince': '{date}-ից',
  'sp.remove': 'Հեռացնել',
  'sp.removeManagerConfirm': 'Հեռացնե՞լ {name}-ին մենեջերներից։',
  'sp.staff': 'Աշխատակիցներ',
  'sp.staffHint': 'ավելացնում է գործընկերոջ մենեջերը',
  'sp.noStaff': 'Աշխատակիցներ դեռ չկան։',
  'sp.limitOrders': 'օրական մինչև {n} պատվեր',
  'sp.limitHours': 'օրական մինչև {n} ժ',
  'sp.notLinked': 'բոտ չի մտել',
  'sp.off': 'անջատված',
  'sp.orders': 'Ընթացիկ պատվերներ',
  'sp.noOrders': 'Գործընկերը հիմա պատվեր չունի։',
  'sp.waitingStaff': 'սպասում է աշխատակցի',
  'sp.toPay': 'Գործընկերոջ եկամուտը վճարման',
  'sp.byOrder': 'Եկամուտն ըստ պատվերների',
  'sp.noIncome': 'Եկամուտը կհայտնվի, երբ հաճախորդը վճարի կատարված պատվերի համար։',
  'sp.paid': 'վճարված՝ {sum}',
  'sp.fotTax': 'աշխ. {fot}, հարկ {tax}',
  'sp.payoutTitle': 'Վճարում գործընկերոջը',
  'sp.phone': 'Հեռախոս',
  'sp.service': 'Ծառայություն',
  'sp.cleaningOnly': 'Մաքրում (առայժմ միայն այն)',
  'sp.deal': 'Համագործակցության մոդել',
  'sp.deal.COMMISSION': 'Uber-ի պես՝ մեր %-ը',
  'sp.deal.PROFIT_SHARE': 'Ծախսերը մերն են՝ % գործընկերոջը',
  'sp.dealHint.COMMISSION': 'Վճարված պատվերից վերցնում ենք մեր %-ը, մնացածը՝ գործընկերոջը։ Աշխատավարձն ու ծախսերը նրանն են։',
  'sp.dealHint.PROFIT_SHARE': 'Մաքրողի աշխատավարձը և հարկը վճարում ենք մենք․ գործընկերոջը՝ մնացորդի իր %-ը՝ վճարված − աշխ. − հարկ։',
  'sp.commissionPct': 'Մեր %-ը՝ վճարված պատվերից',
  'sp.profitSharePct': 'Գործընկերոջ %-ը շահույթից',
  'sp.fotPct': 'Մաքրողի աշխ., պատվերի %',
  'sp.taxPct': 'Հարկ, %',
  'sp.defaultHint': 'դատարկ՝ կարգավորումներից ({n}%)',
  'sp.exampleCOMMISSION': 'Օրինակ՝ վճարվել է {sum} → մեզ {ours}, գործընկերոջը {partner}։',
  'sp.examplePROFIT_SHARE': 'Օրինակ՝ վճարված {sum} − աշխ. {fot} − հարկ {tax} = {profit}․ գործընկերոջը {partner}, մեզ {ours}։',
  'sp.activeLabel': 'Գործընկերն աշխատում է (կարելի է նշանակել պատվերներ)',
  'nav.points': 'Բարիկներ',
  'st.group.points': 'Բարիկներ (քեշբեք)',
  'ag.hwPoints': 'Բարիկներ բնակարանամուտին',
  'ag.hwDays': 'Ժամկետ, օր',
  'ag.hwHint': '0՝ չենք նվիրում։ Մեկ անգամ, անմիջապես հասանելի։',
  'ag.hwValue': '{n} բարիկ {days} օրով',
  'pt.title': 'Բարիկներ',
  'pt.tab.accrue': 'Հաշվեգրել',
  'pt.tab.requests': 'Հայտեր',
  'pt.tab.suggestions': 'Համակարգի առաջարկներ',
  'pt.tab.ledger': 'Մատյան',
  'pt.tab.report': 'Հաշվետվություն',
  'pt.tab.import': 'CSV ներմուծում',
  'pt.tab.setup': 'Տոկոսներ և բոնուսներ',
  'pt.search': 'Հաճախորդ',
  'pt.searchPh': 'Հեռախոս, @username, անուն կամ ID',
  'pt.notFound': 'Ոչ ոք չգտնվեց',
  'pt.pickClient': 'Գտեք հաճախորդին ձախից․ այստեղ կհայտնվի ձևը։',
  'pt.flagged': 'ստուգել՝ շատ չեղարկումներ',
  'pt.balance': 'Հասանելի',
  'pt.pending': 'Սպասման մեջ',
  'pt.availableFrom': '{date}-ից',
  'pt.tier': 'Մակարդակ',
  'pt.burnAt': 'Կայրվեն',
  'pt.agency': 'Գործակալություն',
  'pt.streak': 'Մաքրումների շարք',
  'pt.debt': 'Պարտք',
  'pt.redeem': 'Դուրս գրել',
  'pt.redeemBtn': 'Դուրս գրել',
  'pt.redeemed': 'Դուրս է գրված',
  'pt.allOps': 'Բոլոր գործողությունները',
  'pt.noOps': 'Գործողություններ դեռ չկան։',
  'pt.reverse': 'Չեղարկել',
  'pt.accrueTitle': 'Հաշվեգրում',
  'pt.kind.ORDER': 'Պատվերի համար',
  'pt.kind.BONUS': 'Բոնուս',
  'pt.f.category': 'Կատեգորիա',
  'pt.f.orderAmount': 'Պատվերի գումար, ֏',
  'pt.f.excluded': 'Չի հաշվարկվում, ֏',
  'pt.f.excludedHint': 'տուրքեր, նյութեր, թեյավճար',
  'pt.f.paidWithPoints': 'Վճարված բարիկներով',
  'pt.f.serviceDate': 'Ծառայության ամսաթիվ',
  'pt.f.orderRef': 'Պատվերի համար',
  'pt.f.orderRefHint': 'նույն համարով երկրորդ անգամ չի կարելի',
  'pt.f.bonusType': 'Բոնուսի տեսակ',
  'pt.f.ttl': 'Ժամկետ, օր',
  'pt.f.ttlDefault': 'լռելյայն՝ {n}',
  'pt.f.ttlNone': 'դատարկ՝ ընդհանուր կանոն',
  'pt.f.amount': 'Բարիկների գումար',
  'pt.f.amountHint': 'առաջարկ՝ {n}',
  'pt.f.reason': 'Պատճառ',
  'pt.f.reasonRequired': 'Պատճառ (պարտադիր)',
  'pt.f.comment': 'Մեկնաբանություն հաճախորդի համար',
  'pt.f.commentPh': 'Կտեսնի պատմության մեջ',
  'pt.f.noWait': 'Առանց սպասման՝ անմիջապես',
  'pt.f.noWaitReason': 'Առանց սպասման (պատճառ պետք է)',
  'pt.f.redeemAmount': 'Որքան դուրս գրել',
  'pt.f.defaultAmount': 'Լռելյայն գումար',
  'pt.formula': '{tier}՝ {rate}%{extra} → {n} բարիկ',
  'pt.accrueBtn': 'Հաշվեգրել',
  'pt.confirmTitle': 'Ստուգեք',
  'pt.edit': 'Փոխել',
  'pt.confirmOrder': 'Հաշվեգրել {n} բարիկ {name}-ին՝ {what} {sum} ֏ ({tier}, {rate}%)։ Հասանելի՝ {date}։',
  'pt.confirmBonus': 'Հաշվեգրել {n} բարիկ {name}-ին՝ {what}, {until}։',
  'pt.now': 'անմիջապես',
  'pt.forDays': '{n} օրով',
  'pt.noTtl': 'առանց ժամկետի',
  'pt.changedNote': 'Գումարը տարբերվում է առաջարկից ({n})՝ կգրանցվի ձեր պատճառով։',
  'pt.accrued': 'Հաշվեգրված է {n}',
  'pt.needsApproval': 'Գերազանցում է սահմանաչափը՝ ուղարկված է սուպերադմինին',
  'pt.zero': 'Գումարը 0 է',
  'pt.reverseAccrual': 'Չեղարկել հաշվեգրումը',
  'pt.reverseRedemption': 'Չեղարկել դուրսգրումը',
  'pt.reverseAccrualText': '{n} բարիկի հաշվեգրումը կչեղարկվի։ Սկզբնական գրառումը կմնա մատյանում։',
  'pt.reverseRedemptionText': '{n} բարիկը կվերադառնա սկզբնական հաշվեգրումներին։',
  'pt.debtWarn': 'Արդեն ծախսված է {spent}՝ մնացորդը կդառնա բացասական՝ {after}։ Կծածկվի հաջորդ հաշվեգրումներով։',
  'pt.reasonPh': 'Վերադարձ, սխալ…',
  'pt.reverseHint': 'Աջակցությունը չեղարկում է միայն իր գործողությունները 24 ժամում։',
  'pt.reversed': 'Չեղարկված է',
  'pt.maxHint': 'առավելագույնը {max} ({share}%), նվազագույնը {min}',
  'pt.redeemReasonPh': 'Զանգով, պատվեր հավելվածից դուրս…',
  'pt.toPay': 'Մնաց վճարել՝ {sum} ֏',
  'pt.noRequests': 'Հայտեր չկան։',
  'pt.wants': 'ուզում է դուրս գրել {n}',
  'pt.forOrder': 'պատվեր №{n}',
  'pt.noOrder': 'պատվեր հավելվածից դուրս',
  'pt.balanceN': 'մնացորդ {n}',
  'pt.confirm': 'Հաստատել',
  'pt.confirmN': 'Դուրս գրել {n}',
  'pt.confirmed': 'Դուրս է գրված {n}',
  'pt.reject': 'Մերժել',
  'pt.rejectReason': 'Պատճառը՝ կտեսնի հաճախորդը',
  'pt.noSuggestions': 'Առաջարկներ չկան։',
  'pt.suggestionsHint': 'Համակարգը գտել է, ում է հասնում բոնուս։ Հաշվեգրեք, փոխեք գումարը կամ մերժեք։',
  'pt.mech.first_month_route': 'Առաջին ամսվա երթուղի',
  'pt.mech.photo_review': 'Կարծիք լուսանկարով',
  'pt.mech.over_limit': 'Աջակցության սահմանաչափից բարձր',
  'pt.why.first_month_route': 'առաջին 30 օրում 3 տարբեր կատեգորիա',
  'pt.why.photo_review': 'լուսանկար է ուղարկել №{n}-ից հետո',
  'pt.why.over_limit': '{by}՝ {op}',
  'pt.op.accrual': 'հաշվեգրում',
  'pt.op.redemption': 'դուրսգրում',
  'pt.approve': 'Հաստատել',
  'pt.changeAmount': 'Փոխել գումարը',
  'pt.allTypes': 'Բոլոր տեսակները',
  'pt.allCategories': 'Բոլոր կատեգորիաները',
  'pt.allChannels': 'Բոլոր ալիքները',
  'pt.allClients': 'Բոլոր հաճախորդները',
  'pt.type.accrual': 'Հաշվեգրում',
  'pt.type.bonus': 'Բոնուս',
  'pt.type.redemption': 'Դուրսգրում',
  'pt.type.request': 'Հաճախորդի հայտ',
  'pt.type.reversal': 'Չեղարկում',
  'pt.channel.WEB': 'ադմին',
  'pt.channel.BOT': 'բոտ',
  'pt.channel.CSV': 'CSV',
  'pt.channel.AUTO': 'ավտո',
  'pt.channel.CLIENT': 'հաճախորդ',
  'pt.status.PENDING': 'սպասման մեջ',
  'pt.status.ACTIVE': 'ակտիվ',
  'pt.status.SPENT': 'ծախսված',
  'pt.status.EXPIRED': 'այրված',
  'pt.status.REVERSED': 'չեղարկված',
  'pt.status.REQUESTED': 'սպասում է',
  'pt.status.CONFIRMED': 'դուրս գրված',
  'pt.status.REJECTED': 'մերժված',
  'pt.status.ACCRUAL': 'հաշվեգրման',
  'pt.status.REDEMPTION': 'դուրսգրման',
  'pt.col.date': 'Երբ',
  'pt.col.client': 'Հաճախորդ',
  'pt.col.what': 'Ինչ',
  'pt.col.amount': 'Բարիկներ',
  'pt.col.status': 'Կարգավիճակ',
  'pt.col.author': 'Ով',
  'pt.system': 'համակարգ',
  'pt.suggestedN': 'առաջարկ {n}',
  'pt.debtN': 'պարտք {n}',
  'pt.rep.accrued': 'Հաշվեգրված',
  'pt.rep.spent': 'Ծախսված',
  'pt.rep.expired': 'Այրված',
  'pt.rep.reversed': 'Չեղարկված',
  'pt.rep.liabilities': 'Պարտավորություններ',
  'pt.rep.rate': 'Ծախսման տոկոս',
  'pt.rep.byCategory': 'Ծրագրի արժեքը ըստ կատեգորիաների',
  'pt.rep.cost': 'հասույթի {pct}%՝ {revenue} ֏',
  'pt.rep.retention': 'Կրկնակի պատվեր 30 օրում',
  'pt.rep.with': 'Բարիկներով',
  'pt.rep.without': 'Առանց բարիկների',
  'pt.rep.clientsN': '{n} հաճախորդ, 2+ կատեգորիայով՝ {multi}',
  'pt.rep.topClients': 'Թոփ ըստ մնացորդի',
  'pt.rep.topAgencies': 'Գործակալություններ՝ բնակարանամուտի ակտիվացումներ',
  'pt.importHint': 'CSV՝ հեռախոս, կատեգորիա, պատվերի գումար, ամսաթիվ (ՏՏՏՏ-ԱԱ-ՕՕ), պատվերի համար։ Նախ՝ նախադիտում։',
  'pt.importRows': 'Տողեր',
  'pt.importTotal': 'Ընդամենը բարիկ',
  'pt.importErr.client': 'հաճախորդը չգտնվեց',
  'pt.importErr.category': 'անհայտ կատեգորիա',
  'pt.importErr.date': 'սխալ ամսաթիվ',
  'pt.importErr.duplicate': 'կրկնվող պատվեր',
  'pt.importCommit': 'Հաշվեգրել {n} տող',
  'pt.importDone': 'Հաշվեգրված է {n} տող',
  'pt.categories': 'Կատեգորիաներ և տոկոսներ',
  'pt.bonusTypes': 'Բոնուսների տեսակներ',
  'pt.code': 'Կոդ',
  'pt.activeLabel': 'Ակտիվ',
  'pt.rulesHint': 'Հասանելի՝ {hours} ժ հետո · մինչև {share}%, նվազագույնը {min} · այրվում են {burn} օր անգործությունից հետո։',
  'err.POINTS_DUPLICATE_ORDER': 'Այս պատվերի համար արդեն հաշվեգրվել է։',
  'err.POINTS_REASON_REQUIRED': 'Նշեք պատճառը։',
  'err.POINTS_BELOW_MIN': 'Նվազագույնից քիչ է։',
  'err.POINTS_NOT_ENOUGH': 'Բարիկները չեն բավարարում։',
  'err.POINTS_OVER_SHARE': 'Ավելի է թույլատրելի մասից։',
  'err.POINTS_OVER_LIMIT': 'Սահմանաչափից բարձր է։',
  'err.POINTS_FINAL': 'Այլևս հնարավոր չէ չեղարկել։',
  'err.POINTS_REQUEST_CLOSED': 'Հայտն արդեն մշակված է։',
  'err.POINTS_WITH_PROMO': 'Բարիկները չեն համատեղվում պրոմոկոդի հետ։',
  'pt.formulaStreak': ' + շարք {streak}՝ +{bonus}%',
  'ord.editComposition': 'Փոխել կազմը',
  'ord.editTitle': '№{n} պատվերի կազմը',
  'ord.editPrice': 'Գինը՝ {was} → {now} (զեղչ {pct}%)',
  'ord.editDuration': 'Տևողություն՝ {was} → {now} րոպե',
  'ord.editBusy': 'Պատվերը երկարեց, իսկ կատարողն այդ ժամին զբաղված է։',
  'ord.editPaid': 'Արդեն վճարված է․ կվերահաշվարկվեն պարգևը, գործընկերոջ եկամուտը և բարիկները։',
  'ord.editOwnPrice': 'Սեփական գին, ֏',
  'ord.editOwnPriceHint': 'դատարկ՝ գնացուցակով՝ {n}',
  'ord.editReason': 'Պատճառ',
  'ord.editReasonRequired': 'Պատճառ (պարտադիր)',
  'ord.editReasonPh': 'Տեղում ավելացրին պատուհաններ…',
  'ord.editNotify': 'Տեղեկացնել հաճախորդին և կատարողին',
  'ord.edited': 'Պատվերը փոխված է',
  'ord.editedBusy': 'Փոխված է։ Ստուգեք կատարողի գրաֆիկը',
  'ord.uncheck': 'Հանել նշումը',
  'ord.checkForProvider': 'Նշել կատարողի փոխարեն',
  'ord.noPhotoReason': 'առանց լուսանկարի՝ {reason}',
  'ord.byStaff': 'նշել է թիմը',
  'ord.checkNoPhoto': 'Նշել առանց լուսանկարի',
  'ord.photoHint': 'Այս կետի համար պետք է լուսանկար։',
  'ord.uploadPhoto': 'Վերբեռնել լուսանկար',
  'ord.noPhotoField': 'Կամ պատճառը, թե ինչու առանց լուսանկարի',
  'ord.noPhotoPh': 'Մաքրողն ուղարկել է զրույցում…',
  'err.PHOTO_REQUIRED': 'Պետք է լուսանկար կամ պատճառ։',
};

const DICTS: Record<BoLocale, Dict> = { ru, en, hy };

export type T = (key: BoKey | (string & {}), params?: Record<string, string | number | null | undefined>) => string;

function makeT(locale: BoLocale): T {
  const dict = DICTS[locale];
  return (key, params) => {
    let s: string = (dict as Record<string, string>)[key] ?? (ru as Record<string, string>)[key] ?? key;
    if (params) for (const [k, v] of Object.entries(params)) s = s.replaceAll(`{${k}}`, String(v ?? ''));
    return s;
  };
}

interface Ctx {
  locale: BoLocale;
  setLocale: (l: BoLocale) => void;
  t: T;
  /** общие переводы проекта: services.<code>.name, roles.<ROLE>, params.* и т.п. */
  shared: (key: string, params?: Record<string, string | number>) => string;
  /** время по Еревану: 14:00 */
  time: (d: Date) => string;
  /** общий t проекта целиком — для хелперов @barev/web (paramsSummary и т.п.) */
  sharedT: ReturnType<typeof createSharedT>;
  amd: (n: number) => string;
  date: (d: Date) => string;
  dateTime: (d: Date) => string;
  /** сокращённая сумма для плотных мест: 1,2 млн */
  amdShort: (n: number) => string;
}

const I18nCtx = createContext<Ctx | null>(null);
const KEY = 'barev.bo.locale';

function initialLocale(): BoLocale {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved && (BO_LOCALES as readonly string[]).includes(saved)) return saved as BoLocale;
  } catch {
    /* приватный режим */
  }
  const nav = navigator.language.slice(0, 2);
  return (BO_LOCALES as readonly string[]).includes(nav) ? (nav as BoLocale) : 'ru';
}

export function BoI18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<BoLocale>(initialLocale);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  // тексты, изменённые в «Продуктах» (названия услуг и т.п.), — перерисовать, когда подгрузились
  const [textsVersion, setTextsVersion] = useState(textOverridesVersion());
  useEffect(() => {
    const off = onTextOverrides(() => setTextsVersion(textOverridesVersion()));
    return () => void off();
  }, []);
  const value = useMemo<Ctx>(() => {
    const sharedT = createSharedT(locale as SharedLocale);
    const intl = locale === 'en' ? 'en-GB' : locale === 'hy' ? 'hy-AM' : 'ru-RU';
    const compact = new Intl.NumberFormat(intl, { notation: 'compact', maximumFractionDigits: 1 });
    return {
      locale,
      setLocale: (l) => {
        setLocaleState(l);
        try {
          localStorage.setItem(KEY, l);
        } catch {
          /* приватный режим */
        }
      },
      t: makeT(locale),
      shared: sharedT,
      sharedT,
      amd: (n) => formatAmd(locale as SharedLocale, n),
      amdShort: (n) => `${compact.format(n)} ֏`,
      date: (d) => formatDateTime(locale as SharedLocale, d, { day: 'numeric', month: 'short', year: 'numeric' }),
      dateTime: (d) => formatDateTime(locale as SharedLocale, d, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
      time: (d) => formatDateTime(locale as SharedLocale, d, { hour: '2-digit', minute: '2-digit' }),
    };
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, textsVersion]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useT() {
  const v = useContext(I18nCtx);
  if (!v) throw new Error('BoI18nProvider missing');
  return v;
}

/** Текст ошибки API: код бизнес-ошибки → перевод, иначе сообщение */
export function errText(t: T, err: unknown): string {
  const msg = (err as { message?: string })?.message ?? '';
  const known = `err.${msg}`;
  const tr = t(known);
  return tr !== known ? tr : t('ui.error', { msg: msg || '—' });
}
